// import github.css
import './github-markdown-light.css';
import './scoped.css';

import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { message } from 'antd';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import {
  Navigate,
  UNSAFE_NavigationContext,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import {
  ArrowForward as ArrowForwardIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  EditNote as EditNoteIcon,
  ExpandMore as ExpandMoreIcon,
  ScienceOutlined as ScienceOutlinedIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button as MuiButton,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { emptyFunnel, mergeFunnel, settleFunnel } from './funnel';
/* What a stopped run leaves on screen, and the rule for when it is written — shared, because
   three paths reach it now (the live Stop, a stopped reattach, and a poll that finds the run
   already cancelled) and a reader can meet more than one of them in a single run. */
import { keepsWhatItWrote, stoppedMessageFor, withStoppedMessage } from './stopped';
import InvestigateProgress, { formatElapsed } from './InvestigateProgress';
import {
    mergeInvestigateDetail,
    mergeLiveKeywords,
    mergeLivePapers,
    mergePercentMonotonic,
    mergePhaseMonotonic,
} from './traceReplay';
import ClarifyPanel, { getClarificationQuestionKey } from './ClarifyPanel';
import ReferenceHoverCard from './ReferenceHoverCard';
import AnswerGraphForMessage from '../AnswerGraph/AnswerGraphForMessage';
import IconButton from '../Units/HintIconButton';
import { splitAnswerForGraph } from '../AnswerGraph/answerGraph';
import { getBookmarks, toggleBookmark } from '../../utils/bookmarks';
import { resolveClarifyRound } from './clarifyRound';
import { sortReferences } from './referenceSort';
import { resolveReferenceSourceIndex } from './referenceSource';
import { mintSessionId } from './sessionId';
import { makeDrip } from './streamDrip';
import {
    claimQueuedPrompts,
    nextReleasableEntry,
    promptsForConversation,
    promptsSurvivingReset,
    queuedPromptOwner,
    releaseTargetFor,
    restoredQueueOwnerKey,
} from './promptQueue';
import { ReactComponent as ContentCopyIcon } from '../../img/llm/content_copy.svg';
import { ReactComponent as DownloadIcon } from '../../img/llm/download_2.svg';
import { ReactComponent as ReferenceIcon } from '../../img/llm/reference.svg';
import { ReactComponent as ReplayIcon } from '../../img/llm/replay.svg';
import { ReactComponent as ThumbsUpDownIcon } from '../../img/llm/thumbs_up_down.svg';
import { CHAT_HOME_PATH, CHAT_NEW_PATH } from '../../config/entryRoutes';
import { submitChatFeedback } from '../../service/Feedback';
import {
  LLMAgentService,
  PHASE_PERCENT_FLOOR,
  answerOptionsOf,
  extractProgress,
  inferInvestigatePhase,
} from '../../service/LLMAgent';
import { getCurrentUser } from '../../service/Auth';
import { answerOptionsForTurn } from './answerOptions';
import {
  applyStreamCredits,
  fetchUsage,
  getUsageSnapshot,
  limitReachedText,
  recordGuestQuestion,
  subscribeToUsage,
} from '../../service/credits';
import {
  createConversation,
  fetchConversationDetail,
  fetchConversationDetailByPublicId,
  fetchConversations,
  getActiveConversationId,
  getConversations,
  setActiveConversationId,
  setConversations,
  updateConversationMessages,
  updateConversationTitle,
  upsertConversation,
} from '../../utils/chatHistory';
import {
  fetchConversationBookmarks,
  getConversationBookmarks,
  toggleConversationBookmark,
} from '../../utils/conversationBookmarks';
import { createQuerySubmitSuccessTracker, trackGtagEvent } from '../../utils/gtag';
import { CREDITS_PURCHASE_URL } from '../../config/features';
import { useAuth } from '../Auth/AuthContext';
import {
    NOTIFY_EMAIL_KEY,
    getNotifyPrefs,
    getUserNotifyEmail,
    notifyRunComplete,
    setNotifyPref,
    subscribeToNotifyPrefs,
} from '../../service/notifications';
import {
    effectiveTier,
    getTierPref,
    GUEST_TIER,
    setTierPref,
    subscribeToTierPref,
} from '../../service/serviceTiers';
import { refusalNeedsSignIn, refusalText } from '../../utils/refusals';
import {
    attachmentIdsOf,
    defaultQuestionFor,
    GUEST_ATTACH_REASON,
    normalizeAttachmentList,
} from '../../service/attachments';
import {
    clearActiveRun,
    clearPendingRun,
    forgetRun,
    getRunSessionId,
    isConversationRunning,
    setActiveRun,
    subscribeToActiveRun,
} from '../../service/activeRun';
import { getStoredSessionId, setStoredSessionId } from '../../service/runRecovery';
import {
    clearActiveRunSnapshot,
    readActiveRunSnapshot,
    readActiveRunSnapshotFor,
    releaseGuestSlot,
    removeQueuedPromptFromSnapshot,
    restoreQueuedPromptToSnapshot,
    consumeQueuedPrompt,
    unconsumeQueuedPrompt,
    pendingQueuedPrompts,
    queueEntryIdentity,
    writeActiveRunSnapshot,
} from '../../service/agentRunSnapshot';
import { shouldSkipConversationRestore } from '../../service/conversationRestore';
import { markInvestigateConversation } from '../../utils/investigateConversations';
import { resolveConversationMode, readSubmittedMode, recordSubmittedMode, normalizePendingMode, resolveQueuedSearchOptions } from './conversationMode';
import { useQueueDispatch } from './useQueueDispatch';
import { holdRecentPriority } from '../../service/recentPriority';
import {
    getLiveConversationNavigationAction,
    isExchangeUnfinished,
} from '../../service/resumeRun';
import {
    bindMarkersToLinks,
    citationsFor,
    hrefWithoutMarker,
    indexByMarker,
    markerFromHref,
    parseDirectCitations,
    pmidFromHref,
    stripCitationsBlock,
} from '../../utils/directCitations';
import { MessageAttachments, useAttachments } from '../Units/AttachmentChips';
import CiteDialog from '../Units/CiteDialog';
import { cslFromCard, toBibTeX } from '../Units/CiteDialog/format';
import { fetchCitations } from '../../service/Citation';
import ErrorBoundary from '../Units/ErrorBoundary';
import ReferenceCard from '../Units/ReferenceCard/ReferenceCard';
import ChatSearchBar from './ChatSearchBar';
import { repairOrphanSingleItemMarkdown } from './markdownRendering';
import stepLabels from './step.json';

const formatDuration = (durationMs) => {
    if (durationMs === null || durationMs === undefined) return '';
    const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
};

/**
 * The "Investigated for m:ss" row shown once the run closes.
 *
 * Formatting is delegated to the live header clock's `formatElapsed` so the two can never drift
 * apart — same shape, same truncation, same `Date.now()` origin. Returns '' for an unknown
 * duration, which is also what gates the row's visibility.
 */
const formatInvestigatedDuration = (durationMs) => {
    if (durationMs === null || durationMs === undefined) return '';
    return formatElapsed(durationMs / 1000);
};

const formatFunnelValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return num.toLocaleString();
};

const buildClarificationDrafts = (questions) => {
    if (!Array.isArray(questions)) return {};
    return questions.reduce((acc, question, index) => {
        const key = getClarificationQuestionKey(question, index);
        const defaults = Array.isArray(question?.default)
            ? question.default.map((item) => String(item || '').trim()).filter(Boolean)
            : [];
        acc[key] = {
            selected: defaults,
            text: '',
            otherSelected: false,
        };
        return acc;
    }, {});
};

const buildClarifyAnswers = (questions, drafts) => {
    if (!Array.isArray(questions)) return [];

    return questions.reduce((acc, question, index) => {
        const key = getClarificationQuestionKey(question, index);
        const draft = drafts?.[key] || { selected: [], text: '' };
        const header = typeof question?.header === 'string' ? question.header.trim() : key;
        const responseType = String(question?.response_type || 'text').toLowerCase();
        const selected = Array.isArray(draft.selected)
            ? draft.selected.map((item) => String(item || '').trim()).filter(Boolean)
            : [];
        const text = typeof draft.text === 'string' ? draft.text.trim() : '';

        if (responseType === 'text') {
            if (!text) return acc;
            acc.push({ header, selected: [], text });
            return acc;
        }

        if (responseType === 'single') {
            if (text) {
                acc.push({ header, selected: [], text });
                return acc;
            }
            if (selected[0]) {
                acc.push({ header, selected: [selected[0]], text: null });
            }
            return acc;
        }

        if (responseType === 'multi') {
            if (selected.length === 0 && !text) return acc;
            acc.push({
                header,
                selected,
                text: text || null,
            });
            return acc;
        }

        if (!selected.length && !text) return acc;
        acc.push({
            header,
            selected,
            text: text || null,
        });
        return acc;
    }, []);
};

const logDev = (...args) => {
    if (process.env.NODE_ENV !== 'production') {
        console.log(...args);
    }
};

const getStoredChatHistory = (conversationId = null) => {
    if (typeof window === 'undefined') return [];
    const conversations = getConversations();
    const activeId = conversationId || getActiveConversationId();
    const active = conversations.find((item) => String(item.id) === String(activeId));
    return active?.messages || [];
};

const getConversationInvestigateMode = (conversationId, messages = []) => resolveConversationMode({
    conversationId,
    conversation: conversationId == null ? null : getConversations().find(
        (item) => String(item.id) === String(conversationId),
    ),
    snapshot: readActiveRunSnapshotFor(conversationId),
    messages,
    submittedMode: readSubmittedMode(conversationId),
});

/**
 * The thread a restored run should be drawn on top of.
 *
 * The stored conversation when there is one — it is authoritative and holds the whole history.
 * Otherwise the snapshot's own copy, which is all a guest has: they never get a conversation
 * row, so the store is empty for them and restoring against it produced a blank page with the
 * question gone.
 */
const baseMessagesForSnapshot = (snapshot) => {
    /* Only ask the store when the snapshot names a conversation. `getStoredChatHistory(null)`
       does not mean "no conversation" — it falls back to the remembered active id, so a guest
       snapshot would have been drawn on top of whatever conversation the last signed-in reader
       of this browser had open, and preferred that stranger's transcript to its own. */
    const stored = snapshot?.conversationId
        ? getStoredChatHistory(snapshot.conversationId)
        : [];
    if (stored.length) return stored;
    return Array.isArray(snapshot?.messages) ? snapshot.messages : [];
};

const mergeMessagesWithRunSnapshot = (messages, snapshot) => {
    const base = Array.isArray(messages) ? [...messages] : [];
    const liveAssistant = snapshot?.assistantMessage;
    if (!snapshot?.active || !liveAssistant || liveAssistant.role !== 'assistant') return base;

    const tail = base[base.length - 1];
    if (tail?.role === 'assistant') {
        base[base.length - 1] = { ...tail, ...liveAssistant };
    } else if (tail?.role === 'user') {
        base.push(liveAssistant);
    }
    return normalizePendingMode(base, getConversationInvestigateMode(snapshot?.conversationId, base));
};

const getStoredProcessingFlag = () => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('llmWasProcessing') === 'true';
};

const getStoredIncompleteFlag = () => {
    if (typeof window === 'undefined') return false;
    try {
        const parsed = getStoredChatHistory();
        if (!Array.isArray(parsed) || parsed.length === 0) return false;
        const lastMessage = parsed[parsed.length - 1];
        return lastMessage?.role === 'assistant' && !lastMessage?.content;
    } catch (error) {
        return false;
    }
};

/* The per-conversation session id map moved to localStorage — see service/runRecovery.js. It
   was in sessionStorage, which the browser discards when the tab closes, so closing the tab
   mid-answer destroyed the only address the answer could be collected from. */

const STEP_LABELS = stepLabels || {};

// Reattaching to a run left behind by a reload. An investigate run can take fifteen minutes, so
// the cap is generous on purpose: giving up early is the expensive mistake here — it turns an
// answer that is still coming into a message that says it is gone.
const RESUME_POLL_MS = 3000;
const RESUME_MAX_POLLS = 300;      // 15 minutes
const RESUME_LOST_MESSAGE =
    'This answer could not be recovered after the page was reloaded. Please ask again.';

const PUBMED_ESUMMARY_URL = 'https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi';
const PLACEHOLDER_PMID_PREFIX = 'PMID ';

const LEFT_MIN_PX = 360;
const RIGHT_MIN_PX = 360;
const DIVIDER_PX = 8;
const PREFERRED_CHAT_COLUMN_MIN_PX = 680;
const MIN_SPLIT_WIDTH_WITH_REFERENCES = PREFERRED_CHAT_COLUMN_MIN_PX + DIVIDER_PX + RIGHT_MIN_PX;
const DEFAULT_LEFT_PERCENT = 66;
const FALLBACK_MIN_LEFT_PERCENT = 45;
const FALLBACK_MAX_LEFT_PERCENT = 80;
const FALLBACK_COLLAPSE_THRESHOLD = 84;
const DEBUG_FORCE_LIMIT_WARNING = false;
const MOBILE_HEADER_NEW_CHAT_EVENT = 'glkb-mobile-header-new-chat';
const isPhoneViewport = () => window.matchMedia('(max-width: 767px)').matches;

const areMessagesEqual = (left, right) => {
    if (left === right) return true;
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i += 1) {
        const leftMsg = left[i];
        const rightMsg = right[i];
        const leftSignature = JSON.stringify({
            role: leftMsg?.role,
            content: leftMsg?.content,
            timestamp: leftMsg?.timestamp,
            references: leftMsg?.references,
            thinkingSteps: leftMsg?.thinkingSteps,
            thoughtDurationMs: leftMsg?.thoughtDurationMs,
            trajectory: leftMsg?.trajectory,
            invocationId: leftMsg?.invocationId,
            investigateMode: leftMsg?.investigateMode,
            modeSource: leftMsg?.modeSource,
        });
        const rightSignature = JSON.stringify({
            role: rightMsg?.role,
            content: rightMsg?.content,
            timestamp: rightMsg?.timestamp,
            references: rightMsg?.references,
            thinkingSteps: rightMsg?.thinkingSteps,
            thoughtDurationMs: rightMsg?.thoughtDurationMs,
            trajectory: rightMsg?.trajectory,
            invocationId: rightMsg?.invocationId,
            investigateMode: rightMsg?.investigateMode,
            modeSource: rightMsg?.modeSource,
        });
        if (leftSignature !== rightSignature) return false;
    }

    return true;
};

const AGENT_STEP_LABEL = (action) => {
    if (action === 'AGENT START' || action === 'AGENT INPUT') {
        return STEP_LABELS.GLKBAgent || 'Agent is thinking';
    }
    if (action === 'AGENT OUTPUT') {
        return STEP_LABELS.FinalAnswerAgent || 'Formulating the final answer';
    }
    return '';
};

// Anything that still carries a JSON payload or a pipe is a raw transport trace
// rather than something worth showing a user.
const looksLikeRawPayload = (value) => /[{}]|\bInput:|\bOutput:|\|/.test(String(value));

const getStepLabel = (stepName) => {
    if (!stepName) return '';
    if (STEP_LABELS[stepName]) return STEP_LABELS[stepName];

    // Raw traces ("[TOOL CALL] search_pubmed | Input: {…}") reach this function
    // directly from progress frames, which bypass parseThinkingEntry. Only the
    // token right after the bracket carries meaning; the payload is never shown.
    const bracketMatch = String(stepName).match(/^\s*\[([^\]]+)\]\s*([\s\S]*)$/);
    if (bracketMatch) {
        const action = bracketMatch[1].trim().toUpperCase();
        const token = bracketMatch[2].split('|')[0].trim();
        if (token && STEP_LABELS[token]) return STEP_LABELS[token];
        const agentLabel = AGENT_STEP_LABEL(action);
        if (agentLabel) return agentLabel;
        if (token && !looksLikeRawPayload(token)) return token.replace(/_/g, ' ');
        return STEP_LABELS[bracketMatch[1].trim()] || STEP_LABELS.Processing || 'Working...';
    }

    const toolMatch = String(stepName).match(/^TOOL\s+(?:CALL|RESULT):\s*(.+)$/i);
    if (toolMatch?.[1]) {
        const toolName = toolMatch[1].split('|')[0].trim();
        return STEP_LABELS[toolName] || toolName.replace(/_/g, ' ');
    }

    const agentLabel = AGENT_STEP_LABEL(String(stepName).toUpperCase());
    if (agentLabel) return agentLabel;

    // Never let an unmapped payload string become the visible step wording.
    if (looksLikeRawPayload(stepName)) {
        return STEP_LABELS.Processing || 'Working...';
    }

    return stepName;
};

const extractYearFromPubDate = (value) => {
    if (!value || typeof value !== 'string') return '';
    const match = value.match(/\b(19|20)\d{2}\b/);
    return match ? match[0] : '';
};

const extractPmidFromReference = (ref) => {
    if (!ref || typeof ref !== 'object') return null;
    const direct = String(ref.pmid || '').trim();
    if (/^\d+$/.test(direct)) return direct;

    const url = String(ref.url || '').trim();
    if (url) {
        const urlMatch = url.match(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/i);
        if (urlMatch?.[1]) return urlMatch[1];
    }

    const title = String(ref.title || '').trim();
    const titleMatch = title.match(/^PMID\s+(\d+)$/i);
    if (titleMatch?.[1]) return titleMatch[1];

    return null;
};

const isPlaceholderPmidReference = (ref) => {
    const pmid = extractPmidFromReference(ref);
    if (!pmid) return false;
    const title = String(ref.title || '').trim();
    return title === `${PLACEHOLDER_PMID_PREFIX}${pmid}`;
};

const fetchPubmedSummaryMap = async (pmids) => {
    if (!Array.isArray(pmids) || pmids.length === 0) return {};

    const params = new URLSearchParams();
    params.set('db', 'pubmed');
    params.set('id', pmids.join(','));
    params.set('retmode', 'json');

    const response = await fetch(`${PUBMED_ESUMMARY_URL}?${params.toString()}`, {
        method: 'GET',
        credentials: 'omit',
    });
    if (!response.ok) {
        throw new Error(`PubMed esummary request failed: ${response.status}`);
    }

    const payload = await response.json();
    const result = payload?.result;
    if (!result || typeof result !== 'object') return {};

    const map = {};
    pmids.forEach((pmid) => {
        const doc = result[pmid];
        if (!doc || typeof doc !== 'object') return;

        const title = typeof doc.title === 'string' ? doc.title.trim() : '';
        const journal = typeof doc.fulljournalname === 'string'
            ? doc.fulljournalname.trim()
            : (typeof doc.source === 'string' ? doc.source.trim() : '');
        const pubDate = typeof doc.pubdate === 'string' ? doc.pubdate : '';
        const sortDate = typeof doc.sortpubdate === 'string' ? doc.sortpubdate : '';
        const year = extractYearFromPubDate(pubDate) || extractYearFromPubDate(sortDate);
        const authors = Array.isArray(doc.authors)
            ? doc.authors
                .map((item) => (typeof item?.name === 'string' ? item.name.trim() : ''))
                .filter(Boolean)
                .join(', ')
            : '';

        map[pmid] = {
            title,
            journal,
            year,
            authors,
        };
    });

    return map;
};

const ThoughtLine = React.memo(function ThoughtLine({ line, lineKey }) {
    const isTrajectoryLine = line && typeof line === 'object' && !Array.isArray(line);

    if (isTrajectoryLine) {
        const tool = line.tool || '';
        const summary = line.summary || '';
        const result = line.result || '';
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                }}
                data-line-key={lineKey}
            >
                {(tool || summary) && (
                    <Typography
                        sx={{
                            /* body-sm. This is what a step did, not what the answer says, and
                               it was set at 16 — larger than the answer's own 14, and larger
                               than the 12 the streaming lines beside it use. The rest of the
                               app left DM Sans behind too. */
                            fontFamily: 'Geist, sans-serif',
                            fontSize: '12px',
                            fontWeight: 400,
                            color: 'var(--color-text-tertiary)',
                            whiteSpace: 'pre-wrap',
                            lineHeight: '20px',
                        }}
                    >
                        {tool && (
                            <Box
                                component="span"
                                sx={{
                                    fontFamily: 'Geist, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    color: 'var(--color-text-tertiary)',
                                    marginRight: '6px',
                                }}
                            >
                                {tool}
                            </Box>
                        )}
                        {summary}
                    </Typography>
                )}
                {result && (
                    <Typography
                        sx={{
                            fontFamily: 'Geist, sans-serif',
                            fontSize: '12px',
                            fontWeight: 400,
                            color: 'var(--color-text-tertiary)',
                            whiteSpace: 'pre-wrap',
                            lineHeight: '20px',
                        }}
                    >
                        {result}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Typography
            sx={{
                fontFamily: 'Geist, sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '20px',
                color: 'var(--color-grey-400)',
                whiteSpace: 'pre-wrap',
            }}
            data-line-key={lineKey}
        >
            {line}
        </Typography>
    );
});

const ThoughtGroup = React.memo(
    function ThoughtGroup({
        group,
        groupIndex,
        expanded,
        onToggle,
        disableAnimation = false,
        disableToggle = false,
        showBorder = true,
    }) {
        const hasLines = group.lines.length > 0;
        const canToggle = hasLines && !disableToggle;
        return (
            <Box>
                <Box
                    role={canToggle ? 'button' : undefined}
                    tabIndex={canToggle ? 0 : -1}
                    onClick={canToggle ? () => onToggle(groupIndex) : undefined}
                    onKeyDown={(event) => {
                        if (!canToggle) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onToggle(groupIndex);
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: canToggle ? 'pointer' : 'default',
                        '&:hover': canToggle ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : undefined,
                        '&:hover .thought-step-arrow': canToggle ? { opacity: 1 } : undefined,
                    }}
                >
                    <Typography sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'var(--color-text-tertiary)',
                    }}>
                        {getStepLabel(group.name)}
                    </Typography>
                    {canToggle && (
                        <ExpandMoreIcon
                            className="thought-step-arrow"
                            sx={{
                                fontSize: '16px',
                                color: 'var(--color-grey-400)',
                                opacity: 0,
                                transition: 'opacity 0.2s ease, transform 0.2s ease',
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    )}
                </Box>
                {expanded && hasLines && (
                    <Box sx={{
                        mt: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        borderLeft: showBorder ? '2px solid var(--color-border-strong)' : 'none',
                        pl: showBorder ? '10px' : '0px',
                        ml: showBorder ? 2 : '0px',
                    }}>
                        {group.lines.map((line, lineIndex) => (
                            <ThoughtLine
                                key={`${group.name}-${lineIndex}`}
                                line={line}
                                lineKey={`${group.name}-${lineIndex}`}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        );
    },
    (prev, next) => (
        prev.group === next.group
        && prev.expanded === next.expanded
        && prev.disableToggle === next.disableToggle
    )
);

const parseThinkingEntry = (entry) => {
    const stepFromEntry = typeof entry?.step === 'string' ? entry.step.trim() : '';
    const raw = entry?.content ?? '';
    // A run of text the model streamed before a tool call — its own narration on the way to
    // the next step, not the answer. There is no transport tag to parse: the content IS the
    // line, and the heading is fixed so consecutive narrations collapse into one group.
    if (entry?.isThought) {
        return { stepName: 'Thinking', line: raw };
    }
    const trimmed = raw.trim();
    if (!trimmed) {
        return { stepName: stepFromEntry || 'Step', line: raw };
    }

    let action = '';
    let rest = trimmed;
    const match = trimmed.match(/^\s*\[([^\]]+)\]\s*([\s\S]*)$/);
    if (match) {
        action = match[1].trim();
        rest = match[2].trim();
    }

    let stepName = rest;
    let detail = '';
    if (rest.includes('|')) {
        const parts = rest.split('|');
        stepName = parts.shift().trim();
        detail = parts.join('|').trim();
    }

    if (!stepName) {
        stepName = action || 'Step';
    }

    let derivedStepName = stepName;
    const normalizedAction = action.toUpperCase();
    if (normalizedAction) {
        if (normalizedAction === 'TOOL CALL' || normalizedAction === 'TOOL RESULT') {
            const toolName = stepName || 'Tool';
            derivedStepName = `${normalizedAction}: ${toolName}`;
        } else if (
            normalizedAction === 'AGENT START'
            || normalizedAction === 'AGENT INPUT'
            || normalizedAction === 'AGENT OUTPUT'
        ) {
            derivedStepName = normalizedAction;
        } else {
            derivedStepName = action;
        }
    }

    const isGenericTransportStep = (
        stepFromEntry === 'Processing'
        || stepFromEntry === 'Started'
        || stepFromEntry === 'Complete'
    );

    // Resolve to the human-facing wording from step.json. Grouping keys off this
    // value, so "[TOOL CALL] search_pubmed" and its matching "[TOOL RESULT]"
    // collapse into a single "Searching for relevant articles".
    //
    // The tool/agent name must win over `stepFromEntry`: the transport tags
    // nearly every frame as "Processing", which would otherwise flatten every
    // distinct tool into the generic "Working...".
    const derivedLabel = derivedStepName ? getStepLabel(derivedStepName) : '';
    const hasSpecificLabel = Boolean(derivedLabel) && derivedLabel !== derivedStepName;

    if (hasSpecificLabel) {
        stepName = derivedLabel;
    } else if (stepFromEntry && STEP_LABELS[stepFromEntry] && !isGenericTransportStep) {
        stepName = STEP_LABELS[stepFromEntry];
    } else if (derivedStepName) {
        stepName = derivedLabel || derivedStepName;
    } else if (stepFromEntry) {
        stepName = getStepLabel(stepFromEntry) || stepFromEntry;
    } else {
        stepName = 'Step';
    }

    // `[TOOL CALL] … | Input: {…}` / `[AGENT START] …` are internal debug traces.
    // Users see only the mapped wording, never the raw payload.
    const isInternalTrace = Boolean(action);

    return { stepName, line: isInternalTrace ? '' : raw };
};

const groupThinkingSteps = (steps) => {
    if (!Array.isArray(steps)) return [];
    const groups = [];

    steps.forEach((entry) => {
        const { stepName, line } = parseThinkingEntry(entry);
        if (groups.length === 0 || groups[groups.length - 1].name !== stepName) {
            groups.push({ name: stepName, lines: line ? [line] : [] });
        } else if (line) {
            groups[groups.length - 1].lines.push(line);
        }
    });

    return groups;
};

const normalizeTrajectory = (trajectory) => {
    if (!trajectory) return [];
    if (Array.isArray(trajectory)) return trajectory;
    if (typeof trajectory === 'string') {
        try {
            const parsed = JSON.parse(trajectory);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    }
    return [];
};

const trajectoryToGroups = (trajectory) => {
    const normalized = normalizeTrajectory(trajectory);
    if (!normalized.length) return [];

    return normalized
        .map((entry, index) => {
            const phase = typeof entry?.phase === 'string' ? entry.phase.trim() : '';
            const name = phase || `Phase ${index + 1}`;
            const actions = Array.isArray(entry?.actions) ? entry.actions : [];
            const lines = [];

            actions.forEach((action) => {
                if (!action) return;
                const tool = typeof action.tool === 'string' ? action.tool.trim() : '';
                const summary = typeof action.summary === 'string' ? action.summary.trim() : '';
                const result = typeof action.result === 'string' ? action.result.trim() : '';

                if (tool || summary) {
                    lines.push({
                        tool,
                        summary: summary || 'Action',
                        result: result ? `Result: ${result}` : '',
                    });
                }
            });

            return { name, lines };
        })
        .filter((group) => group.name || group.lines.length > 0);
};

/**
 * References placeholder shown while a run is streaming (MOTION-SPEC §7).
 *
 * A highlight wave travels top-to-bottom on a loop — at any instant one card group is at full
 * opacity and the rest are near-invisible. This is deliberately a shimmer and NOT progressive
 * loading: references only exist once the run completes, so an appearance of cards filling in
 * would be a lie about what the panel knows.
 */
const REFERENCE_SKELETON_CARDS = 6;
// Figma 44:4744 draws five bars per card; their widths are set positionally in scoped.css so the
// markup stays a plain list and the design's 80/232/200/180/160 ladder lives in one place.
const REFERENCE_SKELETON_BARS = 5;

const ReferencesSkeleton = () => (
    <div className="references-list ref-skeleton" aria-hidden="true">
        {Array.from({ length: REFERENCE_SKELETON_CARDS }).map((_, i) => (
            <div
                className="ref-skeleton-card"
                key={`ref-skeleton-${i}`}
                style={{ animationDelay: `${(i * 1.6) / REFERENCE_SKELETON_CARDS}s` }}
            >
                {Array.from({ length: REFERENCE_SKELETON_BARS }).map((__, j) => (
                    <span className="ref-skeleton-bar" key={`ref-skeleton-${i}-${j}`} />
                ))}
            </div>
        ))}
    </div>
);

// The agent occasionally emits raw citation placeholders (`[[c1]]`, `[[c1, c2]]`)
// that were never resolved into links. They carry no meaning for the reader, so
// they are stripped rather than shown as literal text next to the real badges.
const UNRESOLVED_CITATION_TOKEN = /\[\[\s*c\d+(?:\s*,\s*c\d+)*\s*\]\]/gi;

const stripUnresolvedCitations = (content) => {
    if (typeof content !== 'string' || !content.includes('[[')) return content;
    return content.replace(UNRESOLVED_CITATION_TOKEN, '');
};

// The tail of a streamed answer is a sentence caught mid-word, and markdown does not degrade
// gracefully when you cut it there: half of `[38743124](https://…` renders as literal text, and
// `## Citation` renders as a heading that vanishes a chunk later. Both flicker, and neither is in
// the finished answer. They are removed while the text is still arriving and left alone once it
// is final, so what the reader sees only ever grows.
const PARTIAL_CITATIONS_HEADING = /\n+#{1,6}[ \t]*c(i(t(a(t(i(o(n(s)?)?)?)?)?)?)?)?[ \t]*$/i;
const UNCLOSED_LINK = /\[[^\]\n]*\][ \t]*\([^)\n]*$/;
const UNCLOSED_BRACKET = /\[[^\]\n]*$/;

const tidyStreamingText = (content) => {
    if (typeof content !== 'string' || !content) return content;
    return content
        .replace(PARTIAL_CITATIONS_HEADING, '')
        .replace(UNCLOSED_LINK, '')
        .replace(UNCLOSED_BRACKET, '');
};

const rafSchedule = (fn) => (
    typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 33)
);
const rafCancel = (id) => {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
    else clearTimeout(id);
};

/**
 * A callback whose identity never changes but which always calls the latest closure.
 *
 * `MessageCard` is memoised, and six of the handlers it takes were re-created on every
 * render — which defeated the memo for EVERY card, so a single streamed chunk re-parsed
 * the markdown of every answer in the conversation, not just the one being written.
 */
const useStableCallback = (fn) => {
    const ref = useRef(fn);
    useLayoutEffect(() => { ref.current = fn; });
    return useCallback((...args) => ref.current?.(...args), []);
};

// Stable placeholders for the live-run props, which only the streaming card ever reads
// (every use inside MessageCard is behind `isLoading`). Passing the live values to the
// settled cards as well changed their props on every frame and re-rendered them for nothing.
// How far from the bottom the reader may be before auto-follow stops chasing them.
const AUTO_FOLLOW_SLACK_PX = 80;
/* Module scope, so it is the SAME array on every render. As a `{[remarkGfm]}` literal in the
   JSX it was a new array each time, which is enough on its own to defeat any memoisation of
   the markdown subtree — react-markdown rebuilds its unified processor when the plugin list
   is not identical, and the answer is re-parsed in full on every streamed frame. */
const REMARK_PLUGINS = [remarkGfm];
// The conversation is mirrored into sessionStorage for a reload mid-answer; it does not need
// to be written once per streamed chunk.
const CHAT_PERSIST_DEBOUNCE_MS = 400;

const NO_GROUPS = [];
const NO_KEYWORDS = [];
const NO_PAPERS = [];
const NO_DETAIL = {};

const enrichReferenceSummaries = (references, summaries) => references.map((ref) => {
    const pmid = extractPmidFromReference(ref);
    const summary = pmid && isPlaceholderPmidReference(ref) ? summaries[pmid] : null;
    if (!summary) return ref;
    return {
        ...ref, pmid,
        title: summary.title || ref.title,
        journal: summary.journal || ref.journal,
        year: summary.year || ref.year,
        authors: summary.authors || ref.authors,
        citation_count: 'N/A',
    };
});

const MessageCard = React.memo(function MessageCard({
    index,
    message,
    referenceSortOption,
    referenceSummaryMap,
    totalMessages,
    isProcessing,
    streamingGroups,
    streamingStepName,
    preamble,
    investigatePhase,
    investigateFunnel,
    investigateStartedAt,
    investigatePercent,
    investigateKeywords,
    investigatePapers,
    investigateDetail,
    pendingClarification,
    clarificationDrafts,
    clarificationError,
    clarificationSubmitting,
    hasInvalidOtherSelection,
    onUpdateClarificationDraft,
    onSubmitClarification,
    onSkipClarification,
    onDisplayFunnel,
    getDisplayFunnel,
    showReloadPrompt,
    onReloadLatest,
    onStop,
    refresh,
    copy,
    save,
    downloadConversation,
    onOpenFeedback,
    interactionLocked = false,
    conversationId,
    answerReady = false,
}) {
    const isAssistant = message.role === "assistant";
    const isLastUserMessage = index === totalMessages - 1 && message.role === 'assistant';
    const isLoading = isProcessing && isLastUserMessage;
    const messageID = index;
    /* Each chip is bound to one passage, so a paper cited twice for two different sentences
       shows two different quotes. The binding rides on the link as a `#cN` fragment — see
       utils/directCitations — which is why every read of a citation href goes through
       pmidFromHref rather than splitting on '/'. */
    const directCitations = useMemo(
        () => citationsFor(message.directCitations, message.content),
        [message.directCitations, message.content],
    );
    const citationsByMarker = useMemo(() => indexByMarker(directCitations), [directCitations]);

    // While the answer is still streaming there are no `references` yet, so every citation fell
    // through to its raw PMID and then snapped to a small number when the Complete frame landed —
    // the most visible way the streamed text differed from the finished one. The backend numbers
    // references by order of first appearance in the answer, so reading that same order off the
    // text already on screen gives each citation the number it is about to be given.
    // (With an active `ranking_mode` the backend reranks afterwards, so a number can still change
    // once — a far smaller change than an eight-digit PMID becoming a 1.)
    const streamingNumberByPmid = useMemo(() => {
        if ((message.references || []).length) return null;
        const order = new Map();
        const text = String(message.content || '');
        for (const match of text.matchAll(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/g)) {
            if (!order.has(match[1])) order.set(match[1], order.size + 1);
        }
        return order;
    }, [message.references, message.content]);

    const numberedReferences = useMemo(() => sortReferences(
        enrichReferenceSummaries(message.references || [], referenceSummaryMap || {})
            .map((reference, originalIndex) => ({ reference, originalIndex })),
        referenceSortOption,
    ), [message.references, referenceSummaryMap, referenceSortOption]);

    const getReferenceNumber = (href) => {
        const pmid = pmidFromHref(href);
        const match = numberedReferences.find(
            ({ reference }) => extractPmidFromReference(reference) === pmid
        );
        if (match) return match.displayNumber;
        return streamingNumberByPmid?.get(pmid) ?? null;
    };
    const allowUserEdit = !interactionLocked;

    /**
     * Reference hover card (Figma "Reference - Hover Preview"). Replaces the browser's native
     * `title` tooltip on a citation, which could only show one unstyled string and never said
     * which paper the quote came from.
     *
     * The close is delayed because the card is a hover target itself — Full Text and the bookmark
     * are only reachable if moving the pointer off the citation and onto the card does not dismiss
     * it on the way across the gap.
     */
    const [hoverCard, setHoverCard] = useState(null);
    const [bookmarkedPmids, setBookmarkedPmids] = useState(() => new Set());
    const hoverCloseTimer = useRef(null);

    const cancelHoverClose = () => {
        if (hoverCloseTimer.current) {
            clearTimeout(hoverCloseTimer.current);
            hoverCloseTimer.current = null;
        }
    };

    const showReferenceCard = (href, element) => {
        cancelHoverClose();
        const pmid = pmidFromHref(href);
        const reference = (message.references || []).find(
            (item) => extractPmidFromReference(item) === pmid,
        );
        if (!reference || !element) return;
        setHoverCard({
            reference,
            number: getReferenceNumber(href),
            rect: element.getBoundingClientRect(),
            // The passage this particular chip rests on, when the answer bound one.
            // Without it the card falls back to the reference's own evidence, which is
            // the same blob for every chip that cites the paper.
            citation: citationsByMarker.get(markerFromHref(href)) || null,
        });
    };

    const hideReferenceCard = () => {
        cancelHoverClose();
        hoverCloseTimer.current = setTimeout(() => setHoverCard(null), 160);
    };

    useEffect(() => () => cancelHoverClose(), []);

    useEffect(() => {
        const sync = (event) => {
            const list = event?.detail || getBookmarks();
            setBookmarkedPmids(new Set(
                (Array.isArray(list) ? list : []).map((item) => String(item.id ?? item.pmid ?? '')),
            ));
        };
        sync();
        window.addEventListener('glkb-bookmarks-updated', sync);
        return () => window.removeEventListener('glkb-bookmarks-updated', sync);
    }, []);

    const [editContent, setEditContent] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [thoughtsExpanded, setThoughtsExpanded] = useState(() => isLoading);
    const [animatedStepLabel, setAnimatedStepLabel] = useState('');
    const [stepLabelPhase, setStepLabelPhase] = useState('idle');
    const [investigateExpanded, setInvestigateExpanded] = useState(true);
    const allowResponseRefresh = true;
    const stepLabelTimersRef = useRef([]);
    const renderedStepLabelRef = useRef('');
    const thoughtDurationLabel = formatDuration(message.thoughtDurationMs);
    const investigatedDurationLabel = formatInvestigatedDuration(message.thoughtDurationMs);
    const isInvestigateMessage = Boolean(message.investigateMode);
    const groupedThoughts = useMemo(
        () => groupThinkingSteps(message.thinkingSteps),
        [message.thinkingSteps]
    );
    const trajectoryGroups = useMemo(
        () => trajectoryToGroups(message.trajectory),
        [message.trajectory]
    );
    const activeStreamingGroups = isLoading ? streamingGroups : [];
    const staticGroups = !isLoading
        ? (
            (isInvestigateMessage && groupedThoughts.length)
                ? groupedThoughts
                : (trajectoryGroups.length ? trajectoryGroups : groupedThoughts)
        )
        : groupedThoughts;
    const displayGroups = isLoading ? activeStreamingGroups : staticGroups;
    const hasDisplayGroups = displayGroups.length > 0;
    const isTrajectoryDisplay = !isLoading && !isInvestigateMessage && trajectoryGroups.length > 0;
    const loadingCurrentIndex = isLoading ? displayGroups.length - 1 : -1;
    const currentStepLabel = useMemo(() => {
        if (!isLoading) return '';
        if (streamingStepName) return getStepLabel(streamingStepName);
        if (!activeStreamingGroups.length) return 'Thinking';
        return getStepLabel(activeStreamingGroups[activeStreamingGroups.length - 1].name);
    }, [isLoading, streamingStepName, activeStreamingGroups]);
    const loadingStepLabel = useMemo(() => {
        if (!isLoading) return '';
        if (!currentStepLabel) return 'Thinking...';
        return currentStepLabel.endsWith('...') ? currentStepLabel : `${currentStepLabel}...`;
    }, [isLoading, currentStepLabel]);
    /* The answer text is final the moment the `Answer` frame lands; what runs after it is
       reference reranking, full-text link resolution (an NCBI call, rate-limited) and the KG
       query build. That is not the model thinking, and an animated "…" over a finished answer
       reads as a run that has stalled rather than one that is tidying up.
    
       Only the header changes. `displayGroups`, the expand control and everything keyed on
       `isLoading` stay as they are, so the thought list does not swap contents or collapse
       underneath the reader while the last frames arrive. */
    const thinkingIsOver = !isLoading || answerReady;
    const thoughtHeaderText = !thinkingIsOver
        ? (animatedStepLabel || loadingStepLabel)
        : (thoughtDurationLabel ? `Thought for ${thoughtDurationLabel}` : 'Thought summary');
    const showInvestigateProgress = isAssistant && isInvestigateMessage && isLoading;
    // Once an investigation finishes its thinking collapses into this eyebrow —
    // it should never vanish outright, so a missing duration still renders the row
    // as long as there are thoughts behind it.
    const showInvestigateSummary = isAssistant && isInvestigateMessage && !isLoading
        && (Boolean(investigatedDurationLabel) || hasDisplayGroups);
    const showThoughtHeader = isAssistant && (
        !isInvestigateMessage && (isLoading || thoughtDurationLabel || hasDisplayGroups)
    );
    const showReloadInMessage = showReloadPrompt && isLastUserMessage && isAssistant && !isLoading;
    const canToggleThoughts = !isLoading && hasDisplayGroups;
    const investigateStageLabels = ['Retrieved', 'Screened', 'Extracted', 'Cited'];
    const resolvedPhase = useMemo(() => {
        if (!isInvestigateMessage) return null;
        if (isLoading) {
            // 'planning' is the pre-retrieval floor: the panel mounts at submit, before any frame
            // has arrived, and must not claim to be searching yet.
            return investigatePhase
                || inferInvestigatePhase(streamingStepName, '')
                || 'planning';
        }
        return message.investigatePhase || 'verifying';
    }, [isInvestigateMessage, isLoading, investigatePhase, streamingStepName, message.investigatePhase]);

    const resolvedFunnel = useMemo(() => {
        const fromMessage = message.investigateFunnel || null;
        if (!isLoading) return fromMessage || emptyFunnel();
        // Structured fields only. This used to also regex-scrape numbers out of the raw tool-log
        // lines and let THOSE win, which is how Retrieved could jump to 9,000 and then fall back
        // to 3,000: any stray figure in a tool result that happened to sit near the word
        // "retrieved" overwrote the agent's real count, and the merge was last-write-wins rather
        // than monotonic. The agent now reports every funnel number on every phase, so the
        // scraper has nothing to add and plenty to break.
        return investigateFunnel || emptyFunnel();
    }, [isLoading, message.investigateFunnel, investigateFunnel]);

    // Figma: real % bar when agent sends percent; else phase floor (monotonic)
    // After complete: prefer message.investigatePercent (persisted on final)
    const displayPercent = useMemo(() => {
        const live = Number.isFinite(Number(investigatePercent))
            ? Math.max(0, Math.min(100, Math.round(Number(investigatePercent))))
            : null;
        const saved = Number.isFinite(Number(message.investigatePercent))
            ? Math.max(0, Math.min(100, Math.round(Number(message.investigatePercent))))
            : null;
        if (showInvestigateProgress) {
            if (live != null) return live;
            return PHASE_PERCENT_FLOOR[resolvedPhase] ?? 8;
        }
        // completed card: show 100 if we have funnel/phase saved
        if (showInvestigateSummary) return saved ?? (isInvestigateMessage ? 100 : null);
        return null;
    }, [
        showInvestigateProgress,
        showInvestigateSummary,
        investigatePercent,
        message.investigatePercent,
        resolvedPhase,
        isInvestigateMessage,
    ]);

    const resolvedKeywords = useMemo(() => {
        if (isLoading && Array.isArray(investigateKeywords) && investigateKeywords.length) {
            return investigateKeywords;
        }
        if (Array.isArray(message.investigateKeywords) && message.investigateKeywords.length) {
            return message.investigateKeywords;
        }
        return Array.isArray(investigateKeywords) ? investigateKeywords : [];
    }, [isLoading, investigateKeywords, message.investigateKeywords]);

    const resolvedPapers = useMemo(() => {
        if (isLoading && Array.isArray(investigatePapers) && investigatePapers.length) {
            return investigatePapers;
        }
        if (Array.isArray(message.investigatePapers) && message.investigatePapers.length) {
            return message.investigatePapers;
        }
        return Array.isArray(investigatePapers) ? investigatePapers : [];
    }, [isLoading, investigatePapers, message.investigatePapers]);

    // Tool call step icons — maps tool names and phase labels to MUI icons (per Figma)
    const toggleGroup = useCallback((nextIndex) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [nextIndex]: !prev[nextIndex],
        }));
    }, []);

    useEffect(() => {
        if (isLoading) {
            setThoughtsExpanded(true);
            return;
        }
        setThoughtsExpanded(false);
    }, [isLoading, conversationId]);

    useEffect(() => {
        if (showInvestigateProgress) {
            setInvestigateExpanded(true);
        }
    }, [showInvestigateProgress]);

    useEffect(() => {
        const clearTimers = () => {
            stepLabelTimersRef.current.forEach((timerId) => clearTimeout(timerId));
            stepLabelTimersRef.current = [];
        };

        clearTimers();

        if (!isLoading || !loadingStepLabel) {
            renderedStepLabelRef.current = '';
            setAnimatedStepLabel('');
            setStepLabelPhase('idle');
            return undefined;
        }

        const currentLabel = renderedStepLabelRef.current;
        if (!currentLabel) {
            renderedStepLabelRef.current = loadingStepLabel;
            setAnimatedStepLabel(loadingStepLabel);
            setStepLabelPhase('idle');
            return undefined;
        }

        if (currentLabel === loadingStepLabel) {
            /* Settle the phase before leaving, rather than leaving it wherever the last run
               put it.

               A swap goes out (opacity 0), then swap, then in, on chained timers, and every
               run of this effect cancels those timers first. `renderedStepLabelRef` is only
               advanced inside the first timer, so a label that changes and changes BACK inside
               that ~220ms window — which the step labels do, cycling through a tool name and
               returning to Processing — arrives here with the label matching again and the
               timers already cancelled. The phase was still `out`, and nothing left to move
               it: `.loading-step-label--out` is opacity 0, so the only thing on screen during
               thinking became invisible, and stayed that way until the label changed to
               something genuinely different. The body renders nothing while the answer is
               empty, so the card read as completely blank. */
            setStepLabelPhase((phase) => (phase === 'idle' ? phase : 'idle'));
            return undefined;
        }

        const OUT_MS = 140;
        const BUFFER_MS = 80;
        const IN_MS = 180;
        const SWAP_MS = 16;

        setStepLabelPhase('out');
        const outTimer = setTimeout(() => {
            renderedStepLabelRef.current = loadingStepLabel;
            setAnimatedStepLabel(loadingStepLabel);
            setStepLabelPhase('swap');
            const swapTimer = setTimeout(() => {
                setStepLabelPhase('in');
                const inTimer = setTimeout(() => {
                    setStepLabelPhase('idle');
                }, IN_MS);
                stepLabelTimersRef.current.push(inTimer);
            }, SWAP_MS);
            stepLabelTimersRef.current.push(swapTimer);
        }, OUT_MS + BUFFER_MS);
        stepLabelTimersRef.current.push(outTimer);

        return clearTimers;
    }, [isLoading, loadingStepLabel]);


    /* The answer's text, split where its in-text knowledge graph goes (components/
       AnswerGraph). Only a finished answer gets a graph: while it streams, the split point
       would move under the reader. */
    const answerMarkdown = stripUnresolvedCitations(
            bindMarkersToLinks(
                stripCitationsBlock(
                    repairOrphanSingleItemMarkdown(
                        isLoading
                            ? tidyStreamingText(message.content)
                            : message.content,
                    ),
                ),
                citationsByMarker,
            ),
        );
    // Investigate answers, and an AI Chat answer whose turn asked for its graph (Options →
    // Include in the answer); any other chat answer has none and never asks the backend for one.
    // With no list, an answer that cites papers still gets a graph, built from its text.
    const showAnswerGraph = isAssistant && !isLoading
        && (isInvestigateMessage || Boolean(message.answerOptions?.graph))
        && (message.kgQueryList?.length > 0 || message.references?.length > 0);
    const answerParts = showAnswerGraph ? splitAnswerForGraph(answerMarkdown) : [answerMarkdown];
    if (showAnswerGraph && answerParts.length === 1) answerParts.push('');
    const markdownComponents = {
        /* Wide tables scroll inside their own box.
           Without this a table with more columns
           than the message column can hold widens
           the whole answer and puts a horizontal
           scrollbar under the entire conversation. */
        table: ({ node, ...props }) => (
            <div className="markdown-table-scroll">
                <table {...props} />
            </div>
        ),
        a: ({ href, children, title, ...props }) => {
            const isPubMedReference = href?.includes('pubmed.ncbi.nlm.nih.gov');
            const referenceNumber = isPubMedReference ? getReferenceNumber(href) : null;
            if (!isPubMedReference) {
                return <a href={href} title={title} {...props}>{children}</a>;
            }
            // The marker is ours; PubMed should not be sent it.
            const linkHref = hrefWithoutMarker(href);
            // `title` is dropped on purpose: the agent puts the evidence
            // sentence there, and leaving it would show the browser's own
            // tooltip on top of the card that now presents the same quote
            // with the paper it came from.
            return (
                <a
                    href={linkHref}
                    {...props}
                    onMouseEnter={(event) => showReferenceCard(href, event.currentTarget)}
                    onMouseLeave={hideReferenceCard}
                    onFocus={(event) => showReferenceCard(href, event.currentTarget)}
                    onBlur={hideReferenceCard}
                >
                    <span className="inline-citation-number">{referenceNumber || children}</span>
                </a>
            );
        },
    };

    return (
        <div
            className="message-card"
            data-message-index={index}
            data-message-role={message.role}
        >
            <Container className="message-pair" key={index} sx={{ display: "flex", flexDirection: "row", alignItems: "flex-end", mb: isAssistant ? '5px' : 0, justifyContent: "flex-end" }}>
                <Box
                    sx={{
                        bgcolor: isAssistant ? "transparent" : "var(--color-background-muted)",
                        boxShadow: "none",
                        /* 45:1176/1177 — the question is a background/muted bubble at radius/2,
                           8 by 16, holding body-lg, and its text is capped at 560 rather than at
                           a fraction of the column. 80% of a wide column is a very long line to
                           read; 560 is the measure the frame sets. */
                        maxWidth: isAssistant ? "100%" : "560px",
                        width: isAssistant ? "100%" : "auto",
                        display: "flex",
                        alignItems: "flex-start",
                        px: isAssistant ? "0px" : "16px",
                        pt: isAssistant ? "12px" : "8px",
                        pb: isAssistant ? "24px" : "8px",
                        borderColor: "divider",
                        borderRadius: isAssistant ? "24px" : "var(--radius-2, 8px)",
                        ...(isAssistant ? {} : {
                            fontSize: "16px",
                            fontWeight: 400,
                            lineHeight: "26px",
                            color: "var(--color-text-secondary)",
                        }),
                        // The assistant fills the column; the user bubble hugs its
                        // text, so a one-line question is a one-line-wide box.
                        flex: isAssistant ? 1 : "0 1 auto",
                    }}
                >
                    <Box sx={{ flex: 1, maxWidth: "100%" }}>
                        {showInvestigateSummary && (
                            <Box
                                className={`investigate-summary-row${canToggleThoughts ? ' can-toggle' : ''}`}
                                role={canToggleThoughts ? 'button' : undefined}
                                tabIndex={canToggleThoughts ? 0 : -1}
                                onClick={canToggleThoughts ? () => setThoughtsExpanded((prev) => !prev) : undefined}
                                onKeyDown={(event) => {
                                    if (!canToggleThoughts) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setThoughtsExpanded((prev) => !prev);
                                    }
                                }}
                                aria-label={canToggleThoughts ? (thoughtsExpanded ? 'Collapse investigation thoughts' : 'Expand investigation thoughts') : undefined}
                            >
                                <ScienceOutlinedIcon className="investigate-summary-icon" />
                                <span className="investigate-summary-text">
                                    {investigatedDurationLabel ? `Investigated for ${investigatedDurationLabel}` : 'Investigation details'}
                                </span>
                                {canToggleThoughts && (
                                    <ChevronRightIcon className={`investigate-summary-chevron${thoughtsExpanded ? ' expanded' : ''}`} />
                                )}
                            </Box>
                        )}
                        {showInvestigateSummary && (resolvedFunnel?.retrieved != null || resolvedFunnel?.cited != null) && (
                            <Box className="investigate-funnel-summary" aria-label="Investigation funnel">
                                {investigateStageLabels.map((label) => {
                                    const key = label.toLowerCase();
                                    return (
                                        <span key={label} className="investigate-funnel-chip">
                                            <strong>{formatFunnelValue(resolvedFunnel?.[key])}</strong> {label}
                                        </span>
                                    );
                                })}
                            </Box>
                        )}

                        {showInvestigateProgress && (
                            <InvestigateProgress
                                phase={resolvedPhase}
                                funnel={resolvedFunnel}
                                percent={displayPercent}
                                keywords={resolvedKeywords}
                                papers={resolvedPapers}
                                detail={investigateDetail}
                                label={investigateDetail?.label || streamingStepName || ''}
                                /* Same `Date.now()` the final "Investigated for m:ss" is measured
                                   from, so the live clock and the summary row cannot disagree. */
                                startedAt={investigateStartedAt}
                                /* The agent's `summary` frame lands just before the report itself,
                                   so this is the brief terminal beat: bar at 100%, clock freezes,
                                   title becomes "Report ready" — then the panel gives way to the
                                   "Investigated for m:ss" summary row when the run closes. */
                                done={resolvedPhase === 'summary'}
                                /* A clarify round PAUSES the run: the harness is blocked on
                                   the user's answer and nothing is being retrieved, read or
                                   written. Everything that conveys motion has to stop with
                                   it — the counters, the bar's creep, the clock — or the
                                   panel claims progress that is not happening while the
                                   reader is being asked to supply the missing piece. */
                                paused={Boolean(pendingClarification)}
                                onDisplayFunnel={onDisplayFunnel}
                                getDisplayFunnel={getDisplayFunnel}
                                expanded={investigateExpanded}
                                onToggleExpanded={() => setInvestigateExpanded((prev) => !prev)}
                            />
                        )}

                        {showThoughtHeader && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mt: '8px',
                                mb: '8px',
                            }}>
                                <Box
                                    role={canToggleThoughts ? 'button' : undefined}
                                    tabIndex={canToggleThoughts ? 0 : -1}
                                    onClick={canToggleThoughts ? () => setThoughtsExpanded((prev) => !prev) : undefined}
                                    onKeyDown={(event) => {
                                        if (!canToggleThoughts) return;
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            setThoughtsExpanded((prev) => !prev);
                                        }
                                    }}
                                    aria-label={
                                        canToggleThoughts
                                            ? (thoughtsExpanded ? 'Collapse thoughts' : 'Expand thoughts')
                                            : undefined
                                    }
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: isLoading ? '4px 0' : '2px 0',
                                        borderRadius: '18px',
                                        cursor: canToggleThoughts ? 'pointer' : 'default',
                                        '&:hover': canToggleThoughts ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : undefined,
                                    }}
                                >
                                    <Box
                                        component="span"
                                        className={isLoading
                                            ? `loading-step-label${stepLabelPhase !== 'idle' ? ` loading-step-label--${stepLabelPhase}` : ''}`
                                            : undefined}
                                        sx={{
                                            fontFamily: isLoading ? 'DM Sans, sans-serif' : 'Geist, sans-serif',
                                            fontSize: isLoading ? '16px' : '12px',
                                            fontWeight: isLoading ? 400 : 600,
                                            lineHeight: isLoading ? 'normal' : '20px',
                                            color: isLoading ? 'transparent' : 'var(--color-text-tertiary)',
                                            WebkitTextFillColor: isLoading ? 'transparent' : undefined,
                                        }}
                                    >
                                        {thoughtHeaderText}
                                    </Box>
                                    {canToggleThoughts && (
                                        <ExpandMoreIcon
                                            sx={{
                                                fontSize: '16px',
                                                color: 'var(--color-text-tertiary)',
                                                transform: thoughtsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease',
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* The opening line, while the run is still going. It cannot live in the
                            group list below: that list is gated on `!isLoading`, so every thought
                            group is drawn only after the answer has landed, and during the run the
                            panel is one animated status line. This is the one thing that has to be
                            readable DURING the wait, so it gets its own slot. */}
                        {isAssistant && isLoading && preamble && (
                            <Box sx={{
                                mt: '6px',
                                ml: 1,
                                pl: '10px',
                                borderLeft: '2px solid var(--color-border-default)',
                            }}>
                                <Typography sx={{
                                    fontFamily: 'DM Sans, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: 1.5,
                                    color: 'var(--color-text-tertiary)',
                                }}>
                                    {preamble}
                                </Typography>
                            </Box>
                        )}

                        {isAssistant && !isLoading && thoughtsExpanded && hasDisplayGroups && (
                            <Box sx={{
                                mt: '6px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0px',
                                borderLeft: '2px solid var(--color-border-default)',
                                pl: '4px',
                                ml: 1,
                            }}>
                                {displayGroups.map((group, groupIndex) => (
                                    <ThoughtGroup
                                        key={`${group.name}-${groupIndex}`}
                                        group={group}
                                        groupIndex={groupIndex}
                                        expanded={isLoading ? groupIndex === loadingCurrentIndex : !!expandedGroups[groupIndex]}
                                        onToggle={toggleGroup}
                                        disableAnimation={isLoading}
                                        disableToggle={isLoading}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* The images and PDFs the question was sent with. */}
                        {!isAssistant && <MessageAttachments attachments={message.attachments} />}

                        {/* Separates the body from the investigate summary and thinking rows
                            above it. The user bubble has none of those, so on that side the
                            margin was just 8px of dead space above the text — 20px above it
                            against 12px below, inside padding that is 12px on both sides. */}
                        <Box mt={isAssistant ? 1 : 0}>
                            {showReloadInMessage ? (
                                <Box
                                    sx={{
                                        backgroundColor: 'var(--color-background-subtle)',
                                        borderRadius: '8px',
                                        padding: '6px 8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'DM Sans, sans-serif',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: 'var(--color-text-tertiary)',
                                        }}
                                    >
                                        Response interrupted. Reload latest message.
                                    </Typography>
                                    <MuiButton
                                        variant="outlined"
                                        size="small"
                                        onClick={onReloadLatest}
                                        sx={{
                                            textTransform: 'none',
                                            fontFamily: 'DM Sans, sans-serif',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            minHeight: '28px',
                                            padding: '2px 8px',
                                            borderRadius: '8px',
                                            borderColor: 'var(--color-border-strong)',
                                            color: 'var(--color-text-tertiary)',
                                            '&:hover': {
                                                borderColor: 'var(--color-grey-300)',
                                                backgroundColor: 'var(--color-background-muted)',
                                            },
                                        }}
                                    >
                                        Reload
                                    </MuiButton>
                                </Box>
                            ) : (
                                // While the run is in flight this used to render `null`
                                // unconditionally, so the answer could only appear once
                                // `isProcessing` went false — which is why streaming it made no
                                // visible difference: the text was in state, and the body was
                                // not being drawn. It stays hidden only until there is text to
                                // show, so a deep-research turn (no Delta frames, content empty
                                // until the end) looks exactly as it did before.
                                isLoading && !message.content
                            ) ? null :
                                isEditing ?
                                    <TextField
                                        hiddenLabel
                                        multiline
                                        id="filled-hidden-label-small"
                                        value={editContent}
                                        variant="filled"
                                        size="small"
                                        sx={{ flex: 1, width: "100%" }}
                                        onChange={(event) => setEditContent(event.target.value)}
                                    /> : (
                                        <div className="markdown-body">
                                            {answerParts.map((part, partIndex) => (
                                                <React.Fragment key={partIndex}>
                                                    {partIndex === 1 && (
                                                        <AnswerGraphForMessage kgQueryList={message.kgQueryList} answer={message.content} />
                                                    )}
                                                    {part && (
                                                        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
                                                            {part}
                                                        </ReactMarkdown>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            {hoverCard && (
                                                <ReferenceHoverCard
                                                    reference={hoverCard.reference}
                                                    citation={hoverCard.citation}
                                                    number={hoverCard.number}
                                                    anchorRect={hoverCard.rect}
                                                    isBookmarked={bookmarkedPmids.has(
                                                        String(extractPmidFromReference(hoverCard.reference)),
                                                    )}
                                                    onMouseEnter={cancelHoverClose}
                                                    onMouseLeave={hideReferenceCard}
                                                    onBookmark={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        const pmid = extractPmidFromReference(hoverCard.reference);
                                                        toggleBookmark({ ...hoverCard.reference, pmid })
                                                            .then((next) => window.dispatchEvent(new CustomEvent(
                                                                'glkb-bookmarks-updated', { detail: next },
                                                            )))
                                                            .catch(() => {});
                                                    }}
                                                    onCite={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        copy(`[${hoverCard.number}] ${hoverCard.reference.title || ''} `
                                                            + `PMID: ${extractPmidFromReference(hoverCard.reference)}`);
                                                    }}
                                                    onFullText={() => setHoverCard(null)}
                                                />
                                            )}
                                        </div>
                                    )}
                        </Box>

                        {isAssistant && (
                            <Stack className="message-action-row" direction="row" mt={2} sx={{ pb: "8px" }}>
                                {!isLoading && (
                                    <IconButton
                                        size="small"
                                        onClick={() => copy(message.content)}
                                        title="Copy response"
                                        aria-label="Copy response"
                                    >
                                        <ContentCopyIcon style={{ width: '16px', height: '16px', display: 'block', color: 'var(--color-grey-400)' }} />
                                    </IconButton>
                                )}
                                {allowResponseRefresh && isLastUserMessage && !isLoading && !interactionLocked && (
                                    <IconButton
                                        size="small"
                                        onClick={() => refresh(null, index)}
                                        title="Regenerate response"
                                    >
                                        <ReplayIcon style={{ width: '16px', height: '16px', display: 'block', color: 'var(--color-grey-400)' }} />
                                    </IconButton>
                                )}
                                {!isLoading && <IconButton size="small" onClick={() => downloadConversation(messageID)} title="Download this Q&A">
                                    <DownloadIcon
                                        aria-label="Download"
                                        style={{ width: '16px', height: '16px', display: 'block', color: 'var(--color-grey-400)' }}
                                    />
                                </IconButton>}
                                {!isLoading && (
                                    <IconButton
                                        size="small"
                                        onClick={onOpenFeedback}
                                        title="Share feedback"
                                    >
                                        <ThumbsUpDownIcon style={{ width: '16px', height: '16px', display: 'block', color: 'var(--color-grey-400)' }} />
                                    </IconButton>
                                )}
                            </Stack>
                        )}

                    </Box>

                </Box>

            </Container>
            {!isAssistant && <Box sx={{ justifyContent: "flex-end", direction: "row", display: "flex", alignItems: "center" }}>
                <Stack className="message-action-row user-action-row" direction="row" sx={{ pb: "8px", pr: { xs: '16px', sm: '24px' }, mt: '16px' }}>
                    {
                        isEditing ? <>
                            <IconButton size="small" title="Cancel editing" onClick={() => {
                                setIsEditing(false);
                                setEditContent('');
                            }}>
                                <ClearIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="Save message" onClick={(event) => {
                                if (editContent.trim() === '') {
                                    return;
                                }
                                save(event, messageID, editContent);
                                setIsEditing(false);
                                setEditContent('');
                            }} disabled={interactionLocked}>
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        </> : <div className="user-message-actions">
                            <IconButton
                                size="small"
                                onClick={() => copy(message.content)}
                                title="Copy message"
                                aria-label="Copy message"
                            >
                                <ContentCopyIcon style={{ width: '16px', height: '16px', display: 'block', color: 'var(--color-grey-400)' }} />
                            </IconButton>
                            {allowUserEdit && (
                                <IconButton
                                    size="small"
                                    title="Edit message"
                                    onClick={() => {
                                        if (!allowUserEdit || isAssistant) return;

                                        setIsEditing(true);
                                        setEditContent(message.content);
                                    }}
                                >
                                    <EditNoteIcon />
                                </IconButton>
                            )}
                        </div>
                    }

                </Stack>
            </Box>}
        </div>
    );
});

function LLMAgent({ isRouteActive = true }) {
    const location = useLocation();
    /* SSE callbacks belong to the render that started the request. Read route visibility
       through a ref so a long Investigate run can tell that the reader has since left /chat. */
    const isRouteActiveRef = useRef(isRouteActive);
    isRouteActiveRef.current = isRouteActive;
    /* The conversation this page is showing, taken from the address bar.
    
       Before this existed the id lived only in router state and sessionStorage, so a link
       could not open a conversation and closing the tab lost which one was open — the page
       came back empty and bounced to the home page. */
    const { publicId: routePublicId } = useParams();
    const initialRunSnapshotRef = useRef(undefined);
    if (initialRunSnapshotRef.current === undefined) {
        initialRunSnapshotRef.current = readActiveRunSnapshot() || null;
    }
    const initialRunSnapshot = initialRunSnapshotRef.current;
    const [userInput, setUserInput] = useState('');
    /**
     * Follow-ups typed while an answer is still being written.
     *
     * Sending one straight away is not an option: the agent is mid-stream and a second turn on
     * the same conversation would race the first. Refusing it is what this replaced — the
     * reader had to hold the question in their head for the length of a run, measured at 19 to
     * 68 seconds. So it is queued: the text leaves their hands at once, and the run that
     * carries it starts when the current one ends.
     *
     * A list rather than one slot — someone who thinks of two things should not have to wait
     * for the first answer before writing the second. They go out oldest first, each waiting
     * for the previous answer.
     */
    const [queuedPrompts, setQueuedPrompts] = useState(
        () => (Array.isArray(initialRunSnapshotRef.current?.queuedPrompts)
            ? initialRunSnapshotRef.current.queuedPrompts
            : []),
    );
    /* Ids keep climbing past anything restored from a snapshot, so a follow-up asked after a
       reload cannot collide with one that survived it. */
    const queueSeqRef = useRef(
        (initialRunSnapshotRef.current?.queuedPrompts || []).reduce((max, item) => {
            const n = Number(String(item?.id || '').replace(/^q-/, ''));
            return Number.isFinite(n) && n > max ? n : max;
        }, 0),
    );

    /* Fold a restored conversation's queued follow-ups back in, without duplicating any the
       live state already holds — a restore can run more than once for the same mount. */
    const restoreQueuedPrompts = useCallback((entries) => {
        if (!Array.isArray(entries) || !entries.length) return;
        setQueuedPrompts((prev) => {
            /* The entry's own identity (see `queueEntryIdentity`): the id alone once ids
               became globally unique, the old composite for a legacy `q-N`, where two
               conversations' snapshots can each hold one. */
            const identityFor = queueEntryIdentity;
            const known = new Set(prev.map(identityFor));
            const missing = pendingQueuedPrompts(entries).filter((item) => (
                item?.id && !known.has(identityFor(item))
            ));
            if (!missing.length) return prev;
            missing.forEach((item) => {
                const n = Number(String(item?.id || '').replace(/^q-/, ''));
                if (Number.isFinite(n) && n > queueSeqRef.current) queueSeqRef.current = n;
            });
            return [...prev, ...missing];
        });
    }, []);
    /* The answer text has landed and will not change; only the trailing annotations are still
       coming. Presentation only — `isLoading` and `isProcessing` are untouched, so the run is
       still a run: the composer stays as it was and nothing new can start. */
    const [answerReady, setAnswerReady] = useState(false);
    const [chatHistory, setChatHistory] = useState(() => {
        const initialQuery = location.state?.initialQuery;
        if (initialQuery) {
            const initialAttachments = normalizeAttachmentList(
                location.state?.initialSearchOptions?.attachments,
            );
            return [{
                role: 'user',
                content: initialQuery,
                references: [],
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                investigateMode: Boolean(location.state?.initialSearchOptions?.investigateEnabled),
                ...(initialAttachments.length ? { attachments: initialAttachments } : {}),
            }];
        }
        return mergeMessagesWithRunSnapshot(
            baseMessagesForSnapshot(initialRunSnapshot),
            initialRunSnapshot,
        );
    });
    const runningConversationIdRef = useRef(
        initialRunSnapshot?.active ? initialRunSnapshot.conversationId : null,
    );
    const runningChatHistoryRef = useRef(
        initialRunSnapshot?.active
            ? mergeMessagesWithRunSnapshot(
                baseMessagesForSnapshot(initialRunSnapshot),
                initialRunSnapshot,
            )
            : [],
    );
    const runHistoryUpdaterRef = useRef(null);
    const [runHistoryRevision, setRunHistoryRevision] = useState(0);
    const [selectedMessageIndex, setSelectedMessageIndex] = useState(null);
    const [isLoading, setIsLoading] = useState(() => Boolean(initialRunSnapshot?.active));
    const [streamingGroups, setStreamingGroups] = useState(
        () => initialRunSnapshot?.streamingGroups || [],
    );
    // The cheap-tier opening line as it streams. Held as STATE, not only in the thought refs:
    // the thought list is not rendered at all while `isLoading`, so a ref that only feeds it
    // cannot put anything on screen during the wait this line exists to fill.
    const [preambleText, setPreambleText] = useState(initialRunSnapshot?.preambleText || '');
    const [streamingStepName, setStreamingStepName] = useState(
        initialRunSnapshot?.streamingStepName || '',
    );
    const [isProcessing, setIsProcessing] = useState(() => Boolean(initialRunSnapshot?.active));
    const [leftPaneWidth, setLeftPaneWidth] = useState(66);
    const [isDraggingSplit, setIsDraggingSplit] = useState(false);
    const [dragIndicatorY, setDragIndicatorY] = useState(0);
    const [isReferencesCollapsed, setIsReferencesCollapsed] = useState(false);
    const [isPhoneDevice, setIsPhoneDevice] = useState(isPhoneViewport);
    const [isMobileReferencesDrawerOpen, setIsMobileReferencesDrawerOpen] = useState(false);
    const [conversationsState, setConversationsState] = useState(() => getConversations());
    const [activeConversationId, setActiveConversationIdState] = useState(
        () => initialRunSnapshot?.conversationId || getActiveConversationId(),
    );
    const [isConversationLoading, setIsConversationLoading] = useState(false);
    const [loadingConversationId, setLoadingConversationId] = useState(null);
    const [conversationBookmarks, setConversationBookmarksState] = useState(() => getConversationBookmarks());
    /* "Response interrupted — reload the latest message" is for a run that cannot be picked up
       again. It renders INSTEAD of the answer body, so offering it while a reattach is under
       way covers the very answer that is on its way in, and the reader is invited to spend a
       second run getting what the first one already produced.

       A snapshot with a session id means the agent still has the run and polling will land it,
       so there is nothing interrupted to report. */
    const [showReloadPrompt, setShowReloadPrompt] = useState(
        () => (getStoredProcessingFlag() || getStoredIncompleteFlag())
            && !initialRunSnapshotRef.current?.sessionId
    );
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedbackRating, setFeedbackRating] = useState(0);
    const [feedbackText, setFeedbackText] = useState('');
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackSuccessOpen, setFeedbackSuccessOpen] = useState(false);
    const [feedbackSuccessText, setFeedbackSuccessText] = useState('Feedback submitted.');
    const [isEditingChatTitle, setIsEditingChatTitle] = useState(false);
    const [chatTitleDraft, setChatTitleDraft] = useState('');
    const [isQueryLimitReached, setIsQueryLimitReached] = useState(false);
    // What the reader has left — credits signed in, free questions as a guest (service/credits.js).
    const [usage, setUsage] = useState(() => getUsageSnapshot());
    const [pendingClarification, setPendingClarification] = useState(
        initialRunSnapshot?.pendingClarification || null,
    );
    const [clarificationDrafts, setClarificationDrafts] = useState(
        () => initialRunSnapshot?.clarificationDrafts || {},
    );
    const [clarificationError, setClarificationError] = useState('');
    const [clarificationSubmitting, setClarificationSubmitting] = useState(false);
    const [investigatePhase, setInvestigatePhase] = useState(
        initialRunSnapshot?.investigatePhase || 'searching',
    );
    const [investigateFunnel, setInvestigateFunnel] = useState(
        () => initialRunSnapshot?.investigateFunnel || emptyFunnel(),
    );
    const [investigateStartedAt, setInvestigateStartedAt] = useState(
        initialRunSnapshot?.investigateStartedAt || null,
    );
    const [investigatePercent, setInvestigatePercent] = useState(
        initialRunSnapshot?.investigatePercent ?? null,
    );
    const [investigateKeywords, setInvestigateKeywords] = useState(
        () => initialRunSnapshot?.investigateKeywords || [],
    );
    const [investigatePapers, setInvestigatePapers] = useState(
        () => initialRunSnapshot?.investigatePapers || [],
    );
    // The structured fields of the progress frames seen so far (topic, facets, n_claims,
    // n_conflicted, section/step/total). Accumulated rather than replaced, so a later frame that
    // omits a field does not blank the active step's detail block.
    const [investigateDetail, setInvestigateDetail] = useState(
        () => initialRunSnapshot?.investigateDetail || {},
    );
    const [notifyEmailEnabled, setNotifyEmailEnabled] = useState(() => getNotifyPrefs().email);

    // The same preference has a row in Settings and a toggle here; either can
    // move it, including from another tab.
    useEffect(() => subscribeToNotifyPrefs(
        (prefs) => setNotifyEmailEnabled(prefs.email),
    ), []);
    const chatInvestigateEnabled = getConversationInvestigateMode(activeConversationId, chatHistory);
    /* Which model answers the next question.

       Seeded from localStorage, so a reader's choice survives a reload, and '' until the
       picker reports the default tier. Two setters on purpose: a click is remembered, a
       resolved default is not — storing the default would pin today's tier and a later
       server-side change would never reach this reader. Same cross-tab subscription as the
       notify preference above, for the same reason: the value is in localStorage and another
       tab can move it. */
    const [serviceTier, setServiceTier] = useState(() => getTierPref());
    useEffect(() => subscribeToTierPref(setServiceTier), []);
    const handleServiceTierChange = useCallback((tierId) => {
        setServiceTier(tierId);
        setTierPref(tierId);
    }, []);
    const investigateFunnelRef = useRef(initialRunSnapshot?.investigateFunnel || emptyFunnel());
    /* What the funnel counters actually SHOWED, as opposed to what the agent reported.
       Kept apart from `investigateFunnelRef` for two reasons, and they pull in opposite
       directions:

       The panel's counters do not render the agent's number verbatim. An unknown counter
       ramps while its phase runs, and Retrieved deliberately settles on `real + ramp` (see
       RAMP_RANGE in InvestigateProgress). So when the run ended and the live panel gave way
       to the summary chips — which read the agent's raw funnel — Retrieved dropped from
       ~4,000 to ~1,300 in front of the reader. The chips read this instead, so the number
       the run finished on is the number it keeps.

       And it must NOT be fed back in as the counters' `value`: the ramp bonus would be
       added to a figure that already contains it, once per frame. `investigateFunnelRef`
       stays the agent's own counts and remains what drives the counters. */
    const investigateDisplayFunnelRef = useRef(
        initialRunSnapshot?.investigateDisplayFunnel || emptyFunnel(),
    );
    /* Which pipeline the turn in flight is on. Stop is a stable callback with no access to
       the submit-time options, and the two pipelines differ in both the router that owns the
       relay task and the sentence a stopped run leaves behind — so the running turn has to
       leave its own name somewhere Stop can read it. */
    const runningInvestigateRef = useRef(getConversationInvestigateMode(
        initialRunSnapshot?.conversationId, initialRunSnapshot?.messages || [],
    ));
    const investigatePhaseRef = useRef(initialRunSnapshot?.investigatePhase || 'searching');
    const investigatePercentRef = useRef(initialRunSnapshot?.investigatePercent ?? null);
    const investigateKeywordsRef = useRef(initialRunSnapshot?.investigateKeywords || []);
    const investigatePapersRef = useRef(initialRunSnapshot?.investigatePapers || []);
    const investigateDetailRef = useRef(initialRunSnapshot?.investigateDetail || {});
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const thinkingStepsRef = useRef(initialRunSnapshot?.thinkingSteps || []);
    // The answer as it streams in. `block` rises on every tool call, and only the newest block is
    // the answer — in a ReAct loop the model narrates before each call and that text streams too,
    // so a lower block number means "that was a previous train of thought, throw it away".
    const streamingAnswerRef = useRef(
        initialRunSnapshot?.streamingAnswer || { block: -1, text: '' },
    );
    // Lazily built once. Both of its inputs are stable: `streamingAnswerRef` is a ref and
    // `setChatHistory` is a useState setter, so the drip never captures a stale render.
    const dripRef = useRef(null);
    if (!dripRef.current) {
        dripRef.current = makeDrip({
            getFull: () => streamingAnswerRef.current.text,
            show: (text) => {
                const updateHistory = runHistoryUpdaterRef.current || setChatHistory;
                updateHistory((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (!last || last.role !== 'assistant') return prev;
                if (last.content === text) return prev;
                next[next.length - 1] = { ...last, content: text };
                return next;
                });
            },
        });
    }
    // A run can still be in flight when the reader leaves the page; nothing should go on
    // painting into a conversation that is no longer mounted.
    useEffect(() => () => dripRef.current?.stop(), []);
    // The cheap-tier opening line, accumulated in place. It occupies ONE entry in the thought
    // list that grows as the chunks land, rather than one entry per chunk — `groupThinkingSteps`
    // would otherwise render a column of two-word fragments.
    const preambleRef = useRef(
        initialRunSnapshot?.preamble || { text: '', index: -1 },
    );
    const prevSelectedMessageIndexRef = useRef(null);
    const lastAutoSelectedRef = useRef(null);
    const sessionIdRef = useRef(initialRunSnapshot?.sessionId || null);
    const runIdRef = useRef(initialRunSnapshot?.runId || null);
    // The conversation whose answer is currently being recovered, so the two restore
    // paths cannot both poll for it and overwrite each other's result.
    const resumingConversationRef = useRef(null);
    /* Stop, for the two states in which the view is following a run it cannot abort.
     *
     * Stop worked by aborting the SSE, which assumes the view owns a socket. It does not,
     * twice over: a submit spends its first moments on the conversation round trip and the
     * quota check before the stream opens, and a REATTACHED run (a reload, a conversation
     * reopened, a dropped connection) is polled and never streamed at all. In both states
     * `abortControllerRef` is null, so Stop wrote its "stopped" sentence and changed nothing
     * else — the panel carried on counting, and when the run finished the poll wrote the
     * finished report straight over the sentence. That is the reader's "I pressed stop and it
     * kept going".
     *
     * These two are how Stop reaches those states: the reattach loop checks its key on every
     * pass, and a submit acts on its own the moment the run announces itself — the first
     * point at which there is a name to cancel by.
     */
    const stoppedResumeKeyRef = useRef(null);
    const stopRequestedStreamRef = useRef(null);
    // The clarify round's identifiers, mirrored out of React state.
    //
    // The panel is answered by POSTing to /clarify with (session_id, invocation_id, stage). Issue
    // #12 captured a HAR where all three were plainly on the wire and Skip still produced
    // "Clarification session has expired" with ZERO clarify requests — i.e. the submit handler read
    // them as null. Every other live investigate value here is already mirrored into a ref for the
    // same reason (funnel, phase, percent, papers, session id): a value the SSE handler writes and
    // a click handler reads must not depend on which render each of them closed over.
    const pendingClarificationRef = useRef(initialRunSnapshot?.pendingClarification || null);
    // The ONLY way to set the pending round: the ref and the state must never disagree, or the
    // panel would render one round while submit answers another.
    /* One counter reporting the figure it is showing. Merged monotonically, so a counter
       that is mid-animation cannot lower a number a later frame already raised, and a
       remount (which resets the ramp) cannot walk one backwards either. */
    const onDisplayFunnel = useCallback((columnKey, shownValue) => {
        if (!columnKey || !Number.isFinite(Number(shownValue))) return;
        const current = investigateDisplayFunnelRef.current || emptyFunnel();
        const previous = current[columnKey];
        const next = Number(shownValue);
        if (previous != null && previous >= next) return;
        investigateDisplayFunnelRef.current = { ...current, [columnKey]: next };
        // Counters animate without a parent render: keep the pending snapshot current too.
        // Otherwise switching/closing between server frames persists an old (often empty) count.
        const snapshot = liveRunSnapshotRef.current;
        if (snapshot && String(snapshot.conversationId ?? '') === String(runningConversationIdRef.current ?? '')) {
            snapshot.investigateDisplayFunnel = investigateDisplayFunnelRef.current;
        }
    }, []);

    /* The same readings, handed back to a panel that is being CREATED over a run already in
       flight — after a reload, or after the reader left this conversation and came back. The
       card unmounts on both, and a fresh counter opened at nothing and counted up to the truth
       all over again, which is the "it starts from the beginning" the reader sees.

       A stable function, not the object: the object changes on every animation frame, and a
       prop that changes at that rate would re-render every message in the conversation. The
       panel calls it once, when its counters are created. */
    const getDisplayFunnel = useCallback(() => investigateDisplayFunnelRef.current, []);

    /* The funnel a FINISHED message keeps — the larger of the agent's counts and what the
       panel actually showed. The rule and the reason live in `funnel.js:settleFunnel`. */
    const settledFunnel = useCallback(
        () => settleFunnel(investigateFunnelRef.current, investigateDisplayFunnelRef.current),
        [],
    );

    const applyPendingClarification = useCallback((next) => {
        pendingClarificationRef.current = next || null;
        setPendingClarification(next || null);
    }, []);
    // The Agent now outlives /chat, so this cannot be a one-shot boolean: every trip from Home
    // to Chat gets a fresh location key and may carry a new question.
    const consumedInitialQueryKeyRef = useRef(null);
    const initialQueryTransitionRef = useRef(false);
    const initialSearchOptionsRef = useRef(null);
    const lastSearchOptionsRef = useRef(null);
    const activeConversationIdRef = useRef(
        initialRunSnapshot?.conversationId || getActiveConversationId(),
    );
    const loadingConversationIdRef = useRef(null);
    const activeStreamIdRef = useRef(null);
    // Unlike activeStreamIdRef, this survives a turn finishing. Only New Chat
    // resets the nameless conversation that owns queued guest follow-ups.
    const queueOwnerKeyRef = useRef(restoredQueueOwnerKey(initialRunSnapshot));
    /* The conversations this tab is answering in the background (a released follow-up).
       The registry mark says the same thing, but it is shared with other tabs and can go
       stale, so the visit-time reconcile below is allowed to take it down — and used to take
       it down on a conversation whose follow-up this tab was still writing. This ref is the
       one thing that cannot be stale: it is set before the request and cleared after it. */
    const backgroundRunsRef = useRef(new Set());
    const liveRunSnapshotRef = useRef(initialRunSnapshot);
    /* The slot the live snapshot is being written under — a conversation id, or null for a run
       that has none yet. `undefined` means nothing has been written this mount. Kept so that
       when a run settles only ITS record is cleared: the other runs still in flight own the
       other slots. */
    const snapshotConversationIdRef = useRef(
        initialRunSnapshot?.active ? (initialRunSnapshot.conversationId ?? null) : undefined,
    );
    const snapshotWriteTimerRef = useRef(null);
    const splitContainerRef = useRef(null);
    const isDraggingSplitRef = useRef(false);
    const navigationBypassRef = useRef(false);
    const originalNavigatorMethodsRef = useRef({ push: null, replace: null });
    const navigate = useNavigate();

    /* One conversation's stored messages, written through to both copies of the list.
       `touch`: a conversation being answered belongs at the top of the sidebar for as long as
       it runs — not at the rank it held before the question. Used by the attached run below
       and by background runs, which write here and nowhere else. */
    const writeConversationMessages = useCallback((conversationId, messages) => {
        const nextList = updateConversationMessages(
            getConversations(),
            conversationId,
            messages,
            { touch: true },
        );
        setConversations(nextList);
        setConversationsState(nextList);
    }, []);

    /* The store write, throttled for the streaming hot path. `updateRunningChatHistory`
       runs on every drip tick (~30/s), and writing through on each one meant JSON-parsing
       the whole conversation list, sorting it three times, stringifying it back and firing
       the NavBar's update event per animation frame — visible jank on long answers. The
       refs and the on-screen transcript stay per-tick; the store gets the LATEST copy at
       most every CHAT_PERSIST_DEBOUNCE_MS, and `flushConversationStoreWrite` hands over
       anything pending before the view detaches. */
    const conversationStoreWriteRef = useRef({ timer: null, conversationId: null, messages: null });
    const flushConversationStoreWrite = useCallback(() => {
        const pending = conversationStoreWriteRef.current;
        if (pending.timer) {
            window.clearTimeout(pending.timer);
            pending.timer = null;
        }
        if (pending.conversationId && pending.messages) {
            writeConversationMessages(pending.conversationId, pending.messages);
        }
        pending.conversationId = null;
        pending.messages = null;
    }, [writeConversationMessages]);
    const scheduleConversationStoreWrite = useCallback((conversationId, messages) => {
        const pending = conversationStoreWriteRef.current;
        if (pending.conversationId && String(pending.conversationId) !== String(conversationId)) {
            // A write for another conversation is waiting; it must not be dropped.
            flushConversationStoreWrite();
        }
        pending.conversationId = conversationId;
        pending.messages = messages;
        if (pending.timer) return;
        pending.timer = window.setTimeout(() => {
            pending.timer = null;
            const latest = conversationStoreWriteRef.current;
            if (latest.conversationId && latest.messages) {
                writeConversationMessages(latest.conversationId, latest.messages);
                latest.conversationId = null;
                latest.messages = null;
            }
        }, CHAT_PERSIST_DEBOUNCE_MS);
    }, [writeConversationMessages, flushConversationStoreWrite]);

    const updateRunningChatHistory = useCallback((updater) => {
        const previous = runningChatHistoryRef.current;
        const next = typeof updater === 'function' ? updater(previous) : updater;
        if (!Array.isArray(next) || next === previous) return previous;

        runningChatHistoryRef.current = next;
        const runningId = runningConversationIdRef.current;
        if (runningId) {
            scheduleConversationStoreWrite(runningId, next);
        }

        const selectedId = activeConversationIdRef.current;
        /* When neither the run nor the viewport names a conversation there is only one thread,
           and it is the one on screen.

           This used to also require a live stream, which is exactly what a recovered run does
           not have: after a reload the answer arrives by polling `GET /run?session_id=`, not
           down an open SSE connection. A guest's recovered answer was therefore written into
           the run history and never into `chatHistory`, so the reader watched a spinner over a
           finished answer that was already in memory. Every caller here owns a run, so the
           extra condition was not protecting anything. */
        const isSelectedRun = runningId
            ? String(selectedId) === String(runningId)
            : !selectedId;
        if (isSelectedRun) {
            setChatHistory(next);
        }
        setRunHistoryRevision((revision) => revision + 1);
        return next;
    }, [scheduleConversationStoreWrite]);
    runHistoryUpdaterRef.current = updateRunningChatHistory;

    /* Re-evaluated whenever the run registry changes. The queue release below waits on "is the
       target conversation still answering", which the registry knows and no state does — a run
       finishing in the background changes no view flag, so without this the queue only woke up
       when something else happened to re-render, and a queued follow-up sat unsent for as long
       as the reader sat still. */
    const [runRegistryRevision, setRunRegistryRevision] = useState(0);
    useEffect(() => subscribeToActiveRun(() => {
        setRunRegistryRevision((revision) => revision + 1);
    }), []);

    /* Detach the view from the run it was following — and, unless told otherwise, end that
       run. `abort: false` is the release: the request stays open, the server finishes the
       answer against its own history, and only the view lets go. Defined this early because
       every conversation-switching path below uses it. */
    const cancelStreaming = useCallback((options = {}) => {
        const { abort = true } = options;
        // The throttled store write may still be holding this run's newest messages; hand
        // them over before the refs stop naming their conversation.
        flushConversationStoreWrite();
        if (liveRunSnapshotRef.current) writeActiveRunSnapshot(liveRunSnapshotRef.current);
        if (abort && abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = null;
        activeStreamIdRef.current = null;
        /* The reattach's single-flight claim goes with the view.
        
           It was released only in the resume's own `finally`, which cannot run until the
           sleeping poll loop next wakes (three seconds). Inside that window this function has
           already emptied `runningChatHistoryRef` and nulled `runningConversationIdRef`, so a
           reader who left and came straight back was refused a new reattach — the claim was
           still held — while the abandoned one had nothing left to write into. The answer
           landed on an empty array and was dropped, under a card that never appeared. */
        resumingConversationRef.current = null;
        /* The last investigate run's id belonged to the view, not to whatever conversation
           the view moves to next. Left standing, the background recovery poll below fetched
           THAT run and wrote its report into whichever transcript happened to be open. */
        runIdRef.current = null;
        runningConversationIdRef.current = null;
        runningChatHistoryRef.current = [];
        dripRef.current?.stop();
        setAnswerReady(false);
        setIsLoading(false);
        setIsProcessing(false);
        setStreamingGroups([]);
        setPreambleText('');
        setStreamingStepName('');
        thinkingStepsRef.current = [];
        applyPendingClarification(null);
        setClarificationDrafts({});
        setClarificationError('');
        setClarificationSubmitting(false);
    }, [flushConversationStoreWrite]);

    const isViewingRunningConversation = Boolean(isLoading && (
        (runningConversationIdRef.current
            && String(activeConversationId) === String(runningConversationIdRef.current))
        /* A nameless thread is "the running one" whether its answer arrives down a live
           stream or through a recovery poll. A signed-out reader's resumed run has neither a
           conversation id nor a stream, and counting it as "running elsewhere" hid every
           piece of progress UI behind a blank bubble — and let a fresh submit start on the
           same session id the recovery was still polling. */
        || (!runningConversationIdRef.current
            && (activeStreamIdRef.current || resumingConversationRef.current)
            && !activeConversationId)
    ));

    // Conversations already announced. A Set, not a slot: several runs can be in flight.
    const backgroundCompletionNotifiedRef = useRef(new Set());
    const backgroundClarificationNotifiedRef = useRef(new Set());
    /**
     * @param {*} [forConversationId] The conversation that finished, when the caller knows it.
     *
     * A released run has to name itself. The refs describe whatever took the foreground after
     * it, so reading them told the announcement about the wrong conversation — and since the
     * de-duplication key came from those same refs, one run could also swallow another's
     * notification. Announced conversations are remembered by id, one entry each.
     */
    const announceBackgroundCompletion = useCallback((forConversationId) => {
        const runningId = forConversationId ?? runningConversationIdRef.current;
        if (!runningId || String(activeConversationIdRef.current) === String(runningId)) return;
        const notificationKey = String(runningId);
        if (backgroundCompletionNotifiedRef.current.has(notificationKey)) return;
        backgroundCompletionNotifiedRef.current.add(notificationKey);
        const conversation = getConversations().find(
            (item) => String(item.id) === String(runningId),
        );
        const title = conversation?.leadingTitle || 'Your background conversation';
        message.success(`${title} is ready.`);
    }, []);

    /**
     * Tell the reader their report landed. Both completion paths call this, and
     * a run can finish through either, so it fires at most once per run.
     */
    const announcedRunRef = useRef(null);
    const announceInvestigateComplete = useCallback(() => {
        const runId = runIdRef.current || 'current';
        if (announcedRunRef.current === runId) return;
        announcedRunRef.current = runId;
        notifyRunComplete({
            title: 'Investigate finished',
            body: 'Your report is ready to read.',
            onClick: () => navigate(CHAT_NEW_PATH),
        });
    }, [navigate]);
    const { isAuthenticated, loading: authLoading, openLoginModal } = useAuth();
    /* A guest may ask again (guest mode reopened 2026-10-03): AI Chat at Standard only, 10
       questions per IP in all (since 2026-10-09; Investigate needs an account, and the count
       never resets). The backend enforces both and refuses with a
       code (utils/refusals.js); what is left here is asking them to sign in when they reach
       for more. */
    const isGuest = !authLoading && !isAuthenticated;
    const requireSignIn = useCallback((reason) => {
        openLoginModal(typeof reason === 'string' ? reason : undefined);
    }, [openLoginModal]);
    const requestPremiumSignIn = useCallback((tier) => {
        requireSignIn(`${tier?.label || 'GPT-6.1 Sol'} is available to signed-in users. Sign in to use it — it's free.`);
    }, [requireSignIn]);
    /* Images and PDFs waiting on the composer (service/attachments.js). Held here, not in the
       composer, because the send reads them: `submitOrQueue` puts them on the turn's options
       and clears the chips once the question has left. Signed-in readers and AI Chat only. */
    const requestAttachSignIn = useCallback(() => {
        requireSignIn(GUEST_ATTACH_REASON);
    }, [requireSignIn]);
    const composerAttachments = useAttachments({ onRequireSignIn: requestAttachSignIn });
    const clearComposerAttachments = composerAttachments.clear;
    const useMobileReferencesDrawer = isPhoneDevice;

    useEffect(() => {
        const evaluateIsPhone = () => {
            setIsPhoneDevice(isPhoneViewport());
        };

        evaluateIsPhone();
        window.addEventListener('resize', evaluateIsPhone);
        return () => {
            window.removeEventListener('resize', evaluateIsPhone);
        };
    }, []);

    useEffect(() => {
        if (!useMobileReferencesDrawer) {
            setIsMobileReferencesDrawerOpen(false);
        }
    }, [useMobileReferencesDrawer]);

// Nothing guards in-app navigation any more: a run survives the route change,
    // so a dialog asking whether to abandon it would be describing something that
    // no longer happens.

    // Closing the tab is the one exit that does end a run, and it needs warning
    // about from whichever page the reader is on — so the handler lives in the
    // layout and reads this registry instead of this component's state.
    const isLoadingRef = useRef(false);
    /* What THIS effect last registered, so that only that may be cleared. The false-branch
       used to read the live refs instead — trusting that a release nulls them first — and
       that was a race: switching conversations releases the old run and starts a reattach,
       and when the reattach repointed the refs before the release's `isLoading=false` had
       flushed, the clear below deleted the mark of the conversation being OPENED, whose run
       was alive. The queue then saw it as free and released a second turn onto it. */
    const registeredAttachmentRef = useRef(null);
    useEffect(() => {
        isLoadingRef.current = isLoading;
        if (!isLoading) {
            const registered = registeredAttachmentRef.current;
            registeredAttachmentRef.current = null;
            if (!registered) return;
            /* Cleared only when the refs still describe the registered attachment — i.e. the
               run genuinely settled in place (a reattach ends this way: no stream, flags come
               down where they went up). A release nulls the refs first, and a newer attach
               repoints them; in both cases the mark now belongs to a run that is still being
               written and clears itself by id when its own request settles. */
            const conversationUnchanged = String(runningConversationIdRef.current ?? '')
                === String(registered.conversationId ?? '');
            const streamUnchanged = (activeStreamIdRef.current ?? null)
                === (registered.streamId ?? null);
            if (!conversationUnchanged || !streamUnchanged) return;
            /* A run with no conversation id — a guest, or a row the server never returned —
               is filed under the pending key and has to be released by that name, or it would
               sit in the registry forever making the app think something is still working. */
            if (registered.conversationId != null) clearActiveRun(registered.conversationId);
            else clearPendingRun(registered.streamId);
            return;
        }
        // Both pipelines return run ids. Only the submitted mode identifies the pipeline.
        registeredAttachmentRef.current = {
            conversationId: runningConversationIdRef.current || null,
            streamId: activeStreamIdRef.current ?? null,
        };
        setActiveRun({
            kind: runningInvestigateRef.current ? 'investigate' : 'chat',
            runId: runIdRef.current || null,
            conversationId: runningConversationIdRef.current || null,
            // The address this run can be picked up at after the page goes away. It is the
            // durable record's whole point — see service/runRecovery.js.
            sessionId: sessionIdRef.current || null,
            // Its own slot until the conversation row arrives, so two runs that are both still
            // nameless do not overwrite each other's mark.
            key: activeStreamIdRef.current,
        });
    }, [isLoading, activeConversationId]);

    useEffect(() => () => {
        /* Only the mark THIS view registered. The old no-argument clear wiped the whole
           registry, background turns included — their requests carry on after an unmount
           (nothing aborts them) and their own `finally` owns their marks; wiping them here
           silenced the tab-close warning and disarmed the double-submit guard for
           conversations that were still being written. */
        if (isLoadingRef.current) return;
        const registered = registeredAttachmentRef.current;
        if (!registered) return;
        registeredAttachmentRef.current = null;
        if (registered.conversationId != null) clearActiveRun(registered.conversationId);
        else clearPendingRun(registered.streamId);
    }, []);

    const refreshTierStatus = useCallback(async () => {
        if (authLoading) {
            setIsQueryLimitReached(false);
            return;
        }
        const next = await fetchUsage({ isAuthenticated });
        if (!next) return;
        setUsage(next);
        setIsQueryLimitReached(next.limitReached);
    }, [authLoading, isAuthenticated]);
    // The balance a stream frame carried (`applyStreamCredits`) arrives here too.
    useEffect(() => subscribeToUsage((next) => {
        setUsage(next);
        setIsQueryLimitReached(Boolean(next?.limitReached));
    }), []);
    /* A refusal from before the stream opened (utils/refusals.js). Credits can only be fixed by
       waiting or buying; a guest's limit or a members-only tier by signing in, so that opens the
       sign-in overlay with the reason on it. */
    const handleRefusal = useCallback((refusal) => {
        if (!refusal) return;
        if (refusalNeedsSignIn(refusal)) {
            requireSignIn(refusalText(refusal));
            // A members-only tier puts the picker back on the guest's; a refusal for attached
            // files (`reason: "attachments"`) has nothing to do with the tier.
            if (refusal.code === 'GUEST_LOGIN_REQUIRED' && refusal.reason !== 'attachments') {
                setServiceTier(GUEST_TIER);
            }
        }
        if (refusal.code === 'INSUFFICIENT_CREDITS' || refusal.code === 'GUEST_LIMIT_REACHED') {
            refreshTierStatus();
        }
    }, [requireSignIn, refreshTierStatus]);

    const llmService = useMemo(() => new LLMAgentService(), []);
    const isLimitReachedEffective = isQueryLimitReached || DEBUG_FORCE_LIMIT_WARNING;
    const showLimitWarning = isLimitReachedEffective;
    const limitWarningText = limitReachedText(usage);
    const activeConversation = useMemo(() => {
        const currentId = activeConversationIdRef.current || activeConversationId;
        if (!currentId) return null;
        return conversationsState.find((item) => String(item.id) === String(currentId)) || null;
    }, [activeConversationId, conversationsState]);
    const chatTitle = useMemo(() => {
        if (activeConversation?.leadingTitle) return activeConversation.leadingTitle;
        const firstUser = chatHistory.find((msg) => msg.role === 'user');
        if (firstUser?.content) return firstUser.content;
        return 'New Chat';
    }, [activeConversation, chatHistory]);
    const isConversationBookmarked = useMemo(() => {
        const currentId = activeConversationIdRef.current || activeConversationId;
        if (!currentId) return false;
        return conversationBookmarks.some((item) => String(item.id) === String(currentId));
    }, [activeConversationId, conversationBookmarks]);

    useEffect(() => {
        activeConversationIdRef.current = activeConversationId;
    }, [activeConversationId]);

    useEffect(() => {
        if (authLoading || !isAuthenticated) {
            setConversationBookmarksState([]);
            return undefined;
        }

        let isMounted = true;
        const update = (event) => {
            const next = event?.detail || getConversationBookmarks();
            if (!isMounted) return;
            setConversationBookmarksState(next);
        };

        fetchConversationBookmarks()
            .then((list) => {
                if (!isMounted) return;
                setConversationBookmarksState(list);
            })
            .catch(() => update());

        window.addEventListener('glkb-conversation-bookmarks-updated', update);
        return () => {
            isMounted = false;
            window.removeEventListener('glkb-conversation-bookmarks-updated', update);
        };
    }, [authLoading, isAuthenticated]);

    useEffect(() => {
        refreshTierStatus();
    }, [refreshTierStatus]);

    /* The run's own position, as the SERVER reports it, applied to the live progress panel.
     *
     * A reattached view is polling, not streaming, so it receives no progress frames at all.
     * Before this, the panel showed whatever the tab had saved at the moment the page went
     * away — frozen there for the rest of the run — and showed nothing at all where there was
     * no saved copy to restore: a second device, a cleared tab, a conversation opened from the
     * list. A fifteen-minute run then spent fourteen of those minutes claiming to be on the
     * phase it was on when the reader pressed reload, and its finished card kept the stale
     * counters, because the funnel it settles with is the one held here.
     *
     * The agent folds its progress frames into the run record now (`service/run_store.py`),
     * and this is where that snapshot lands. Read through `extractProgress`, which is the same
     * extractor the SSE path uses, so the two routes cannot drift.
     *
     * Merged, never assigned: `mergeFunnel` and the monotonic percent/phase helpers are the
     * same ones the stream applies, so a poll that arrives with less than the stream already
     * showed cannot walk the panel backwards.
     */
    const applyRunProgress = useCallback((run, investigate) => {
        if (!run || run.status !== 'running') return;
        /* The run's id, learned from the poll. A reattached view has none of its own — the
           reload destroyed it — and Stop needs it to reach the server.

           Safe to assign rather than fill in: every caller is polling the run the view is
           actually following (by its session id, or by this very ref), and a view that lets
           go of a run clears the ref in `cancelStreaming`, so there is no older run's id
           here to protect. */
        if (run.run_id) runIdRef.current = run.run_id;
        if (!investigate) return;
        const progress = extractProgress(run.progress);
        if (!progress) return;

        // The run's real start, so the header clock reads the run's age rather than the
        // reattach's. Only when this view has no start of its own: a restored snapshot's is
        // just as true and is already on screen.
        if (progress.startedAt) setInvestigateStartedAt((prev) => prev || progress.startedAt);

        const nextPhase = mergePhaseMonotonic(investigatePhaseRef.current, progress.phase);
        if (nextPhase && nextPhase !== investigatePhaseRef.current) {
            investigatePhaseRef.current = nextPhase;
            setInvestigatePhase(nextPhase);
        }
        if (progress.funnel) {
            investigateFunnelRef.current = mergeFunnel(investigateFunnelRef.current, progress.funnel);
            setInvestigateFunnel({ ...investigateFunnelRef.current });
        }
        const nextPct = mergePercentMonotonic(
            investigatePercentRef.current,
            progress.percent ?? PHASE_PERCENT_FLOOR[nextPhase],
        );
        investigatePercentRef.current = nextPct;
        setInvestigatePercent(nextPct);
        if (progress.keywords) {
            investigateKeywordsRef.current = mergeLiveKeywords(
                investigateKeywordsRef.current, progress.keywords,
            );
            setInvestigateKeywords([...investigateKeywordsRef.current]);
        }
        if (progress.papers) {
            investigatePapersRef.current = mergeLivePapers(
                investigatePapersRef.current, progress.papers,
            );
            setInvestigatePapers([...investigatePapersRef.current]);
        }
        if (progress.detail && Object.keys(progress.detail).length) {
            investigateDetailRef.current = {
                ...(investigateDetailRef.current || {}), ...progress.detail,
            };
            setInvestigateDetail({ ...investigateDetailRef.current });
        }
        // Not "Still answering": the server has just said what it is doing.
        if (progress.label) setStreamingStepName(progress.label);
    }, []);

    // Reattach to a run that was still going when the page was reloaded.
    //
    // The server does not stop when the browser goes away: the agent finishes the run and the
    // backend writes the answer to history on its own. What was missing was the client half —
    // every recovery path keyed off `runIdRef`, which a reload destroys, and none of them ran on
    // load. The session id is the address that does survive (sessionStorage, written at submit),
    // and `GET /run?session_id=` answers with the latest run for it, for chat and investigate.
    //
    // Two rules keep the recovery from being worse than the problem:
    //
    //   SINGLE FLIGHT. Two restore paths reach this — the mount-time one a plain reload takes,
    //   and the one that runs when a conversation is opened from the list — and they used to
    //   race. One would reach a miss and write a failure into the message while the other was
    //   still polling; the other then landed the real answer on top. That is the "thinking, then
    //   Sorry, then the answer" flicker.
    //
    //   LOOK BEFORE TOUCHING. The first poll happens with the UI untouched. A run that has
    //   already finished — the common case, because a reload takes longer than the poll — is
    //   rendered directly, with no loading state and no flash of anything else. The spinner goes
    //   back only once the server has actually said "running".
    const resumeUnfinishedRun = useCallback(async (
        conversationId, messages, stillMounted, isInvestigateHint, displayMessages = messages,
        sessionIdOverride = null, assumeUnfinished = false,
    ) => {
        /* `assumeUnfinished` is the snapshot's own word that the run was live when the page
           went away. Deriving it from the message shape instead was wrong for the reader it
           mattered most to: a guest whose answer had STARTED streaming has a non-empty
           trailing assistant message, `isExchangeUnfinished` says the exchange is closed, and
           the resume returned — leaving a truncated fragment on screen rendered as the
           finished answer, with nothing polling for the rest. */
        if (!assumeUnfinished && !isExchangeUnfinished(messages)) return;
        /* A run does not need a saved conversation to be reattachable. The agent retains it
           against its SESSION id — `GET /run?session_id=` answers for an ordinary chat turn,
           not just a deep-research one — and a signed-out reader has a session id and no
           conversation. Reading the session id only out of the per-conversation store meant a
           guest who refreshed mid-answer had nothing to reconnect to, and the answer their
           quota had already paid for was simply gone. */
        const sessionId = sessionIdOverride
            || getStoredSessionId(conversationId)
            // The run recovered from the durable record brought its own address with it.
            || getRunSessionId(conversationId);
        /* No session id is NOT the same as nothing to wait for.
        
           This used to return here, and that was the shape of the worst version of the bug:
           a reader closed the tab mid-answer, came back, and got their question sitting alone
           with no assistant bubble, no waiting line and nothing fetching anything. The answer
           existed — the server had never stopped writing it — but the only address it could be
           collected from had been thrown away with the tab. It appeared only if they happened
           to reload again later, which is why it "usually worked once the model had finished".
        
           A saved conversation does not need that address. The backend writes the answer into
           the history itself, so the exchange can be waited out by re-reading the history —
           slower than the run store, and completely reliable. Only a run with neither an
           address nor a conversation has genuinely nothing behind it. */
        if (!sessionId && !conversationId) return;
        const key = conversationId ? String(conversationId) : `session:${sessionId}`;
        if (conversationId == null && !queueOwnerKeyRef.current) {
            queueOwnerKeyRef.current = restoredQueueOwnerKey(readActiveRunSnapshotFor(null)) || key;
        }
        if (resumingConversationRef.current === key) return;
        resumingConversationRef.current = key;
        /* A Stop belongs to the reattach it was aimed at. Cleared as a new one starts, so a
           key left behind by a run that has already settled cannot stop its successor before
           it has polled once. (A run stopped on the server answers the next poll with
           `cancelled`, which settles it here anyway.) */
        stoppedResumeKeyRef.current = null;
        /* Whether some actual request already holds this conversation's registry mark — a run
           released by New Chat or a background follow-up, whose own `finally` will clear it.
           When the mark exists only because THIS reattach put it up (a reload orphan: the run
           lives on the server alone, no client request owns anything), an abandoned reattach
           must take the mark down itself, or the conversation reads as "answering" for the
           life of the tab — every new question in it silently refused, its queue never
           released, its sidebar dot permanent. */
        const markExistedBefore = conversationId
            ? isConversationRunning(conversationId)
            : false;
        runningConversationIdRef.current = conversationId ? String(conversationId) : null;
        runningChatHistoryRef.current = Array.isArray(displayMessages) ? displayMessages : messages;

        // Use the same pipeline resolution for reconnects and new follow-ups. Historical
        // icon marks and run IDs do not identify the current request's pipeline.
        const investigate = resolveConversationMode({
            conversationId,
            conversation: conversationId == null ? null : {
                id: conversationId, isInvestigate: isInvestigateHint, messages,
            },
            messages,
            snapshot: readActiveRunSnapshotFor(conversationId),
            submittedMode: readSubmittedMode(conversationId),
        });
        runningChatHistoryRef.current = normalizePendingMode(runningChatHistoryRef.current, investigate);
        // Restore this run's own presentation BEFORE mounting its counters. The previous
        // conversation's global refs are not a source of progress for this one.
        const candidateSnapshot = readActiveRunSnapshotFor(conversationId);
        const progressSnapshot = candidateSnapshot
            && String(candidateSnapshot.conversationId ?? '') === String(conversationId ?? '')
            ? candidateSnapshot : null;
        investigateDisplayFunnelRef.current = investigate
            ? (progressSnapshot?.investigateDisplayFunnel || emptyFunnel()) : emptyFunnel();
        investigateFunnelRef.current = investigate
            ? (progressSnapshot?.investigateFunnel || emptyFunnel()) : emptyFunnel();
        investigatePhaseRef.current = investigate ? (progressSnapshot?.investigatePhase || 'planning') : 'planning';
        investigatePercentRef.current = investigate ? (progressSnapshot?.investigatePercent ?? null) : null;
        investigateKeywordsRef.current = investigate ? (progressSnapshot?.investigateKeywords || []) : [];
        investigatePapersRef.current = investigate ? (progressSnapshot?.investigatePapers || []) : [];
        investigateDetailRef.current = investigate ? (progressSnapshot?.investigateDetail || {}) : {};
        setInvestigateFunnel(investigateFunnelRef.current);
        setInvestigatePhase(investigatePhaseRef.current);
        setInvestigatePercent(investigatePercentRef.current);
        setInvestigateKeywords(investigateKeywordsRef.current);
        setInvestigatePapers(investigatePapersRef.current);
        setInvestigateDetail(investigateDetailRef.current);
        setInvestigateStartedAt(investigate ? (progressSnapshot?.investigateStartedAt || Date.now()) : null);
        /* Which product Stop must cancel. The two pipelines have separate routers, and this
           ref was only ever written by a submit — so Stop on a reattached investigate run
           called the CHAT cancel endpoint, which knows nothing about the deep-research relay
           and left it running. */
        runningInvestigateRef.current = investigate;
        /* Marked NOW, not at the first successful poll: between opening the conversation and
           that poll, the registry said nothing was running here, so a submit or a queue
           release could put a second turn on a history id the server was still answering. */
        if (!markExistedBefore && conversationId) {
            setActiveRun({
                kind: investigate ? 'investigate' : 'chat',
                runId: null,
                conversationId: String(conversationId),
                sessionId,
                // Its own key. `setActiveRun` re-keys by deleting `provisionalKey(key)`,
                // and with no key that is the shared '__pending__' slot — where a signed-out
                // recovery files ITS mark. Registering this one used to delete that one.
                key: `resume:${key}`,
            });
        }
        const last = displayMessages[displayMessages.length - 1] || messages[messages.length - 1];
        let placeholderAdded = false;

        const showWaiting = () => {
            if (placeholderAdded) return;
            placeholderAdded = true;
            if (last.role === 'user') {
                updateRunningChatHistory((prev) => [...prev, {
                    role: 'assistant',
                    content: '',
                    references: [],
                    timestamp: new Date().toISOString(),
                    thinkingSteps: [],
                    thoughtDurationMs: null,
                    trajectory: null,
                    investigateMode: investigate,
                }]);
            } else {
                updateRunningChatHistory((prev) => normalizePendingMode(prev, investigate));
            }
            setIsLoading(true);
            setIsProcessing(true);
            // Reattached: whatever the mount assumed, this run is not interrupted.
            setShowReloadPrompt(false);
            /* Not "Reconnecting". Nothing is wrong with the connection: the run is on the
               server, it is still being written, and it will land here when it is done —
               including if this page is closed and reopened. The old wording read as a
               failure to connect and invited a reload, which is the one thing that does not
               help. */
            setStreamingStepName((prev) => prev || 'Still answering — this will appear when it finishes');
        };

        const settle = (patch) => {
            updateRunningChatHistory((prev) => {
                if (!prev.length) return prev;
                const next = [...prev];
                const tail = next[next.length - 1];
                if (tail && tail.role === 'assistant') {
                    next[next.length - 1] = { ...tail, ...patch };
                } else {
                    next.push({
                        role: 'assistant',
                        references: [],
                        timestamp: new Date().toISOString(),
                        thinkingSteps: [],
                        thoughtDurationMs: null,
                        trajectory: null,
                        investigateMode: investigate,
                        ...patch,
                    });
                }
                return next;
            });
            setIsLoading(false);
            setIsProcessing(false);
            setStreamingStepName('');
        };

        /* End a reattached turn the reader stopped. The sentence goes in only when there is
           nothing else to show, exactly as the live path does it (STOPPED_BY_USER_TEXT): a
           run that had already written something keeps it. The backend writes the same
           sentence into the conversation, so a later reload reads the same thing. */
        const settleStopped = () => {
            settle(keepsWhatItWrote(runningChatHistoryRef.current)
                ? {}
                : { content: stoppedMessageFor(investigate) });
        };

        const applyRun = (run) => {
            setShowReloadPrompt(false);
            settle({
                content: run.response || '',
                references: parseReferences(run.references),
                thinkingSteps: thinkingStepsRef.current,
                trajectory: run.trajectory || null,
                investigateMode: investigate,
                ...(investigate ? {
                    investigateFunnel: settledFunnel(),
                    investigatePercent: 100,
                    investigatePhase: 'summary',
                    investigateKeywords: [...(investigateKeywordsRef.current || [])],
                    investigatePapers: [...(investigatePapersRef.current || [])],
                } : {}),
            });
            if (run.response) llmService.updateMessages(run.response);
            announceBackgroundCompletion();
        };

        // The answer may already be in history — written there by the backend while the page was
        // reloading. Cheaper and more reliable than the run store, which is in-process memory.
        const answerFromHistory = async () => {
            /* Only a saved conversation has a history to read. Without this a resume with no
               conversation — the signed-out path — fetched `null`, took the empty result for a
               finished exchange, and wrote that empty list over the reader's transcript: the
               question and the partial answer both vanished, a completion was announced, and
               the "this run was lost" message was skipped because the caller was told the
               answer had been found. */
            if (!conversationId) return false;
            try {
                const detail = await fetchConversationDetail(conversationId);
                if (!stillMounted()) return true;
                // A submit landed ON THIS THREAD while the fetch was in flight; the live run
                // owns the transcript now, and the old saved copy must not overwrite it. A
                // stream in some other thread is none of this recovery's business.
                if (takenOverByLiveSubmit()) return true;
                const saved = detail?.messages || [];
                /* Finished THROUGH the turn being waited on — decided by identity, not by
                   count. The local and server histories drift in length in both directions:
                   a locally written error bubble the server never saved makes the local copy
                   permanently longer, and server-side rows the client does not mirror make
                   it shorter. Counting messages therefore either ignored a recovered answer
                   forever (and wrote "lost" over it), or let an old finished history
                   overwrite the queued question. The pending turn is identified by its user
                   message: the server has answered it when that message appears with a
                   non-empty assistant message after it. Scanned from the end, so asking the
                   same question twice finds the latest asking. */
                let pendingUserContent = null;
                for (let i = messages.length - 1; i >= 0; i -= 1) {
                    if (messages[i]?.role === 'user') {
                        pendingUserContent = String(messages[i].content ?? '').trim();
                        break;
                    }
                }
                let turnAnswered = false;
                if (pendingUserContent) {
                    for (let i = saved.length - 1; i >= 0; i -= 1) {
                        if (saved[i]?.role === 'user'
                            && String(saved[i].content ?? '').trim() === pendingUserContent) {
                            const following = saved[i + 1];
                            turnAnswered = following?.role === 'assistant'
                                && Boolean(String(following.content ?? '').trim());
                            break;
                        }
                    }
                }
                if (!isExchangeUnfinished(saved) && turnAnswered) {
                    const next = [...saved];
                    const tail = next[next.length - 1];
                    if (tail?.role === 'assistant') {
                        next[next.length - 1] = {
                            ...tail,
                            thinkingSteps: tail.thinkingSteps?.length
                                ? tail.thinkingSteps
                                : thinkingStepsRef.current,
                            ...(investigate ? {
                                investigateMode: true,
                                investigateFunnel: tail.investigateFunnel
                                    || settledFunnel(),
                                investigatePhase: tail.investigatePhase || 'summary',
                                investigatePercent: tail.investigatePercent ?? 100,
                                investigateKeywords: tail.investigateKeywords
                                    || [...(investigateKeywordsRef.current || [])],
                                investigatePapers: tail.investigatePapers
                                    || [...(investigatePapersRef.current || [])],
                            } : {}),
                        };
                    }
                    updateRunningChatHistory(normalizePendingMode(next, investigate));
                    setIsLoading(false);
                    setIsProcessing(false);
                    setStreamingStepName('');
                    announceBackgroundCompletion();
                    return true;
                }
            } catch (error) {
                logDev('[LLM] resume history refetch failed', error);
            }
            return false;
        };

        /* A live submit has taken THIS thread over — the reader asked something new here,
           or a released follow-up landed on screen. That run owns the state now; polling on
           top of it would write a second answer into its transcript. Scoped to this
           conversation: a stream the reader started in a different thread must not kill this
           one's recovery. */
        const takenOverByLiveSubmit = () => Boolean(
            activeStreamIdRef.current
            && String(runningConversationIdRef.current ?? '') === String(conversationId ?? ''),
        );
        /* Whether the server's latest word was "still writing". An abandoned reattach only
           takes its mark down when the run is NOT known to be live — clearing a live run's
           mark let the queue release a second turn onto its history id. */
        let lastPollSaidRunning = false;
        /* Whether this reattach ENDED the turn — the answer was rendered, or a terminal
           message was written in its place. That, not who put the mark up, is what says the
           conversation is free again.

           `markExistedBefore` used to decide it, and it is true for exactly the case this
           whole recovery exists for: a mark read back from disk on a fresh page, which has
           no live owner and therefore nothing else that will ever take it down. Worse, the
           common path — a reload takes longer than one poll, so the very first `getRun`
           returns complete — returns before `showWaiting()`, so `isLoading` never goes true
           and the registry effect never registers anything either. The answer appeared and
           the conversation stayed "answering" for the rest of the session, silently queueing
           every follow-up the reader typed into it. */
        let turnSettled = false;
        try {
            for (let attempt = 0; attempt < RESUME_MAX_POLLS; attempt += 1) {
                if (!stillMounted()) return;
                if (takenOverByLiveSubmit()) return;
                /* The reader pressed Stop. This loop IS the run as far as the view is
                   concerned — there is no socket to abort — so it has to end itself, and
                   before `showWaiting()` below can raise the waiting state again. */
                if (stoppedResumeKeyRef.current === key) {
                    stoppedResumeKeyRef.current = null;
                    turnSettled = true;
                    settleStopped();
                    return;
                }
                /* No address to ask the agent at, so the history is the only witness. The
                   first pass looks without touching anything, as the run-store path does; the
                   waiting line goes up only once the history has confirmed the turn really is
                   still open. */
                if (!sessionId) {
                    if (await answerFromHistory()) { turnSettled = true; return; }
                    if (!stillMounted()) return;
                    if (takenOverByLiveSubmit()) return;
                    lastPollSaidRunning = true;
                    showWaiting();
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((resolve) => setTimeout(resolve, RESUME_POLL_MS));
                    continue;
                }
                let run = null;
                let missing = false;
                try {
                    run = await llmService.getRun({ sessionId });
                } catch (error) {
                    missing = error?.response?.status === 404;
                    if (!missing) logDev('[LLM] resume poll failed', error);
                }
                if (!stillMounted()) return;
                /* Re-checked AFTER the await too: a submit can land while getRun is in
                   flight, and applying the recovered OLD answer then would overwrite the new
                   turn's bubble and kill its streaming UI mid-run. */
                if (takenOverByLiveSubmit()) return;
                lastPollSaidRunning = Boolean(run && run.status === 'running');
                /* Where the run actually is, and the id Stop needs. Both are things only the
                   server knows once the stream is gone: without the first the panel sits on
                   the position it had when the page went away, and without the second Stop
                   cannot tell the server anything. */
                applyRunProgress(run, investigate);

                if (run?.status === 'cancelled') {
                    /* Someone stopped this run — this reader a moment ago, or another tab.
                       It was neither complete nor an error, so the loop used to poll it for
                       the full fifteen minutes and then write "could not be recovered" over
                       a run that had been stopped on purpose. */
                    turnSettled = true;
                    settleStopped();
                    return;
                }
                if (run && (run.status === 'complete' || run.response)) {
                    applyRun(run);
                    turnSettled = true;
                    return;
                }
                if (run?.status === 'error' || missing) {
                    // The run is gone or failed. Its answer may still have reached history before
                    // that happened, so that is checked before anything is declared lost.
                    turnSettled = true;
                    if (await answerFromHistory()) return;
                    settle({
                        content: run?.error
                            ? `Sorry, this run failed: ${run.error}`
                            : RESUME_LOST_MESSAGE,
                    });
                    return;
                }

                showWaiting();   // only now: the server has said it is still working
                await new Promise((resolve) => setTimeout(resolve, RESUME_POLL_MS));
            }
            turnSettled = true;
            if (stillMounted() && !(await answerFromHistory())) {
                settle({ content: RESUME_LOST_MESSAGE });
            }
        } finally {
            if (resumingConversationRef.current === key) {
                resumingConversationRef.current = null;
            }
            /* The reattach cleans up the mark it put up, on every exit — settle, abandon,
               or timeout. Nothing else owns a reload orphan's mark, and the registry effect
               only clears on an isLoading transition, which the fast path (answer found
               before any waiting state) never makes. Two exceptions: a live submit that took
               this thread over owns the mark now; and a run the server just said is STILL
               WRITING keeps its mark — the dot is telling the truth, and clearing it would
               let the queue put a second turn on the busy history id. A stale mark left this
               way is reconciled the next time the conversation is opened. */
            if (takenOverByLiveSubmit()) {
                // A live submit owns this thread and its mark now.
            } else if (turnSettled) {
                /* The turn is over — the answer is on screen, or a terminal message is. The
                   mark goes down for good: `forgetRun` also stops the next list refresh
                   putting it back from a server field this client has just out-argued. */
                if (conversationId) forgetRun(String(conversationId));
                else clearPendingRun();
            } else if (!markExistedBefore && !lastPollSaidRunning) {
                /* Abandoned before it settled, and the server did not say the run is live.
                   Only a mark THIS reattach put up may go — anything older belongs to
                   whoever put it there. */
                if (conversationId) clearActiveRun(String(conversationId));
                else clearPendingRun();
            }
        }
    }, [announceBackgroundCompletion, applyRunProgress, llmService, settledFunnel,
        updateRunningChatHistory]);

    useEffect(() => {
        if (authLoading) return undefined;
        let isMounted = true;

        const initializeConversations = async () => {
            if (!isAuthenticated) {
                setConversationsState([]);
                setActiveConversationIdState(null);
                activeConversationIdRef.current = null;
                setIsConversationLoading(false);
                setLoadingConversationId(null);
                /* A signed-out reader gets answers too — they have their own quota — and this
                   effect is not the thing that decides when one is finished.

                   It cleared the run flags unconditionally, and `location.state.initialQuery`
                   is among its dependencies. Asking from the home page hands the question over
                   in router state and then REMOVES it, so that a refresh does not ask again —
                   and that removal re-ran this effect a moment after `handleSubmit` had set the
                   flags, wiping them. The request itself carried on, so the answer did arrive;
                   but with `isProcessing` false there was no thinking label, no streaming card
                   and no run snapshot, which is why a guest saw nothing at all for ten seconds
                   and then the whole answer at once, and why refreshing lost the lot.

                   Only a run that is genuinely not in flight is cleared here — and "in flight"
                   includes a reattach, which deliberately has no stream: after a reload the
                   answer is polled for, not streamed. Without `resumingConversationRef` this
                   cleared the waiting state on top of the resume, so the reader watched a
                   static page; worse, the snapshot effect then saw `isProcessing` false and
                   deleted the sessionStorage record, so a second reload lost the run for good. */
                if (!activeStreamIdRef.current && !resumingConversationRef.current) {
                    setIsLoading(false);
                    setIsProcessing(false);
                }
                return;
            }

            const cached = getConversations();
            /* The address bar is the strongest statement of which conversation to show, and
               the only one that survives the tab closing — sessionStorage does not. Resolve it
               to the row id the rest of this effect works in, and let that flow run unchanged. */
            let fromRoute = null;
            if (routePublicId) {
                try {
                    fromRoute = await fetchConversationDetailByPublicId(routePublicId);
                } catch (error) {
                    // A bad or foreign id is a 404 from the backend, which checks ownership.
                    // Fall through to whatever else this mount knows about rather than
                    // stranding the reader on an empty page.
                    logDev('[LLM] Failed to open conversation from the URL', error);
                }
                if (!isMounted) return;
            }
            let nextActiveId = fromRoute?.id
                || initialRunSnapshotRef.current?.conversationId
                || getActiveConversationId();
            const hasInitialQuery = Boolean(location.state?.initialQuery);
            const hasConversationId = Boolean(location.state?.conversationId);
            /* ...or a question from the home page that this mount has already taken.
            
               Consuming that question removes it from location.state, and this effect lists
               location.state.initialQuery among its dependencies — so clearing it ran the
               effect again, this time with nothing telling it to stand back. It then restored
               whatever conversation was most recent over the top of the question being asked:
               the user's message and the spinner vanished for as long as the detail fetch took,
               and came back only when the first token landed. That is the stutter. The ref is
               set before the state is cleared, so it covers the whole gap. */
            // ...or the address bar catching up with the run this mount is already showing.
            // See service/conversationRestore.js for what these cases are and why the address
            // is not always the last word on which conversation to show.
            const shouldSkipRestore = shouldSkipConversationRestore({
                routeConversationId: fromRoute?.id ?? null,
                activeConversationId: activeConversationIdRef.current,
                runningConversationId: runningConversationIdRef.current,
                activeStreamId: activeStreamIdRef.current,
                hasInitialQuery,
                hasConversationId,
                stateConversationId: location.state?.conversationId ?? null,
                isResuming: Boolean(resumingConversationRef.current),
                isInitialQueryTransition: initialQueryTransitionRef.current,
            });

            if (cached.length > 0) {
                setConversationsState(cached);
            }

            try {
                const list = await fetchConversations();
                if (!isMounted) return;
                setConversationsState(list);

                if (!shouldSkipRestore && !nextActiveId && list.length > 0) {
                    nextActiveId = list[0].id;
                    setActiveConversationId(nextActiveId);
                }
            } catch (error) {
                logDev('[LLM] Failed to load conversations', error);
            }

            if (shouldSkipRestore) {
                return;
            }

            if (nextActiveId) {
                const targetId = String(nextActiveId);
                /* Switching threads detaches the view from whatever run it was following. The
                   run itself is released, not aborted: the server keeps writing it, its mark
                   stays in the registry until its own request settles, and reopening its
                   conversation reattaches by session id. Skipping this left the old stream
                   painting into the singleton run state while the restore below repointed that
                   state at the new thread — which is how one conversation's answer landed in
                   another's transcript. (A stream on the conversation being opened never gets
                   here: that is the address-upgrade case, skipped above.) A reattach counts
                   too — it holds `isLoading` without a stream, and leaving it attached would
                   strand the new conversation behind the old one's waiting state. */
                if ((activeStreamIdRef.current || isLoadingRef.current
                        || resumingConversationRef.current)
                    && String(runningConversationIdRef.current ?? '') !== targetId) {
                    cancelStreaming({ abort: false });
                }
                loadingConversationIdRef.current = targetId;
                setLoadingConversationId(targetId);
                setIsConversationLoading(true);
                /* Read BEFORE the fetch: `fetchConversationDetail` upserts the server's copy
                   into the store on its way back, so a read after the await always sees the
                   server's own messages and the comparison below can never be true. A turn
                   can be in flight for this conversation that the server's history does not
                   carry yet — a follow-up released in the background, or a run left behind by
                   New Chat — and the store's optimistic copy is its only record; showing the
                   server's shorter copy would make the question the reader queued here look
                   like it never existed. */
                const storedMessages = getStoredChatHistory(targetId);
                try {
                    const detail = await fetchConversationDetail(nextActiveId);
                    if (!isMounted) return;
                    const serverMessages = detail?.messages || [];
                    /* This conversation's OWN snapshot. The mount-time one is merely the
                       newest, which after a second question is a different conversation's —
                       and reading that one meant the run being opened found no record of
                       itself and drew a bare question. */
                    const runSnapshot = readActiveRunSnapshotFor(targetId)
                        || initialRunSnapshotRef.current;
                    const isSnapshotTarget = runSnapshot?.active
                        && String(runSnapshot.conversationId) === targetId;
                    const serverUnfinished = isExchangeUnfinished(serverMessages);
                    const storedAhead = isConversationRunning(targetId)
                        && storedMessages.length > serverMessages.length
                        && isExchangeUnfinished(storedMessages);
                    /* A run recovered through `GET /run` is written to the local store and
                       nowhere else — there is no client API that can append an answer to
                       server history, so the server's copy still ends at the dangling
                       prompt. `storedAhead` cannot see that: it requires the stored copy to
                       be UNFINISHED, which a recovered answer is precisely not. So the next
                       visit showed the server's shorter copy, decided the exchange was open,
                       polled a run the agent had long since dropped, and wrote "this answer
                       could not be recovered" over the report the reader had already read. */
                    const storedAnswered = !storedAhead
                        && serverUnfinished
                        && storedMessages.length >= serverMessages.length
                        && storedMessages.length > 0
                        && !isExchangeUnfinished(storedMessages);
                    /* A registry mark can outlive its run — a reattach abandoned while the
                       server was still writing keeps the mark up on purpose. Opening the
                       conversation is when the truth arrives: a finished history with no
                       in-flight local turn means nothing is running here, and a stale mark
                       would silently refuse (well, queue forever) every new question. */
                    if (!serverUnfinished && !storedAhead && isConversationRunning(targetId)
                        && String(runningConversationIdRef.current ?? '') !== targetId
                        // ...and this tab is not answering it in the background. That run is
                        // not in the view's refs and its turn is not saved yet, so without
                        // this the mark came down mid-answer — which released every follow-up
                        // still queued for the conversation straight onto the run in flight.
                        && !backgroundRunsRef.current.has(String(targetId))) {
                        clearActiveRun(targetId);
                    }
                    const displayMessages = (storedAhead || storedAnswered)
                        ? storedMessages
                        : (isSnapshotTarget && serverUnfinished
                            ? mergeMessagesWithRunSnapshot(serverMessages, runSnapshot)
                            : serverMessages);
                    /* Only THIS conversation's own snapshot may supply its address. The
                       fallback below is `initialRunSnapshotRef`, which is merely the newest
                       across all slots — after a second question that is a different
                       conversation's, and adopting its session id pointed this thread's
                       follow-ups and its recovery at a stranger's run. */
                    sessionIdRef.current = getStoredSessionId(nextActiveId)
                        || (isSnapshotTarget ? runSnapshot?.sessionId : null)
                        /* The conversation itself now carries the address of the run being
                           written into it, which is the only one left after the tab that
                           started it closed. */
                        || detail?.sessionId
                        || null;
                    if (detail?.sessionId) setStoredSessionId(nextActiveId, detail.sessionId);
                    if (isSnapshotTarget) {
                        /* A paused investigate run is waiting on a question only the reader
                           can answer, and `clarify.timeout_s` is null — so it waits forever.
                           `cancelStreaming` clears the panel on every conversation switch and
                           nothing put it back, which turned "I'll look at the other thread
                           while I think" into a run that could never be resumed and a
                           conversation that stayed unanswered for good. */
                        const restoreInvestigate = getConversationInvestigateMode(targetId);
                        applyPendingClarification(restoreInvestigate ? (runSnapshot.pendingClarification || null) : null);
                        setClarificationDrafts(restoreInvestigate ? (runSnapshot.clarificationDrafts || {}) : {});
                        restoreQueuedPrompts(runSnapshot.queuedPrompts);
                    }
                    setChatHistory(serverUnfinished || storedAhead
                        ? normalizePendingMode(displayMessages, getConversationInvestigateMode(targetId))
                        : displayMessages);
                    setActiveConversationIdState(String(nextActiveId));
                    activeConversationIdRef.current = String(nextActiveId);
                    if (storedAnswered) {
                        // Already answered here, by a recovery the server never heard about.
                        setIsLoading(false);
                        setIsProcessing(false);
                        setStreamingStepName('');
                        clearActiveRun(targetId);
                    } else if (serverUnfinished || storedAhead) {
                        /* The path a plain reload takes. Fire and forget: it polls for minutes
                           and the restore must not wait on it. `displayMessages` retains all
                           progress received before the connection was interrupted.

                           Alive while the VIEW still shows this conversation — not while this
                           effect instance does. The effect re-runs for reasons that have
                           nothing to do with the reader (an address upgrade consuming router
                           state, say), and tying the poll to `isMounted` let such a re-run
                           kill the recovery of an answer the reader was still waiting for,
                           with the waiting card up and nothing behind it. */
                        resumeUnfinishedRun(
                            nextActiveId,
                            storedAhead ? storedMessages : serverMessages,
                            () => String(activeConversationIdRef.current ?? '') === targetId,
                            detail?.isInvestigate,
                            displayMessages,
                        );
                    } else if (isSnapshotTarget) {
                        // The backend finished while this page was reconnecting.
                        setIsLoading(false);
                        setIsProcessing(false);
                        setStreamingStepName('');
                    }
                } catch (error) {
                    logDev('[LLM] Failed to load conversation detail', error);
                } finally {
                    if (isMounted && loadingConversationIdRef.current === targetId) {
                        setIsConversationLoading(false);
                        setLoadingConversationId(null);
                    }
                }
            } else {
                setActiveConversationIdState(null);
                activeConversationIdRef.current = null;
                setIsConversationLoading(false);
                setLoadingConversationId(null);
            }
        };

        initializeConversations();
        return () => {
            isMounted = false;
        };
        // `applyPendingClarification` and `restoreQueuedPrompts` are `useCallback(_, [])` —
        // stable for the life of the mount, so naming them changes nothing but the linter.
    }, [authLoading, isAuthenticated, location.state?.initialQuery, location.state?.conversationId,
        routePublicId, resumeUnfinishedRun, cancelStreaming,
        applyPendingClarification, restoreQueuedPrompts]);

    /* Reattach a run that has no conversation behind it.
     *
     * The effect above restores from the conversation store, which is empty for a signed-out
     * reader — they never get a row. Their run is nonetheless real, still being written, and
     * retained by the agent against its session id, so the snapshot is the whole record: the
     * messages it was showing and the id to poll. Without this a guest who refreshed mid-answer
     * got their question back and then waited forever on an answer nothing was fetching.
     */
    useEffect(() => {
        const snapshot = initialRunSnapshot;
        if (!snapshot?.active || snapshot.conversationId || !snapshot.sessionId) return undefined;
        const messages = Array.isArray(snapshot.messages) ? snapshot.messages : [];
        if (!messages.length) return undefined;
        let mounted = true;
        sessionIdRef.current = snapshot.sessionId;
        runIdRef.current = snapshot.runId || null;
        resumeUnfinishedRun(
            null, messages, () => mounted, snapshot.investigate, messages, snapshot.sessionId,
            // The snapshot says the run was live. That is a better witness than the shape of
            // a message the drip had only half written.
            true,
        );
        return () => { mounted = false; };
        // `initialRunSnapshot` is read once at construction and keeps the same identity for the
        // life of the mount, so naming it here is honest and still runs this only once.
    }, [initialRunSnapshot, resumeUnfinishedRun]);

    /* Put the conversation in the address bar once it has one.
    
       `replace`, not `push`: this is the same page gaining its own name, not a navigation the
       reader made — a pushed entry would make Back step through /chat -> /chat/<id> and look
       like it did nothing. Only ever upgrades /chat to /chat/<id>; it never rewrites one
       conversation's URL to another's, which is what would fight a reader mid-navigation. */
    useEffect(() => {
        /* Only while the chat page is the one being looked at.
        
           This component is mounted persistently — Layout keeps it alive behind every other
           page so a run survives navigation — so without this guard the effect fires on the
           home page too. It did, and it broke New Chat: that button clears the conversation
           and sends the reader home, the agent then restored the most recent conversation
           into `activeConversationId`, and this navigated straight back into it. There was no
           way to reach an empty chat. */
        // `isRouteActive`, not the path: /chat itself is the chat HOME, and this effect running
        // there is exactly the New Chat bug above.
        if (!isRouteActive) return;
        if (!activeConversationId) return;
        if (routePublicId) return;                       // the URL already names a conversation
        const current = conversationsState.find((c) => String(c.id) === String(activeConversationId));
        const publicId = current?.publicId;
        if (!publicId) return;                           // a row the backend has not backfilled
        navigate(`/chat/${publicId}`, { replace: true });
    }, [isRouteActive, activeConversationId, conversationsState, routePublicId, navigate]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const conversationId = location.state?.conversationId;
        if (!conversationId) return;
        let isMounted = true;

        const loadConversation = async () => {
            const targetId = String(conversationId);
            const runningId = runningConversationIdRef.current
                ? String(runningConversationIdRef.current)
                : null;
            const hasLiveRun = Boolean(
                activeStreamIdRef.current
                || resumingConversationRef.current
                || isLoadingRef.current,
            );

            const navigationAction = getLiveConversationNavigationAction({
                requestedConversationId: targetId,
                activeConversationId: runningId,
                hasLiveRun,
            });

            if (navigationAction === 'continue-current') {
                // Returning from History to the conversation that is already streaming must be a
                // pure view change. The old code invalidated activeStreamId here, so every later
                // SSE frame was discarded and only the final polling recovery remained.
                setActiveConversationId(targetId);
                setActiveConversationIdState(targetId);
                activeConversationIdRef.current = targetId;
                setChatHistory(runningChatHistoryRef.current);
                setSelectedMessageIndex(null);
                const { conversationId: _consumedConversationId, ...restState } = location.state || {};
                navigate(location.pathname, {
                    replace: true,
                    state: Object.keys(restState).length ? restState : null,
                });
                return;
            }

            /* Opening any OTHER conversation detaches the view from the run it was following.
               The run is released, not aborted — the server keeps writing it, its registry
               mark stands until its own request settles, and coming back here reattaches by
               session id. This used to preserve the live run's grip on the singleton view
               state instead ("load-other"), which meant the conversation being opened could
               not reattach to its own unfinished answer — and worse, a resume started for it
               later repointed the running refs while the old stream still wrote through them,
               landing one conversation's answer in another's transcript. */
            cancelStreaming({ abort: false });
            loadingConversationIdRef.current = targetId;
            setLoadingConversationId(targetId);
            setIsConversationLoading(true);
            /* Read before the fetch — the fetch upserts the server copy over the store.
               See the route restore above. */
            const storedMessages = getStoredChatHistory(targetId);
            try {
                const detail = await fetchConversationDetail(conversationId);
                if (!isMounted) return;
                const nextId = String(detail?.id || conversationId);
                setConversationsState(getConversations());
                setActiveConversationId(nextId);
                setActiveConversationIdState(nextId);
                activeConversationIdRef.current = nextId;
                sessionIdRef.current = getStoredSessionId(nextId) || detail?.sessionId || null;
                if (detail?.sessionId) setStoredSessionId(nextId, detail.sessionId);
                lastAutoSelectedRef.current = null;
                setHoveredPubmedId(null);
                const serverMessages = detail?.messages || [];
                const storedAhead = isConversationRunning(nextId)
                    && storedMessages.length > serverMessages.length
                    && isExchangeUnfinished(storedMessages);
                // A locally recovered answer over an unfinished server copy — see the route
                // restore above for why the server's copy stays short.
                const storedAnswered = !storedAhead
                    && isExchangeUnfinished(serverMessages)
                    && storedMessages.length >= serverMessages.length
                    && storedMessages.length > 0
                    && !isExchangeUnfinished(storedMessages);
                // Reconcile a stale mark on visit — see the route restore above.
                if (!isExchangeUnfinished(serverMessages) && !storedAhead
                    && isConversationRunning(nextId)
                    && String(runningConversationIdRef.current ?? '') !== nextId
                    // See the route restore above: a background follow-up this tab is
                    // writing is not stale, however finished the server's copy looks.
                    && !backgroundRunsRef.current.has(String(nextId))) {
                    clearActiveRun(nextId);
                }
                /* And the same run-snapshot merge as the route restore. Router state survives
                   a reload, so a reload of a conversation opened from the sidebar lands HERE,
                   not there — without the merge, the partial answer and progress streamed
                   before the reload vanished until the poll recovered the finished run. */
                // This conversation's own snapshot first — see the route restore above.
                const runSnapshot = readActiveRunSnapshotFor(nextId)
                    || initialRunSnapshotRef.current;
                const isSnapshotTarget = runSnapshot?.active
                    && String(runSnapshot.conversationId) === nextId;
                const serverUnfinished = isExchangeUnfinished(serverMessages);
                const displayMessages = (storedAhead || storedAnswered)
                    ? storedMessages
                    : (isSnapshotTarget && serverUnfinished
                        ? mergeMessagesWithRunSnapshot(serverMessages, runSnapshot)
                        : serverMessages);
                // The snapshot's session id is the only address left when the per-conversation
                // store was emptied by the reload — same fallback as the route restore.
                if (!sessionIdRef.current && isSnapshotTarget) {
                    sessionIdRef.current = runSnapshot?.sessionId || null;
                }
                if (isSnapshotTarget) {
                    // The paused clarify round comes back with its conversation — see the
                    // route restore above.
                    const restoreInvestigate = getConversationInvestigateMode(nextId);
                    applyPendingClarification(restoreInvestigate ? (runSnapshot.pendingClarification || null) : null);
                    setClarificationDrafts(restoreInvestigate ? (runSnapshot.clarificationDrafts || {}) : {});
                    restoreQueuedPrompts(runSnapshot.queuedPrompts);
                }
                setChatHistory(serverUnfinished || storedAhead
                    ? normalizePendingMode(displayMessages, getConversationInvestigateMode(nextId))
                    : displayMessages);
                setSelectedMessageIndex(null);
                setShowReloadPrompt(false);
                llmService.clearHistory();
                if (storedAnswered) {
                    clearActiveRun(nextId);
                } else if (serverUnfinished || storedAhead) {
                    /* The conversation may have been left mid-answer. Fire and forget: this
                       polls for minutes, and the load itself must not wait on it. Alive while
                       the VIEW still shows this conversation, not while this effect instance
                       does — see the route restore above for why. */
                    resumeUnfinishedRun(
                        nextId,
                        storedAhead ? storedMessages : serverMessages,
                        () => String(activeConversationIdRef.current ?? '') === nextId,
                        detail?.isInvestigate,
                        displayMessages,
                    );
                }
            } catch (error) {
                logDev('[LLM] Failed to load selected conversation', error);
            } finally {
                if (isMounted && loadingConversationIdRef.current === targetId) {
                    setIsConversationLoading(false);
                    setLoadingConversationId(null);
                }
            }
        };

        loadConversation();
        return () => {
            isMounted = false;
        };
    }, [
        isAuthenticated,
        location.pathname,
        location.state,
        cancelStreaming,
        llmService,
        navigate,
        resumeUnfinishedRun,
        // Both `useCallback(_, [])`, so stable — see the route restore above.
        applyPendingClarification,
        restoreQueuedPrompts,
    ]);

    const startNewConversation = useCallback((options = {}) => {
        /* `keepRunning` releases the run instead of ending it. Everything below still clears —
           the view is starting over — but the request is left open, so the server finishes the
           answer and writes it to its own history. `cancelStreaming` already sets
           `activeStreamIdRef` to null, which is what detaches the frames from this view; the
           registry entry stays until the request itself settles, so the conversation keeps its
           working dot in the sidebar meanwhile. */
        const departingRunKey = activeConversationIdRef.current == null
            ? queueOwnerKeyRef.current
            : null;
        const departingConversationId = activeConversationIdRef.current
            ?? runningConversationIdRef.current
            ?? null;
        cancelStreaming({ abort: !options.keepRunning });
        /* Only the chat being left, and only when its run is being ended with it. `keepRunning`
           means the answer is still being written on the server and the reader can come back to
           it — throwing its snapshot away would lose the partial answer and the progress it had
           already shown them. Other conversations' snapshots were never this button's to
           delete: their runs are still going. */
        if (!options.keepRunning) {
            clearActiveRunSnapshot(departingConversationId);
            snapshotConversationIdRef.current = undefined;
        }
        /* Under `keepRunning` the ref is LEFT NAMING the departing conversation. Blanking it
           here undid the line above: the snapshot effect then ran with no slot to name, and
           the branch that handled that used to clear the whole store — so New Chat during a
           run destroyed the record of the very answer this option exists to preserve. */
        liveRunSnapshotRef.current = null;
        if (!options.skipHistoryReset) {
            setChatHistory([]);
        }
        setSelectedMessageIndex(null);
        lastAutoSelectedRef.current = null;
        setHoveredPubmedId(null);
        sessionIdRef.current = null;
        runIdRef.current = null;
        setStreamingStepName('');
        setShowReloadPrompt(false);
        applyPendingClarification(null);
        setClarificationDrafts({});
        setClarificationError('');
        setClarificationSubmitting(false);
        setIsConversationLoading(false);
        setLoadingConversationId(null);
        loadingConversationIdRef.current = null;
        setActiveConversationIdState(null);
        activeConversationIdRef.current = null;
        setActiveConversationId(null);
        /* Starting a new chat drops only the provisional questions owned by the chat being
           left. Another simultaneously-created nameless run can have its own queue, and named
           conversations are still being written and will still take their follow-ups. */
        setQueuedPrompts((prev) => promptsSurvivingReset(prev, departingRunKey));
        queueOwnerKeyRef.current = null;
        llmService.clearHistory();
    }, [cancelStreaming, llmService]);

    /**
     * Follow the answer down — but only while the reader is standing at the bottom.
     *
     * The view follows the text while the reader is at the bottom, and stops the moment they
     * scroll up to re-read something. It stays stopped until they come back down. This is the
     * behaviour every chat product has, and the reason is that scroll position is the reader's
     * to own: nothing the page does to itself may move it.
     *
     * Two things were moving it.
     *
     * The settled branch called `scrollToBottom(true)` with no check at all — and this effect
     * lists `chatHistory` among its dependencies, which keeps changing AFTER the answer lands
     * (references attach, the `saved` frame patches invocation ids in, the history refresh
     * rewrites the list). Each of those smooth-scrolled the reader back to the bottom, so
     * scrolling up through a finished answer bounced you back down, repeatedly. Only the
     * streaming branch had the "the reader scrolled away" guard; the one that fires after the
     * answer pops did not.
     *
     * And `distance` was sampled inside the effect, i.e. at React's convenience rather than the
     * reader's. Mid-drag the container is momentarily within slack of the bottom, which read as
     * consent to jump. The pin is now updated from the container's own scroll event, so it
     * describes where the reader actually is.
     *
     * A new turn re-pins: asking a question is a deliberate act, and the asker means to watch
     * the answer, wherever they had scrolled to beforehand.
     */
    const scrollFrameRef = useRef(0);
    const pinnedToBottomRef = useRef(true);
    const lastMessageCountRef = useRef(chatHistory.length);
    const scrollToBottom = useCallback((smooth = true) => {
        const container = messagesContainerRef.current;
        /* Not `scrollIntoView`: it scrolls EVERY scrollable ancestor, so it moves the page
           itself as well as the message list (see the reference-list scroll below, which
           avoids it for the same reason). Driving the container's own scrollTop keeps the
           movement inside the thread. */
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
            return;
        }
        messagesEndRef.current?.scrollIntoView({
            behavior: smooth ? 'smooth' : 'auto',
            block: 'end',
        });
    }, []);

    // The pin follows the reader's own scrolling, not the render cycle.
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return undefined;
        const onScroll = () => {
            const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
            pinnedToBottomRef.current = distance <= AUTO_FOLLOW_SLACK_PX;
        };
        onScroll();
        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, [activeConversationId, isRouteActive]);

    const handleClick = (event, link) => {
        event.preventDefault();
        window.open(link, '_blank');
    };

    useEffect(() => {
        const grew = chatHistory.length > lastMessageCountRef.current;
        lastMessageCountRef.current = chatHistory.length;
        if (grew) pinnedToBottomRef.current = true;
        if (!pinnedToBottomRef.current) return undefined;

        if (!isViewingRunningConversation || !isProcessing) {
            scrollToBottom(true);
            return undefined;
        }
        if (scrollFrameRef.current) return undefined;
        scrollFrameRef.current = rafSchedule(() => {
            scrollFrameRef.current = 0;
            scrollToBottom(false);
        });
        return () => {
            if (scrollFrameRef.current) {
                rafCancel(scrollFrameRef.current);
                scrollFrameRef.current = 0;
            }
        };
    }, [chatHistory, streamingGroups, isProcessing, isViewingRunningConversation, scrollToBottom]);

    // Debounced rather than written on every change: this is a synchronous main-thread write of
    // `JSON.stringify(chatHistory)` — the whole conversation, references included — and it ran
    // once per streamed chunk. The tab only needs the latest state, not every intermediate one.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const id = setTimeout(() => {
            sessionStorage.setItem('llmChatHistory', JSON.stringify(chatHistory));
        }, CHAT_PERSIST_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [chatHistory]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        sessionStorage.setItem('llmWasProcessing', isProcessing ? 'true' : 'false');
    }, [isProcessing]);

    // Keep the live presentation state separate from the saved conversation. History only gains
    // the final assistant message, while this snapshot preserves what the reader had already seen
    // (steps, partial text and Investigate progress) if the transport or page reconnects.
    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (!isProcessing) {
            if (snapshotWriteTimerRef.current) {
                window.clearTimeout(snapshotWriteTimerRef.current);
                snapshotWriteTimerRef.current = null;
            }
            /* Only the snapshot of the run THIS view was following, and only when that run has
               actually finished.
            
               `isProcessing` goes false for two very different reasons: the run ended, or the
               view let go of it — switching conversations releases a run that keeps being
               written on the server. The old unconditional `clearActiveRunSnapshot()` could
               not tell them apart and wiped the record either way, so leaving a conversation
               mid-answer threw away its partial answer and its Investigate progress. Coming
               back showed a bare question with nothing behind it, which is the "the query and
               the answer are both gone" the reader sees.
            
               The registry knows the difference: a released run keeps its mark until its own
               request settles. */
            const snapshotKey = snapshotConversationIdRef.current;
            /* Releasing the view can happen inside the throttle window. Flush the in-memory
               snapshot before clearing it; otherwise a quick switch loses the only copy of
               Investigate progress (and of a clarification that arrived just before it). */
            if (snapshotKey !== undefined
                && isConversationRunning(snapshotKey)
                && liveRunSnapshotRef.current) {
                writeActiveRunSnapshot(liveRunSnapshotRef.current);
            }
            liveRunSnapshotRef.current = null;
            initialRunSnapshotRef.current = null;
            /* `undefined` means this mount owns no slot — either nothing has been written
               yet, or the slot it wrote has already been cleared. Either way there is
               nothing here to take down, and the whole-store clear that used to live in
               this branch took down every OTHER run's record with it. That is the bug the
               per-conversation slots were introduced to remove; clearing them all from the
               one branch that cannot name a slot put it straight back. Stale entries are
               aged out by `readAll` on their own. */
            if (snapshotKey !== undefined && !isConversationRunning(snapshotKey)) {
                clearActiveRunSnapshot(snapshotKey);
                snapshotConversationIdRef.current = undefined;
            }
            return;
        }

        /* No conversation id is not a reason to skip the snapshot. A signed-out reader never
           gets one, so this returned early for every guest run and a refresh mid-answer took
           the question and the answer with it. The snapshot now carries the messages it
           describes, so it can restore a run that no conversation store knows about. */
        const conversationId = runningConversationIdRef.current || null;
        const runHistory = runningChatHistoryRef.current;
        let assistantMessage = null;
        for (let i = runHistory.length - 1; i >= 0; i -= 1) {
            if (runHistory[i]?.role === 'assistant') {
                assistantMessage = {
                    role: 'assistant',
                    content: runHistory[i].content || '',
                    timestamp: runHistory[i].timestamp || null,
                    investigateMode: Boolean(runHistory[i].investigateMode),
                };
                break;
            }
        }

        /* Which slot the next write lands in, so the branch above can clear that one and
           leave every other run's record standing. A signed-in run starts nameless — the
           conversation row is a round trip away — and writes into the guest slot meanwhile;
           when its id arrives, that interim copy is handed back rather than left behind as
           the newest snapshot in the store. */
        if (snapshotConversationIdRef.current === null && conversationId != null) {
            releaseGuestSlot();
        }
        snapshotConversationIdRef.current = conversationId;
        liveRunSnapshotRef.current = {
            conversationId,
            queueOwnerKey: conversationId == null ? queueOwnerKeyRef.current : null,
            /* The follow-ups the reader has already handed over. `submitOrQueue` takes the
               text out of the composer and draws it as a pending bubble, so from their side
               the question has been SENT — and it lived in React state alone, so the reload
               this whole mechanism exists for destroyed it without a word. Asking a second
               question while the first is answering is exactly what problem 3 is, so it was
               the one input shape the durability work had skipped. */
            queuedPrompts: queuedPrompts.filter((entry) => (
                entry?.conversationId == null
                    ? conversationId == null
                    : String(entry.conversationId) === String(conversationId)
            )),
            // The thread itself, not just a pointer to it. For a signed-in reader the stored
            // conversation is the better source and this is a fallback; for a guest it is the
            // only record that survives the page.
            messages: runHistory,
            sessionId: sessionIdRef.current,
            runId: runIdRef.current,
            investigate: Boolean(runningInvestigateRef.current),
            assistantMessage,
            thinkingSteps: thinkingStepsRef.current,
            streamingAnswer: streamingAnswerRef.current,
            streamingGroups,
            preamble: preambleRef.current,
            preambleText,
            streamingStepName,
            investigatePhase,
            investigateFunnel,
            /* The counters' own reading, so a reload does not walk Retrieved backwards: the
               ramp restarts from zero in a fresh panel, and without this the restored run
               would settle on the agent's smaller raw count. Read from the ref rather than
               state — nothing re-renders when a counter animates, and it should not. */
            investigateDisplayFunnel: investigateDisplayFunnelRef.current,
            investigateStartedAt,
            investigatePercent,
            investigateKeywords,
            investigatePapers,
            investigateDetail,
            pendingClarification,
            clarificationDrafts,
        };

        // Throttle rather than debounce: a busy stream may update continuously for minutes, and
        // repeatedly cancelling a debounce would leave no usable snapshot during that whole time.
        if (!snapshotWriteTimerRef.current) {
            snapshotWriteTimerRef.current = window.setTimeout(() => {
                snapshotWriteTimerRef.current = null;
                if (liveRunSnapshotRef.current) {
                    writeActiveRunSnapshot(liveRunSnapshotRef.current);
                }
            }, CHAT_PERSIST_DEBOUNCE_MS);
        }
    }, [
        clarificationDrafts,
        investigateDetail,
        queuedPrompts,
        investigateFunnel,
        investigateKeywords,
        investigatePapers,
        investigatePercent,
        investigatePhase,
        investigateStartedAt,
        isProcessing,
        pendingClarification,
        preambleText,
        runHistoryRevision,
        streamingGroups,
        streamingStepName,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const flushSnapshot = () => {
            if (liveRunSnapshotRef.current) {
                writeActiveRunSnapshot(liveRunSnapshotRef.current);
            }
            // The throttled conversation-store write holds up to 400ms of transcript; the
            // page is going away, so it lands now or never.
            flushConversationStoreWrite();
        };
        window.addEventListener('pagehide', flushSnapshot);
        return () => {
            window.removeEventListener('pagehide', flushSnapshot);
            if (snapshotWriteTimerRef.current) {
                window.clearTimeout(snapshotWriteTimerRef.current);
                snapshotWriteTimerRef.current = null;
            }
            flushSnapshot();
        };
    }, [flushConversationStoreWrite]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handlePageShow = (event) => {
            if (!event.persisted || !isProcessing) return;
            /* Same guard the mount initialiser has always had: a run with an address to be
               collected at is not interrupted, it is still being written. Arming the banner
               here renders "Response interrupted — reload the latest message" INSTEAD of the
               message body, so when the answer landed it was hidden behind an invitation to
               spend a second run getting what the first one had already produced. */
            const recoverable = sessionIdRef.current
                || runIdRef.current
                || getStoredSessionId(runningConversationIdRef.current);
            if (recoverable) return;
            setShowReloadPrompt(true);
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [isProcessing]);

    useEffect(() => {
        if (
            location.state?.initialQuery
            && consumedInitialQueryKeyRef.current !== location.key
        ) {
            consumedInitialQueryKeyRef.current = location.key;
            initialQueryTransitionRef.current = true;
            const query = location.state.initialQuery;
            const searchOptions = location.state.initialSearchOptions || null;
            const queryMethod = location.state.initialQueryMethod || 'button';
            // Consuming the query means REMOVING it. React Router keeps navigation state in
            // `history.state`, which the browser restores on reload, but the ref that says it has
            // already been used is component state and does not survive one. So a refresh re-ran
            // the question: a second full agent run, billed again, while the first run's answer —
            // which the server was still writing to history — was never shown.
            const { initialQuery: _consumedQuery, initialSearchOptions: _consumedOptions,
                    initialQueryMethod: _consumedQueryMethod,
                    ...restState } = location.state;
            navigate(location.pathname, {
                replace: true,
                state: Object.keys(restState).length ? restState : null,
            });
            initialSearchOptionsRef.current = searchOptions;
            // Adopt the tier the home page's picker was showing, so the chip here names what this
            // first turn actually ran on. Normally the same value is already in localStorage
            // (both pickers write it), but the handover must not depend on that.
            if (searchOptions?.serviceTier) {
                setServiceTier(searchOptions.serviceTier);
            }
            /* Asked even while another conversation is answering. This used to be behind
               `if (!isLoading)`, so a question handed over from the home page during a run was
               dropped without a word — the reader watched their question sit there and nothing
               happen. It opens its own conversation, which does not race the run in flight.

               `keepRunning` so the answer being written is released rather than aborted: this
               is a new thread, not a replacement for the old one. */
            startNewConversation({ skipHistoryReset: true, keepRunning: isLoading });
            handleSubmit(null, query, null, {
                forceNewConversation: true,
                searchOptions,
                queryMethod,
            });
        }
    }, [location.state, location.pathname, location.key, navigate, isLoading, startNewConversation]);

    useEffect(() => {
        if (chatHistory.length > 0 || isProcessing) {
            initialQueryTransitionRef.current = false;
        }
    }, [chatHistory.length, isProcessing]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!activeConversationIdRef.current) return;
        /* While the attached run is writing this very conversation, the running-history
           updater owns the store copy (throttled); re-parsing and deep-comparing the whole
           list here on every streamed tick only ever concluded "nothing to do", at real
           main-thread cost. */
        if (isLoadingRef.current
            && String(runningConversationIdRef.current ?? '')
                === String(activeConversationIdRef.current)) {
            return;
        }
        const currentList = getConversations();
        const active = currentList.find((item) => item.id === activeConversationIdRef.current);
        const storedMessages = active?.messages || [];
        if (areMessagesEqual(storedMessages, chatHistory)) return;
        const nextList = updateConversationMessages(
            currentList,
            activeConversationIdRef.current,
            chatHistory
        );
        setConversationsState(nextList);
        setConversations(nextList);
    }, [chatHistory]);

    const collapseReferences = useCallback((widthToStore) => {
        if (isReferencesCollapsed) return;
        setIsReferencesCollapsed(true);
    }, [isReferencesCollapsed]);

    const expandReferences = useCallback(() => {
        setIsReferencesCollapsed(false);
    }, []);

    useEffect(() => {
        if (!isRouteActive || useMobileReferencesDrawer || !splitContainerRef.current) return undefined;
        const splitContainer = splitContainerRef.current;

        const updateReferenceLayout = () => {
            setIsReferencesCollapsed(
                splitContainer.getBoundingClientRect().width < MIN_SPLIT_WIDTH_WITH_REFERENCES
            );
        };

        updateReferenceLayout();
        const resizeObserver = new ResizeObserver(updateReferenceLayout);
        resizeObserver.observe(splitContainer);
        return () => resizeObserver.disconnect();
    }, [isRouteActive, useMobileReferencesDrawer]);

    const updateSplitWidth = useCallback((clientX) => {
        if (!splitContainerRef.current) return;
        if (isReferencesCollapsed) return;
        const rect = splitContainerRef.current.getBoundingClientRect();
        const availableWidth = Math.max(1, rect.width - DIVIDER_PX);
        const offset = clientX - rect.left;
        const nextWidth = (offset / rect.width) * 100;
        const minLeftPercent = Math.min(100, (LEFT_MIN_PX / availableWidth) * 100);
        const maxLeftPercent = Math.max(0, 100 - (RIGHT_MIN_PX / availableWidth) * 100);
        const safeMin = Math.min(minLeftPercent, maxLeftPercent);
        const safeMax = Math.max(minLeftPercent, maxLeftPercent);
        const collapseThreshold = Math.min(FALLBACK_COLLAPSE_THRESHOLD, safeMax + 2);

        if (nextWidth >= collapseThreshold) {
            const clamped = Math.min(safeMax, Math.max(safeMin, nextWidth));
            collapseReferences(clamped);
            return;
        }
        const clamped = Math.min(safeMax, Math.max(safeMin, nextWidth));
        setLeftPaneWidth(clamped);
    }, [collapseReferences, isReferencesCollapsed]);

    const updateSplitIndicator = useCallback((clientY) => {
        if (!splitContainerRef.current) return;
        const rect = splitContainerRef.current.getBoundingClientRect();
        const offset = clientY - rect.top;
        const clamped = Math.min(rect.height, Math.max(0, offset));
        setDragIndicatorY(clamped);
    }, []);

    const handleSplitMouseDown = (event) => {
        if (isReferencesCollapsed) return;
        event.preventDefault();
        isDraggingSplitRef.current = true;
        setIsDraggingSplit(true);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        updateSplitWidth(event.clientX);
        updateSplitIndicator(event.clientY);
    };

    useEffect(() => {
        const handleMouseMove = (event) => {
            if (!isDraggingSplitRef.current) return;
            updateSplitWidth(event.clientX);
            updateSplitIndicator(event.clientY);
        };

        const handleMouseUp = () => {
            if (!isDraggingSplitRef.current) return;
            isDraggingSplitRef.current = false;
            setIsDraggingSplit(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [updateSplitWidth, updateSplitIndicator]);


    useEffect(() => {
        const container = document.querySelector('.chat-container');
        if (!container) return;

        const handleMouseOver = (e) => {
            const link = e.target.closest('a[href*="pubmed.ncbi.nlm.nih.gov"]');
            if (link && link.href) {
                const pubmedId = link.href.split('/').filter(Boolean).pop();
                setHoveredPubmedId(pubmedId);
            }
        };

        const handleMouseOut = (e) => {
            const link = e.target.closest('a[href*="pubmed.ncbi.nlm.nih.gov"]');
            if (link) {
                setHoveredPubmedId(null);
            }
        };

        const handleReferenceClick = (e) => {
            const link = e.target.closest('a[href*="pubmed.ncbi.nlm.nih.gov"]');
            if (!link || !link.href) return;
            e.preventDefault();
            const pubmedId = link.href.split('/').filter(Boolean).pop();
            if (!pubmedId) return;
            const messageCard = link.closest('.message-card');
            const messageIndex = messageCard ? Number(messageCard.dataset.messageIndex) : null;
            const messageRole = messageCard?.dataset?.messageRole;
            if (Number.isFinite(messageIndex) && messageRole === 'assistant') {
                handleMessageClick(messageIndex);
            } else if (useMobileReferencesDrawer) {
                setIsMobileReferencesDrawerOpen(true);
            } else if (isReferencesCollapsed) {
                expandReferences();
            }
            setHoveredPubmedId(pubmedId);
        };

        const handleMouseLeave = () => {
            setHoveredPubmedId(null);
        };

        container.addEventListener('mouseover', handleMouseOver);
        container.addEventListener('mouseout', handleMouseOut);
        container.addEventListener('click', handleReferenceClick);
        container.addEventListener('mouseleave', handleMouseLeave);

        const links = container.querySelectorAll('a');
        links.forEach(link => {
            if (link.href && link.href.includes('pubmed.ncbi.nlm.nih.gov')) {
                link.removeAttribute('target');
                link.removeAttribute('rel');
                return;
            }
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });

        return () => {
            container.removeEventListener('mouseover', handleMouseOver);
            container.removeEventListener('mouseout', handleMouseOut);
            container.removeEventListener('click', handleReferenceClick);
            container.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [chatHistory, expandReferences, isReferencesCollapsed, useMobileReferencesDrawer]);

    const parseReferences = (refs) => {
        if (!refs || !Array.isArray(refs)) return [];

        return refs.map((ref) => {
            if (Array.isArray(ref)) {
                const [title, pubmedUrl, citationCount, year, journal, authors] = ref;
                return {
                    title,
                    url: pubmedUrl,
                    citation_count: citationCount,
                    year,
                    journal,
                    authors: Array.isArray(authors) ? authors.join(', ') : 'Authors not available',
                    evidence: [],
                };
            }
            const title = ref?.title || '';
            const url = ref?.url || '';
            const pmid = ref?.pmid || null;
            const citationCount = ref?.n_citation ?? ref?.citation_count ?? 0;
            const year = ref?.date ?? ref?.year ?? '';
            const journal = ref?.journal || '';
            const authors = Array.isArray(ref?.authors) ? ref.authors.join(', ') : 'Authors not available';
            const evidence = Array.isArray(ref?.evidence) ? ref.evidence : [];
            return {
                pmid,
                title,
                url,
                citation_count: citationCount,
                year,
                journal,
                authors,
                evidence,
                // The PMC full text, where the paper has one. This function rebuilds references
                // through a whitelist, so a field it does not name is silently dropped — which is
                // what happened to this one: the agent sent it and nothing downstream ever saw it.
                fulltext_url: ref?.fulltext_url || '',
            };
        });
    };

    const handleSubmit = async (e, input = null, t = null, options = {}) => {
        const inputText = input || userInput;
        e && e.preventDefault();
        /* Returns whether a turn was STARTED. A queued follow-up is taken out of the queue
           before it is submitted (so a conversation switch cannot submit it twice), so a
           refusal here has to be reported — otherwise the question the reader already
           watched leave their composer ends here, silently. */
        if (!inputText.trim()) return false;
        /* Every route to a question ends here — the composer, a released follow-up, and the
           query handed over in the home page's navigation state. A guest who has used the
           month's questions gets the sign-in overlay and keeps their question in the box. */
        if (isLimitReachedEffective) {
            if (isGuest) requireSignIn(limitWarningText);
            return false;
        }

        /* Which conversation this turn belongs to. Normally the one on screen, but a released
           queued prompt names its own: it was written as a follow-up to a particular thread and
           has to land there even if the reader has since moved on to another one. */
        const targetConversationId = options.conversationId ?? activeConversationIdRef.current;
        /* "No conversation id" is not the same as "new conversation". A signed-out reader
           never gets an id, so reading it that way made every guest follow-up start over:
           the second question REPLACED the first exchange on screen — the answer the reader
           was just looking at, gone. A new thread is one that was asked for (`New Chat`, the
           home page hand-over) or one where there is genuinely nothing on screen to follow.
           The agent's context never depended on this: it follows the session id, which is
           kept either way. */
        const shouldStartNewConversation = options.forceNewConversation
            || (!targetConversationId && chatHistory.length === 0);
        /* A run in flight only blocks a second turn in the conversation that is running it —
           that one would race its own predecessor, and `submitOrQueue` holds it back instead.
           A question asked anywhere else is a different session with a different history id,
           and the backend locks per history id rather than per user, so it may simply start.
           This used to refuse every submit while `isLoading`, which meant a reader with a new
           question had to wait out an answer they had already stopped reading. */
        /* Asked of the registry rather than the foreground flags. `runningConversationIdRef`
           is nulled by `cancelStreaming` even on the release path, and `isLoading` describes
           the view, so after New Chat both said "nothing is running" while the released run
           was still being written — and reopening that conversation from History let a second
           turn start on the same history id, which is the race this refuses. */
        if (!shouldStartNewConversation && isConversationRunning(targetConversationId)) {
            return false;
        }
        const baseHistory = Array.isArray(options.baseHistory)
            ? options.baseHistory
            : (shouldStartNewConversation ? [] : chatHistory);
        const streamId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        if (shouldStartNewConversation || !queueOwnerKeyRef.current) {
            queueOwnerKeyRef.current = streamId;
        }
        /* Only the NAMELESS thread has entries waiting for a conversation id, and only its
           own run may adopt them. The key outlives a finished turn on purpose (a thread
           keeps its queue across transports), so passing it from a run that already HAS a
           conversation handed that run another conversation's follow-ups: they left the
           thread they were written for, and — their conversation id rewritten — came back
           looking like follow-ups nothing had sent yet. */
        const queueOwnerKey = targetConversationId == null ? queueOwnerKeyRef.current : null;
        activeStreamIdRef.current = streamId;
        // A Stop aimed at the PREVIOUS run must not follow this one, which the reader has
        // only just asked for.
        stopRequestedStreamRef.current = null;
        stoppedResumeKeyRef.current = null;

        setShowReloadPrompt(false);

        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const requestStartedAt = Date.now();

        const requestSearchOptions = options.searchOptions
            || (shouldStartNewConversation ? initialSearchOptionsRef.current : null) || null;
        initialSearchOptionsRef.current = null;
        const trackQuerySubmitSuccess = createQuerySubmitSuccessTracker(options.queryMethod);
        const investigateEnabled = Boolean(
            requestSearchOptions?.investigateEnabled
                ?? (shouldStartNewConversation ? false
                    : getConversationInvestigateMode(targetConversationId, baseHistory)),
        );
        // Discard expired investigate session on explicit retry (clarify session expiry)
        const resetInvestigateSession = Boolean(options.resetInvestigateSession);
        // Keep latest non-expired query options for clarify retry (no session_id reuse)
        lastSearchOptionsRef.current = {
            investigateEnabled,
            filters: Array.isArray(requestSearchOptions?.filters) ? requestSearchOptions.filters : undefined,
            rankingMode: typeof requestSearchOptions?.rankingMode === 'string'
                ? requestSearchOptions.rankingMode
                : undefined,
            maxArticles: Number.isFinite(Number(requestSearchOptions?.maxArticles))
                ? Number(requestSearchOptions.maxArticles)
                : undefined,
            // Kept so a clarify retry re-runs on the tier the original run used. Without it
            // `prior.serviceTier` below is always undefined and the restarted run silently drops
            // to the default tier — the same question answered by a different model.
            serviceTier: typeof requestSearchOptions?.serviceTier === 'string' && requestSearchOptions.serviceTier
                ? requestSearchOptions.serviceTier
                : undefined,
        };

        /* The images and PDFs this turn carries (service/attachments.js). AI Chat only: the
           Investigate pipeline does not read them, and no composer sends them there. An explicit
           list — a regenerate or an edited message re-sending the original's files — wins over
           the options bag the composer filled. */
        const turnAttachments = investigateEnabled
            ? []
            : normalizeAttachmentList(options.attachments ?? requestSearchOptions?.attachments);
        const turnAnswerOptions = answerOptionsForTurn(requestSearchOptions, baseHistory, investigateEnabled);

        // Create new user message
        const newMessage = {
            role: 'user',
            content: inputText,
            references: [],
            timestamp: t || timestamp,
            investigateMode: investigateEnabled,
            modeSource: 'submitted',
            ...(turnAttachments.length ? { attachments: turnAttachments } : {}),
        };

        /* Paint before waiting on anything.
        
           These used to run after the conversation had been created, which is a network round
           trip: the question sat unrendered and the panel showed no sign of having started for
           as long as that took. Nothing below needs them to have waited — the conversation id
           is not part of what they draw. */
        const optimisticRunHistory = [...baseHistory, newMessage];
        /* Whatever was running a moment ago, captured before the line below overwrites it.
           `runConversationId` then tracks the conversation THIS run owns — through the row
           being created and through the `saved` frame, which is the only thing that can move
           a run onto a different id. Both are closure-local on purpose: once a later run takes
           the foreground the shared refs describe that one, and this run still has to be able
           to say which conversation it was, so it can take its own mark out of the registry
           when it ends. */
        const previousRunWasLive = isLoadingRef.current;
        const previousRunConversationId = previousRunWasLive
            ? runningConversationIdRef.current
            : null;
        runningConversationIdRef.current = shouldStartNewConversation
            ? null
            : targetConversationId;
        let runConversationId = runningConversationIdRef.current;
        runningChatHistoryRef.current = optimisticRunHistory;
        setRunHistoryRevision((revision) => revision + 1);
        /* Only when this turn belongs to the thread on screen. A released queued prompt can
           belong to one the reader has since left, and painting it here is precisely how it
           ended up appearing under whatever conversation happened to be open. The messages
           still reach their own conversation, through `updateRunningChatHistory`, which writes
           against `runningConversationIdRef` rather than against the viewport. */
        const targetIsOnScreen = shouldStartNewConversation
            || String(targetConversationId) === String(activeConversationIdRef.current);
        if (targetIsOnScreen) setChatHistory(optimisticRunHistory);
        // These effects can run before the asynchronous conversation creation finishes.
        // Never publish the preceding run's pipeline as this request's snapshot/registry mode.
        runningInvestigateRef.current = investigateEnabled;
        if (!shouldStartNewConversation) recordSubmittedMode(targetConversationId, investigateEnabled);
        // Only when the text came from the box. Every caller that passes `input` is resending
        // something else — an edited message or a retry — and the box may now
        // hold a draft the reader is in the middle of writing. It could not before, because the
        // box was disabled for the length of a run; now that it is live, clearing it here would
        // delete their work.
        if (!input) setUserInput('');
        // The composer's chips went with this question; a resend never touches them.
        if (options.fromComposer) clearComposerAttachments();
        setIsLoading(true);
        setIsProcessing(true);

        let historyId = targetConversationId;
        if (shouldStartNewConversation && isAuthenticated) {
            try {
                const leadingTitle = inputText.trim().slice(0, 200) || null;
                const conversation = await createConversation(leadingTitle, investigateEnabled);
                const nextList = upsertConversation(getConversations(), conversation);
                setConversationsState(nextList);
                setConversations(nextList);
                historyId = conversation?.id || null;
                recordSubmittedMode(historyId, investigateEnabled);
                /* Only while this run still owns the view. The reader can hit New Chat (or
                   open another conversation) during this round trip; the release nulled these
                   refs — or a newer run owns them — and repointing them from this stale
                   continuation wrote a blank bubble over the new row's stored transcript and
                   re-selected that row under the reader's fresh screen. */
                if (activeStreamIdRef.current === streamId) {
                    runningConversationIdRef.current = historyId;
                    // Creating the server-side row is asynchronous. If the reader opened a
                    // historical conversation during that wait, keep that conversation
                    // selected; the newly-created row still owns the run, but no longer owns
                    // the viewport.
                    if (!activeConversationIdRef.current) {
                        setActiveConversationIdState(historyId);
                        activeConversationIdRef.current = historyId;
                        if (historyId) {
                            setActiveConversationId(historyId);
                        }
                    }
                }
            } catch (error) {
                logDev('[LLM] Failed to create conversation', error);
            }
        }
        // History tells an investigate conversation from a chat by its icon, and the
        // conversation list the API returns carries no such flag — so note it here,
        // where the app is the one deciding.
        if (investigateEnabled && historyId) {
            markInvestigateConversation(historyId);
        }
        /* The session id this RUN will use. For the attached run it is the shared ref —
           frame handlers keep updating that. A run released during the row's round trip must
           not touch the ref at all: New Chat just cleared it for the reader's next question,
           and writing this run's session back would hand that next question a session it was
           never part of. */
        let runSessionId;
        if (activeStreamIdRef.current !== streamId) {
            runSessionId = getStoredSessionId(historyId) || mintSessionId();
            if (historyId) setStoredSessionId(historyId, runSessionId);
        } else {
        if (resetInvestigateSession) {
            sessionIdRef.current = null;
            setStoredSessionId(historyId, null);
        } else {
            sessionIdRef.current = getStoredSessionId(historyId) || sessionIdRef.current;
        }
        // An investigate run must know its session id BEFORE the stream opens, because that id is
        // the only address a clarify round can be answered at. See mintSessionId.
        //
        // A chat run now mints one too. The id is also the address a RUN is recovered at
        // (`GET /run?session_id=`), and after a reload it is the only one left: the run id lives
        // in a ref that the reload destroys. The agent honours a client-supplied id and only
        // invents its own when we send none, so choosing it here changes nothing about the run
        // and gives the reload something to reconnect to.
        if (!sessionIdRef.current) {
            sessionIdRef.current = mintSessionId();
        }
        // Persist it NOW, not when the run finishes. It used to be written on the `Saved` frame,
        // which arrives at the END of the run — precisely never, for the case that needs it: a
        // reader who reloads while the answer is still being written.
        if (historyId) {
            setStoredSessionId(historyId, sessionIdRef.current);
        }
        runSessionId = sessionIdRef.current;
        }
        if (historyId) {
            runConversationId = String(historyId);
            if (activeStreamIdRef.current === streamId) {
                runningConversationIdRef.current = String(historyId);
            } else {
                /* Released while the row was being created. The run carries on detached: its
                   registry mark moves from the provisional stream key onto the row it now
                   owns (the sidebar dot, and the guard against a second turn on this history
                   id, both need the row's name), and its question reaches the row's stored
                   copy directly — the running refs belong to the view, which has let go. */
                setActiveRun({
                    kind: investigateEnabled ? 'investigate' : 'chat',
                    runId: null,
                    conversationId: String(historyId),
                    sessionId: runSessionId || null,
                    key: streamId,
                });
                writeConversationMessages(String(historyId), optimisticRunHistory);
            }
            /* A follow-up queued before this row existed is filed under the run's stream key.
               Several new conversations can be nameless at once, so only THIS run's entries
               may take the new history id — and a run that was never nameless claims none. */
            if (queueOwnerKey) {
                setQueuedPrompts((prev) => (
                    claimQueuedPrompts(prev, queueOwnerKey, String(historyId))
                ));
            }
        }

        /* View state, and only the view's run may touch it. A continuation released during
           the row round trip reaching these lines wiped the streaming panels of whatever run
           the reader had started SINCE. */
        if (activeStreamIdRef.current === streamId) {
        // The question and the spinner are already up — see above.
        setStreamingGroups([]);
        setPreambleText('');
        setStreamingStepName('');
        applyPendingClarification(null);
        setClarificationDrafts({});
        setClarificationError('');
        setClarificationSubmitting(false);
        // Start at `planning`, not `searching`: the panel is mounted here, at submit, and must
        // already be moving before the first SSE frame arrives (the agent's own T0 frame follows
        // within milliseconds, but the network round-trip should not be a visible dead spot).
        setInvestigatePhase('planning');
        setInvestigateFunnel(emptyFunnel());
        setInvestigateStartedAt(investigateEnabled ? requestStartedAt : null);
        setInvestigatePercent(investigateEnabled ? PHASE_PERCENT_FLOOR.planning : null);
        setInvestigateKeywords([]);
        setInvestigatePapers([]);
        setInvestigateDetail({});
        investigateFunnelRef.current = emptyFunnel();
        investigateDisplayFunnelRef.current = emptyFunnel();
        runningInvestigateRef.current = investigateEnabled;
        investigatePhaseRef.current = 'planning';
        investigatePercentRef.current = investigateEnabled ? PHASE_PERCENT_FLOOR.planning : null;
        investigateKeywordsRef.current = [];
        investigatePapersRef.current = [];
        investigateDetailRef.current = {};
        thinkingStepsRef.current = [];
        streamingAnswerRef.current = { block: -1, text: '' };
        dripRef.current.reset();
        setAnswerReady(false);
        preambleRef.current = { text: '', index: -1 };
        }
        // A NEW run re-arms this conversation's completion notice — the announcement set
        // de-duplicates per conversation, not per run.
        if (runConversationId) {
            backgroundCompletionNotifiedRef.current.delete(String(runConversationId));
        }

        /* What became of this run, as the `finally` needs to know it: a run the server is
           still writing keeps its registry mark, because taking it down would free the
           conversation for a second turn onto a history id that is still busy. */
        let streamOutcome = 'ok';
        /* Saved refreshes the server-backed ordering. Keep this run marked active until that
           refresh has landed: Complete can arrive before the request below resolves, and
           removing the mark first briefly drops the row into the stale server order before
           the refreshed list moves it back to the top. */
        let savedConversationRefresh = null;
        const detachedSnapshot = (patch = {}) => {
            if (!runConversationId) return false;
            const existing = readActiveRunSnapshotFor(runConversationId) || {};
            const storedMessages = getStoredChatHistory(runConversationId);
            return writeActiveRunSnapshot({
                ...existing,
                conversationId: String(runConversationId),
                messages: storedMessages.length
                    ? storedMessages
                    : (existing.messages?.length ? existing.messages : optimisticRunHistory),
                sessionId: patch.sessionId || existing.sessionId || runSessionId || null,
                investigate: investigateEnabled,
                ...patch,
            });
        };
        const persistDetachedCompletion = (update = {}) => {
            if (!runConversationId) return false;
            const existing = readActiveRunSnapshotFor(runConversationId) || {};
            const storedMessages = getStoredChatHistory(runConversationId);
            const base = storedMessages.length
                ? storedMessages
                : (existing.messages?.length ? existing.messages : optimisticRunHistory);
            const assistantMessage = {
                role: 'assistant',
                content: update.answer ?? update.response ?? '',
                references: parseReferences(update.references),
                directCitations: parseDirectCitations(
                    update.directCitations ?? update.direct_citations,
                ),
                kgQueryList: update.kgQueryList ?? update.kg_query_list ?? [],
                answerOptions: update.answerOptions ?? answerOptionsOf(update.answer_options),
                timestamp,
                thinkingSteps: existing.thinkingSteps || [],
                thoughtDurationMs: Date.now() - requestStartedAt,
                trajectory: update.trajectory || null,
                investigateMode: investigateEnabled,
                ...(investigateEnabled ? {
                    investigateFunnel: settleFunnel(
                        existing.investigateFunnel || emptyFunnel(),
                        existing.investigateDisplayFunnel || emptyFunnel(),
                    ),
                    investigatePhase: existing.investigatePhase || 'verifying',
                    investigatePercent: 100,
                    investigateKeywords: existing.investigateKeywords || [],
                    investigatePapers: existing.investigatePapers || [],
                } : {}),
            };
            const next = [...base];
            if (next[next.length - 1]?.role === 'assistant') {
                next[next.length - 1] = assistantMessage;
            } else {
                next.push(assistantMessage);
            }
            writeConversationMessages(runConversationId, next);
            clearActiveRunSnapshot(runConversationId);
            announceBackgroundCompletion(runConversationId);
            return true;
        };
        const persistDetachedFailure = (errorText) => {
            if (!runConversationId) return false;
            const storedMessages = getStoredChatHistory(runConversationId);
            const next = [...(storedMessages.length ? storedMessages : optimisticRunHistory)];
            const assistantMessage = {
                role: 'assistant',
                content: errorText,
                references: [],
                timestamp,
                thinkingSteps: [],
                thoughtDurationMs: Date.now() - requestStartedAt,
                investigateMode: investigateEnabled,
            };
            if (next[next.length - 1]?.role === 'assistant') {
                next[next.length - 1] = assistantMessage;
            } else {
                next.push(assistantMessage);
            }
            writeConversationMessages(runConversationId, next);
            clearActiveRunSnapshot(runConversationId);
            return true;
        };
        try {
            logDev('[LLM] submit', { input: inputText });

            const blankAssistant = {
                role: 'assistant',
                content: '',
                references: [],
                timestamp: timestamp,
                thinkingSteps: [],
                thoughtDurationMs: null,
                trajectory: null,
                investigateMode: investigateEnabled,
            };
            if (activeStreamIdRef.current === streamId) {
                // Append a blank message
                updateRunningChatHistory(prev => [...prev, blankAssistant]);
            } else if (runConversationId) {
                // Detached before the stream opened: the placeholder goes straight to the
                // row's stored copy, where the reattach that opens it will find the turn.
                writeConversationMessages(runConversationId, [...optimisticRunHistory, blankAssistant]);
            }

            /* Only a run in THIS conversation is cut off — it is being replaced by a retry or
               an edited message, so its answer is not wanted. A run in another conversation is
               left alone to finish: the reader moved on to a new question, they did not ask for
               the previous answer to be thrown away. Assigning `activeStreamIdRef` above has
               already detached it from the view, so it stops painting here; the server writes
               it to its own history and `resumeUnfinishedRun` collects it when the reader goes
               back. Aborting it instead would have destroyed a half-written answer every time
               someone started a second question. */
            /* The shared controller slot belongs to the run that owns the view — Stop aborts
               whatever sits in it. A run detached during the createConversation round trip
               reaching this line used to abort the reader's NEW live run and then install its
               own controller in the slot, so Stop killed the detached answer instead. A
               detached run keeps a private controller and touches nothing shared. */
            const abortController = new AbortController();
            if (activeStreamIdRef.current === streamId) {
                const replacesOwnRun = !previousRunWasLive
                    || (previousRunConversationId != null
                        && String(previousRunConversationId) === String(runConversationId));
                if (abortControllerRef.current && replacesOwnRun) {
                    abortControllerRef.current.abort();
                }
                abortControllerRef.current = abortController;
            }
            await llmService.chat(inputText, abortController, (update) => {
                trackQuerySubmitSuccess(update);
                const isActiveStream = activeStreamIdRef.current === streamId;
                if (!isActiveStream
                    && !['started', 'clarification', 'final', 'saved', 'error'].includes(update.type)) {
                    return;
                }
                logDev('[LLM] update', update);
                switch (update.type) {
                    case 'started':
                        if (!isActiveStream) {
                            if (runConversationId) {
                                setActiveRun({
                                    kind: investigateEnabled ? 'investigate' : 'chat',
                                    runId: update.runId || null,
                                    conversationId: String(runConversationId),
                                    sessionId: update.sessionId || runSessionId || null,
                                    key: streamId,
                                });
                                detachedSnapshot({
                                    runId: update.runId || null,
                                    sessionId: update.sessionId || runSessionId || null,
                                    investigatePhase: update.phase || 'planning',
                                });
                            }
                            return;
                        }
                        if (update.sessionId) {
                            sessionIdRef.current = update.sessionId;
                        }
                        if (update.runId) {
                            runIdRef.current = update.runId;
                        }
                        /* Stop was pressed while this submit was still in its pre-stream work
                           — the conversation round trip, the session mint, the quota check.
                           There was no socket to abort then, so Stop did nothing whatever and
                           the run went on to answer a question the reader had withdrawn:
                           measured against the stand-in agent, the composer was still
                           offering Stop twenty seconds later with the panel counting up.

                           Acted on HERE rather than before the request goes out, because this
                           is the first moment the run has a name, and cancelling it by that
                           name is what makes the rest fall into place: the backend stops its
                           relay and writes the stopped note into the conversation, so the
                           question and its answer survive a reload. Refusing to send the
                           request at all does stop the run sooner, but it leaves a
                           conversation row the server has never heard of — and the restore,
                           reading an empty history over a local one, then takes the reader's
                           question off the screen. The run is a second old here; there is
                           nothing of substance to save by being faster. */
                        if (stopRequestedStreamRef.current === streamId) {
                            stopRequestedStreamRef.current = null;
                            if (update.runId) {
                                llmService.cancelRun(update.runId, {
                                    investigate: investigateEnabled,
                                }).catch((error) => logDev('cancelRun (pre-stream stop) failed', error));
                            }
                            abortController.abort();
                            updateRunningChatHistory((prev) => withStoppedMessage(prev, {
                                investigate: investigateEnabled,
                            }));
                            return;
                        }
                        if (update.phase) {
                            investigatePhaseRef.current = update.phase;
                            setInvestigatePhase(update.phase);
                        }
                        if (update.funnel) {
                            investigateFunnelRef.current = mergeFunnel(investigateFunnelRef.current, update.funnel);
                            setInvestigateFunnel({ ...investigateFunnelRef.current });
                        }
                        {
                            const nextPct = mergePercentMonotonic(investigatePercentRef.current, update.percent);
                            investigatePercentRef.current = nextPct;
                            setInvestigatePercent(nextPct);
                            if (update.keywords) {
                                investigateKeywordsRef.current = mergeLiveKeywords(
                                    investigateKeywordsRef.current,
                                    update.keywords,
                                );
                                setInvestigateKeywords([...investigateKeywordsRef.current]);
                            }
                            if (update.papers) {
                                investigatePapersRef.current = mergeLivePapers(
                                    investigatePapersRef.current,
                                    update.papers,
                                );
                                setInvestigatePapers([...investigatePapersRef.current]);
                            }
                            if (update.label) {
                                setStreamingStepName(update.label);
                            }
                        }
                        break;
                    case 'clarification':
                        {
                            const round = {
                                invocationId: update.invocationId || null,
                                stage: update.stage || null,
                                sessionId: update.sessionId || runSessionId || null,
                                reason: update.reason || '',
                                questions: Array.isArray(update.questions) ? update.questions : [],
                            };
                            if (!isActiveStream) {
                                detachedSnapshot({
                                    pendingClarification: round,
                                    clarificationDrafts: buildClarificationDrafts(update.questions),
                                });
                                announceBackgroundClarification(runConversationId, round);
                                return;
                            }
                            if (!isRouteActiveRef.current) {
                                announceBackgroundClarification(runConversationId, round);
                            }
                        }
                        if (update.sessionId) {
                            sessionIdRef.current = update.sessionId;
                        }
                        applyPendingClarification({
                            invocationId: update.invocationId || null,
                            stage: update.stage || null,
                            sessionId: update.sessionId || sessionIdRef.current || null,
                            reason: update.reason || '',
                            questions: Array.isArray(update.questions) ? update.questions : [],
                        });
                        setClarificationDrafts(buildClarificationDrafts(update.questions));
                        setClarificationError('');
                        setClarificationSubmitting(false);
                        // Figma Asking Question: hold bar + keep phase; do not advance percent
                        if (update.funnel) {
                            investigateFunnelRef.current = mergeFunnel(investigateFunnelRef.current, update.funnel);
                            setInvestigateFunnel({ ...investigateFunnelRef.current });
                        }
                        setStreamingStepName('Asking user question...');
                        break;
                    case 'step':
                        if (!isActiveStream) return;
                        {
                            const rawContent = update.content ?? '';
                            const hasContent = Boolean(rawContent.trim());
                            if (update.step === 'Error') {
                                setIsProcessing(false);
                                setStreamingStepName('');
                                updateRunningChatHistory(prev => {
                                    const newHistory = [...prev];
                                    const assistantMessage = {
                                        role: 'assistant',
                                        content: update.content,
                                        references: [],
                                        timestamp: timestamp,
                                        thinkingSteps: thinkingStepsRef.current,
                                        thoughtDurationMs: Date.now() - requestStartedAt,
                                        investigateMode: investigateEnabled,
                                    };
                                    newHistory[newHistory.length - 1] = assistantMessage;

                                    // Update the LLMAgentService's internal message history
                                    llmService.updateMessages(update.answer);

                                    return newHistory;
                                });
                                /* The run history's real tail, guarded to the conversation on
                                   screen — the same shape as the 'final' handler. The old
                                   `chatHistory.length + 1` was a stale render-time closure:
                                   after a regenerate it pointed past the end of the shortened
                                   history, and a background run's error frame mis-set the
                                   selection of whatever conversation was open. */
                                if (
                                    String(activeConversationIdRef.current)
                                    === String(runningConversationIdRef.current)
                                ) {
                                    setSelectedMessageIndex(runningChatHistoryRef.current.length - 1);
                                }
                                break;
                            }

                            // Apply live progress even when content is empty (agent progress frames)
                            {
                                const nextPhase = mergePhaseMonotonic(
                                    investigatePhaseRef.current,
                                    update.phase || inferInvestigatePhase(update.step, rawContent),
                                );
                                if (nextPhase && nextPhase !== investigatePhaseRef.current) {
                                    investigatePhaseRef.current = nextPhase;
                                    setInvestigatePhase(nextPhase);
                                }
                                if (update.funnel) {
                                    investigateFunnelRef.current = mergeFunnel(
                                        investigateFunnelRef.current,
                                        update.funnel,
                                    );
                                    setInvestigateFunnel({ ...investigateFunnelRef.current });
                                }
                                const nextPct = mergePercentMonotonic(
                                    investigatePercentRef.current,
                                    update.percent ?? PHASE_PERCENT_FLOOR[nextPhase],
                                );
                                investigatePercentRef.current = nextPct;
                                setInvestigatePercent(nextPct);
                                if (update.keywords) {
                                    investigateKeywordsRef.current = mergeLiveKeywords(
                                        investigateKeywordsRef.current,
                                        update.keywords,
                                    );
                                    setInvestigateKeywords([...investigateKeywordsRef.current]);
                                }
                                if (update.papers) {
                                    investigatePapersRef.current = mergeLivePapers(
                                        investigatePapersRef.current,
                                        update.papers,
                                    );
                                    setInvestigatePapers([...investigatePapersRef.current]);
                                }
                                if (update.label || update.isProgress) {
                                    setStreamingStepName(update.label || update.content || update.step);
                                }
                                if (update.detail && typeof update.detail === 'object') {
                                    investigateDetailRef.current = mergeInvestigateDetail(
                                        investigateDetailRef.current,
                                        update.detail,
                                        update.label,
                                    );
                                    setInvestigateDetail({ ...investigateDetailRef.current });
                                }
                                // Capture progress labels as rich phase markers
                                if (update.isProgress && update.label && update.phase) {
                                    thinkingStepsRef.current = [...thinkingStepsRef.current, {
                                        step: update.step || update.phase,
                                        content: update.label,
                                        isProgress: true,
                                        phase: update.phase,
                                    }];
                                }
                            }

                            if (hasContent && !update.isProgress) {
                                const newEntry = { step: update.step, content: rawContent };
                                thinkingStepsRef.current = [...thinkingStepsRef.current, newEntry];
                                const parsedEntry = parseThinkingEntry(newEntry);
                                if (parsedEntry.stepName) {
                                    setStreamingStepName(parsedEntry.stepName);
                                }

                                const nextPhase = mergePhaseMonotonic(
                                    investigatePhaseRef.current,
                                    update.phase || inferInvestigatePhase(update.step, rawContent),
                                );
                                if (nextPhase && nextPhase !== investigatePhaseRef.current) {
                                    investigatePhaseRef.current = nextPhase;
                                    setInvestigatePhase(nextPhase);
                                }
                                // Structured funnel fields only — see mergeFunnel. Raw tool-log lines
                                // used to be regex-scraped for numbers here, which wrote figures
                                // pulled out of unrelated tool output straight into the counters.
                                if (update.funnel) {
                                    investigateFunnelRef.current = mergeFunnel(
                                        investigateFunnelRef.current,
                                        update.funnel,
                                    );
                                    setInvestigateFunnel({ ...investigateFunnelRef.current });
                                }
                                {
                                    const nextPct = mergePercentMonotonic(
                                        investigatePercentRef.current,
                                        update.percent ?? PHASE_PERCENT_FLOOR[nextPhase],
                                    );
                                    investigatePercentRef.current = nextPct;
                                    setInvestigatePercent(nextPct);
                                    if (update.keywords) {
                                        investigateKeywordsRef.current = mergeLiveKeywords(
                                            investigateKeywordsRef.current,
                                            update.keywords,
                                        );
                                        setInvestigateKeywords([...investigateKeywordsRef.current]);
                                    }
                                    if (update.papers) {
                                        investigatePapersRef.current = mergeLivePapers(
                                            investigatePapersRef.current,
                                            update.papers,
                                        );
                                        setInvestigatePapers([...investigatePapersRef.current]);
                                    }
                                    if (update.label || update.isProgress) {
                                        setStreamingStepName(update.label || update.content || update.step);
                                    }
                                }

                                // Groups are keyed on the mapped step wording, so a step still
                                // shows up while streaming even though its raw trace is withheld.
                                setStreamingGroups((prev) => {
                                    if (!parsedEntry.stepName) {
                                        return prev;
                                    }

                                    const lastGroup = prev[prev.length - 1];
                                    if (!lastGroup || lastGroup.name !== parsedEntry.stepName) {
                                        return [
                                            ...prev,
                                            {
                                                name: parsedEntry.stepName,
                                                lines: parsedEntry.line ? [parsedEntry.line] : []
                                            }
                                        ];
                                    }

                                    if (!parsedEntry.line) {
                                        return prev;
                                    }

                                    const updatedLast = {
                                        ...lastGroup,
                                        lines: [...lastGroup.lines, parsedEntry.line]
                                    };
                                    return [...prev.slice(0, -1), updatedLast];
                                });
                            }
                        }
                        break;
                    case 'thinking': {
                        if (!isActiveStream) return;
                        const pre = preambleRef.current;
                        pre.text += update.delta;
                        const entry = { step: 'Thinking', content: pre.text, isThought: true };
                        if (pre.index < 0) {
                            pre.index = thinkingStepsRef.current.length;
                            thinkingStepsRef.current = [...thinkingStepsRef.current, entry];
                        } else {
                            const next = [...thinkingStepsRef.current];
                            next[pre.index] = entry;
                            thinkingStepsRef.current = next;
                        }
                        setPreambleText(pre.text);
                        // `thinkingStepsRef` is what the FINISHED message carries; the live view
                        // renders `streamingGroups`, which until now was only ever written from
                        // `case 'step'`. Without this the opening line was invisible for the whole
                        // run and only surfaced, collapsed, once the answer had already landed —
                        // i.e. exactly when it is no longer worth reading.
                        //
                        // Matched by name rather than by "the last group": the line keeps growing
                        // while the run's own progress steps are being appended after it.
                        setStreamingGroups((prev) => {
                            const group = { name: 'Thinking', lines: [pre.text] };
                            const at = prev.findIndex((g) => g.name === 'Thinking');
                            if (at < 0) return [...prev, group];
                            const next = [...prev];
                            next[at] = group;
                            return next;
                        });
                        break;
                    }
                    case 'delta': {
                        if (!isActiveStream) return;
                        const buf = streamingAnswerRef.current;
                        if (update.block > buf.block) {
                            // A new block: whatever streamed before it was the model talking its
                            // way to a tool call, not the answer. It is worth showing — it is the
                            // only glimpse of the model's own reasoning the transport carries, and
                            // it arrives long before any answer text does — so it goes into the
                            // body as it streams and moves into the thought list here, once the
                            // block it belonged to has ended.
                            const narration = buf.text.trim();
                            if (narration) {
                                thinkingStepsRef.current = [...thinkingStepsRef.current, {
                                    step: 'Thinking',
                                    content: narration,
                                    isThought: true,
                                }];
                            }
                            streamingAnswerRef.current = { block: update.block, text: update.delta };
                            dripRef.current.reset();
                        } else if (update.block === buf.block) {
                            streamingAnswerRef.current = { block: buf.block, text: buf.text + update.delta };
                        } else {
                            return;   // a straggler from a block already superseded
                        }
                        dripRef.current.start();
                        break;
                    }
                    case 'answer': {
                        if (!isActiveStream) return;
                        // The authoritative text. It replaces the streamed accumulation rather
                        // than being appended to it: `Complete` will carry this same string, and
                        // the citation markers are rewritten server-side after the last chunk, so
                        // the streamed copy is a preview and this is the real thing.
                        if (update.sessionId) {
                            sessionIdRef.current = update.sessionId;
                        }
                        streamingAnswerRef.current = { block: Number.MAX_SAFE_INTEGER, text: update.answer || '' };
                        // No drip here: this is the finished text, and the reader has already
                        // watched most of it arrive. Holding the tail back now would be delay
                        // for its own sake.
                        dripRef.current.flush(update.answer || '');
                        setAnswerReady(true);
                        // The run is NOT over: references, citations and the graph query list are
                        // still on their way. Keep the spinner, but say what it is waiting for —
                        // the answer is already on screen and readable.
                        setStreamingStepName('Finalizing references...');
                        break;
                    }
                    case 'final':
                        if (!isActiveStream) {
                            persistDetachedCompletion(update);
                            return;
                        }
                        // The whole message is replaced below with the authoritative final data.
                        dripRef.current.stop();
                        if (update.sessionId) {
                            sessionIdRef.current = update.sessionId;
                        }
                        applyPendingClarification(null);
                        setClarificationDrafts({});
                        setClarificationError('');
                        setClarificationSubmitting(false);
                        setIsProcessing(false);
                        setStreamingStepName('');
                        if (update.funnel) {
                            investigateFunnelRef.current = mergeFunnel(
                                investigateFunnelRef.current,
                                update.funnel,
                            );
                        }
                        updateRunningChatHistory(prev => {
                            const newHistory = [...prev];
                            const assistantMessage = {
                                role: 'assistant',
                                content: update.answer,
                                references: parseReferences(update.references),
                                directCitations: parseDirectCitations(update.directCitations),
                                kgQueryList: update.kgQueryList || [],
                                answerOptions: update.answerOptions || {},
                                timestamp: timestamp,
                                thinkingSteps: thinkingStepsRef.current,
                                thoughtDurationMs: Date.now() - requestStartedAt,
                                trajectory: update.trajectory || null,
                                investigateMode: investigateEnabled,
                                investigateFunnel: settledFunnel(),
                                investigatePhase: investigatePhaseRef.current || 'verifying',
                                investigatePercent: mergePercentMonotonic(
                                    investigatePercentRef.current,
                                    update.percent ?? 100,
                                ) ?? 100,
                                investigateKeywords: [...(investigateKeywordsRef.current || [])],
                                investigatePapers: [...(investigatePapersRef.current || [])],
                            };
                            newHistory[newHistory.length - 1] = assistantMessage;

                            // Update the LLMAgentService's internal message history
                            llmService.updateMessages(update.answer);

                            return newHistory;
                        });
                        if (
                            String(activeConversationIdRef.current)
                            === String(runningConversationIdRef.current)
                        ) {
                            setSelectedMessageIndex(runningChatHistoryRef.current.length - 1);
                        }
                        announceBackgroundCompletion();
                        break;
                    case 'saved': {
                        // The balance after this answer's charge; a missing figure is refetched
                        // by `refreshTierStatus` in the finally below.
                        applyStreamCredits(update.credits);
                        /* The parts of 'saved' that belong to the RUN, not the view — the id
                           it now answers to, the follow-ups queued against its nameless days,
                           the session id a reattach will need — apply to a detached run too.
                           This is the only place the id arrives at all when createConversation
                           failed; keeping it behind `isActiveStream` left the run's answer
                           unreachable from the row the server created. */
                        const savedId = update.historyId ? String(update.historyId) : null;
                        if (savedId) {
                            runConversationId = savedId;
                            recordSubmittedMode(savedId, investigateEnabled);
                            if (queueOwnerKey) {
                                setQueuedPrompts((prev) => (
                                    claimQueuedPrompts(prev, queueOwnerKey, savedId)
                                ));
                            }
                            const nextSessionId = update.sessionId || runSessionId;
                            if (nextSessionId) {
                                setStoredSessionId(savedId, nextSessionId);
                            }
                            if (!isActiveStream) {
                                // The detached run's registry mark moves onto its row, so the
                                // sidebar dot and the double-submit guard know its real name.
                                setActiveRun({
                                    kind: investigateEnabled ? 'investigate' : 'chat',
                                    runId: null,
                                    conversationId: savedId,
                                    sessionId: update.sessionId || runSessionId || null,
                                    key: streamId,
                                });
                            }
                        }
                        if (isActiveStream) {
                            if (savedId) {
                                const previousRunningId = runningConversationIdRef.current;
                                const wasViewingRun = previousRunningId
                                    ? String(activeConversationIdRef.current) === String(previousRunningId)
                                    : !activeConversationIdRef.current;
                                runningConversationIdRef.current = savedId;
                                if (wasViewingRun) {
                                    setActiveConversationId(savedId);
                                    setActiveConversationIdState(savedId);
                                    activeConversationIdRef.current = savedId;
                                }
                                if (update.sessionId) {
                                    sessionIdRef.current = update.sessionId;
                                }
                            }
                            if (update.invocationId) {
                                updateRunningChatHistory((prev) => {
                                    const next = [...prev];
                                    let assistantIndex = -1;
                                    for (let i = next.length - 1; i >= 0; i -= 1) {
                                        if (next[i]?.role === 'assistant') {
                                            assistantIndex = i;
                                            break;
                                        }
                                    }
                                    if (assistantIndex < 0) return prev;
                                    const userIndex = assistantIndex - 1;
                                    if (userIndex < 0) return prev;
                                    next[userIndex] = { ...next[userIndex], invocationId: update.invocationId };
                                    next[assistantIndex] = { ...next[assistantIndex], invocationId: update.invocationId };
                                    return next;
                                });
                            }
                        }
                        if (isAuthenticated) {
                            savedConversationRefresh = fetchConversations()
                                .then((list) => setConversationsState(list))
                                .catch((error) => logDev('[LLM] Failed to refresh conversations', error));
                        }
                        break;
                    }
                    case 'error': // unsure if this is used
                        applyStreamCredits(update.credits);
                        /* Refused before the stream opened (utils/refusals.js): nothing ran and
                           nothing was charged. The bubble says why, in the backend's words rather
                           than as an "Error:", and the catch below does not go looking for a run
                           that was never started. */
                        if (update.refusal) {
                            streamOutcome = 'refused';
                            handleRefusal(update.refusal);
                            if (!isActiveStream) {
                                persistDetachedFailure(refusalText(update.refusal));
                                return;
                            }
                            applyPendingClarification(null);
                            setIsProcessing(false);
                            setStreamingStepName('');
                            updateRunningChatHistory((prev) => {
                                const newHistory = [...prev];
                                newHistory[newHistory.length - 1] = {
                                    role: 'assistant',
                                    content: refusalText(update.refusal),
                                    references: [],
                                    timestamp,
                                    thinkingSteps: [],
                                    investigateMode: investigateEnabled,
                                };
                                return newHistory;
                            });
                            break;
                        }
                        if (!isActiveStream) {
                            streamOutcome = 'lost';
                            persistDetachedFailure(`Error: ${update.error}`);
                            return;
                        }
                        streamOutcome = 'lost';
                        applyPendingClarification(null);
                        setClarificationDrafts({});
                        setClarificationError('');
                        setClarificationSubmitting(false);
                        setIsProcessing(false);
                        setStreamingStepName('');
                        updateRunningChatHistory(prev => {
                            const newHistory = [...prev];
                            const last = newHistory[newHistory.length - 1];
                            /* An error that arrives AFTER an answer does not get to delete
                               it. A late failure — the history write failing once the report
                               is already on screen, a connection dropping during teardown —
                               says something about storage, not about the answer, and this
                               assignment is destructive: the answer exists nowhere else the
                               reader can reach, and `updateRunningChatHistory` persists the
                               overwrite to the conversation store on its way past. Report
                               the failure alongside the answer instead of on top of it. */
                            if (last?.role === 'assistant' && last.content) {
                                newHistory[newHistory.length - 1] = {
                                    ...last,
                                    streamError: update.error,
                                };
                                return newHistory;
                            }
                            const errorMessage = {
                                role: 'assistant',
                                content: `Error: ${update.error}`,
                                references: [],
                                timestamp: timestamp,
                                thinkingSteps: thinkingStepsRef.current,
                                thoughtDurationMs: Date.now() - requestStartedAt,
                                investigateMode: investigateEnabled,
                            };
                            newHistory[newHistory.length - 1] = errorMessage;
                            return newHistory;
                        });
                        break;
                    default:
                        break;
                }
            }, {
                historyId,
                sessionId: runSessionId,
                filters: Array.isArray(requestSearchOptions?.filters) ? requestSearchOptions.filters : undefined,
                rankingMode: typeof requestSearchOptions?.rankingMode === 'string' ? requestSearchOptions.rankingMode : undefined,
                investigateEnabled,
                // The turn's OWN tier, off the options bag rather than off `serviceTier`: a
                // queued follow-up must send the tier that was showing when the reader hit
                // send, not whatever the picker moved to while it waited in the queue. A guest
                // is always Standard — the backend refuses anything else.
                serviceTier: effectiveTier(requestSearchOptions?.serviceTier, { isGuest }) || undefined,
                attachments: turnAttachments.length ? attachmentIdsOf(turnAttachments) : undefined,
                figures: turnAnswerOptions.figures,
                graph: turnAnswerOptions.graph,
                notifyEmail: (investigateEnabled && notifyEmailEnabled)
                    ? (getUserNotifyEmail() || undefined)
                    : undefined,
                messagesOverride: [...baseHistory, newMessage].map((msg) => ({
                    role: msg?.role,
                    content: msg?.content,
                })),
            });
        } catch (error) {
            console.error('Error in chat:', error);
            // Refused before the stream opened: there is no run to recover (see the 'error' case).
            const refusal = error?.refusal || null;
            // Deep Research disconnect recovery: poll GET /run/{run_id}
            if (!refusal && investigateEnabled && runIdRef.current && activeStreamIdRef.current === streamId) {
                try {
                    const run = await llmService.getRun({ runId: runIdRef.current });
                    if (run && (run.status === 'complete' || run.response)) {
                        announceInvestigateComplete();
                        applyPendingClarification(null);
                        setIsProcessing(false);
                        setStreamingStepName('');
                        updateRunningChatHistory((prev) => {
                            const newHistory = [...prev];
                            newHistory[newHistory.length - 1] = {
                                role: 'assistant',
                                content: run.response || '',
                                references: parseReferences(run.references),
                                timestamp,
                                thinkingSteps: thinkingStepsRef.current,
                                thoughtDurationMs: Date.now() - requestStartedAt,
                                trajectory: run.trajectory || null,
                                investigateMode: true,
                                investigateFunnel: settledFunnel(),
                                investigatePhase: 'verifying',
                                investigatePercent: investigatePercentRef.current ?? 100,
                                investigateKeywords: [...(investigateKeywordsRef.current || [])],
                                investigatePapers: [...(investigatePapersRef.current || [])],
                            };
                            if (run.response) {
                                llmService.updateMessages(run.response);
                            }
                            return newHistory;
                        });
                        announceBackgroundCompletion();
                        refreshTierStatus();
                        if (activeStreamIdRef.current === streamId) {
                            setIsLoading(false);
                            setIsProcessing(false);
                            activeStreamIdRef.current = null;
                        }
                        return;
                    }
                } catch (recoverError) {
                    logDev('[LLM] run recovery failed', recoverError);
                }
            }
            if (error?.response?.status === 429) {
                setIsQueryLimitReached(true);
            }
            /* THE STREAM DYING IS NOT THE ANSWER DYING.
            
               A dropped SSE — wifi blinking, a laptop waking, a proxy timing the connection
               out, a bfcache restore aborting the frozen fetch — reached this block and wrote
               "Sorry, I encountered an error" straight over an answer the server was still
               writing. Worse, it persisted that into the conversation's stored copy and the
               `finally` then deleted the durable record and the snapshot with it, so the
               reader was left with an error, no way back to the run, and every reason to ask
               the same question again. That is the reader's "usually displays fine once the
               model has finished, but not always".
            
               A chat run has an address now — the same one a reload uses — so ask the server
               what actually happened. The recovery this used to run only for investigate
               turns, which were the only ones with a run id. Only a run the server says is
               GONE gets an error written; a run it still calls running is left alone, its
               exchange visibly unfinished, for the reattach path to collect. */
            const pollSessionId = runSessionId || sessionIdRef.current;
            let outcome = refusal ? 'refused' : 'unknown';
            const canRecoverDetached = () => Boolean(
                runConversationId && isConversationRunning(runConversationId),
            );
            if (!refusal && pollSessionId
                && (activeStreamIdRef.current === streamId || canRecoverDetached())) {
                for (let attempt = 0; attempt < 100; attempt += 1) {
                    if (activeStreamIdRef.current !== streamId && !canRecoverDetached()) {
                        outcome = 'released';
                        break;
                    }
                    let run = null;
                    let missing = false;
                    try {
                        // eslint-disable-next-line no-await-in-loop
                        run = await llmService.getRun({ sessionId: pollSessionId });
                    } catch (pollError) {
                        missing = pollError?.response?.status === 404;
                    }
                    if (activeStreamIdRef.current !== streamId && !canRecoverDetached()) {
                        outcome = 'released';
                        break;
                    }
                    // The stream is gone but the run is not: keep the panel moving with what
                    // the server says, instead of freezing it on the last frame that arrived.
                    applyRunProgress(run, investigateEnabled);
                    if (run?.status === 'cancelled') {
                        // Stopped on purpose — by this reader or another tab. Not an error:
                        // saying "something went wrong" about a deliberate Stop is how the
                        // reader ends up asking the same expensive question again.
                        outcome = 'stopped';
                        break;
                    }
                    if (run && (run.status === 'complete' || run.response)) {
                        if (activeStreamIdRef.current !== streamId) {
                            persistDetachedCompletion(run);
                            outcome = 'recovered';
                            break;
                        }
                        applyPendingClarification(null);
                        setIsProcessing(false);
                        setStreamingStepName('');
                        setShowReloadPrompt(false);
                        updateRunningChatHistory((prev) => {
                            const newHistory = [...prev];
                            newHistory[newHistory.length - 1] = {
                                role: 'assistant',
                                content: run.response || '',
                                references: parseReferences(run.references),
                                timestamp,
                                thinkingSteps: thinkingStepsRef.current,
                                thoughtDurationMs: Date.now() - requestStartedAt,
                                trajectory: run.trajectory || null,
                                investigateMode: investigateEnabled,
                            };
                            if (run.response) llmService.updateMessages(run.response);
                            return newHistory;
                        });
                        announceBackgroundCompletion();
                        outcome = 'recovered';
                        break;
                    }
                    if (run?.status === 'error' || missing) { outcome = 'lost'; break; }
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((resolve) => setTimeout(resolve, RESUME_POLL_MS));
                }
            } else if (!refusal && !pollSessionId) {
                outcome = 'lost';        // nothing to ask; the old behaviour is all there is
            }
            /* An exchange left visibly unfinished is the RIGHT outcome for a run still being
               written: its mark stays up, its stored turn stays as [question, empty answer],
               and reopening the conversation reattaches. Say so, so the reader is not staring
               at a still page wondering whether anything is happening. */
            if (outcome === 'unknown' && activeStreamIdRef.current === streamId) {
                setStreamingStepName('Still answering — this will appear when it finishes');
            }
            if (outcome === 'stopped' && activeStreamIdRef.current === streamId) {
                /* Same sentence, same rule as the live Stop: only where the bubble is empty,
                   because partial work is still worth reading. */
                updateRunningChatHistory((prev) => withStoppedMessage(prev, {
                    investigate: investigateEnabled,
                    patch: {
                        thinkingSteps: thinkingStepsRef.current,
                        thoughtDurationMs: Date.now() - requestStartedAt,
                    },
                }));
            }
            if (outcome === 'lost' && activeStreamIdRef.current === streamId) {
                updateRunningChatHistory(prev => {
                    const newHistory = [...prev];
                    const errorMessage = {
                        role: 'assistant',
                        content: 'Sorry, I encountered an error while processing your request. Please try again.',
                        references: [],
                        timestamp: timestamp,
                        thinkingSteps: thinkingStepsRef.current,
                        thoughtDurationMs: Date.now() - requestStartedAt,
                        investigateMode: investigateEnabled,
                    };
                    newHistory[newHistory.length - 1] = errorMessage;
                    return newHistory;
                });
            } else if (outcome === 'lost') {
                persistDetachedFailure(
                    'Sorry, I encountered an error while processing your request. Please try again.',
                );
            }
            streamOutcome = outcome;
        } finally {
            // A guest's ten are counted per browser (service/credits.js), once the answer is in.
            if (isGuest && !investigateEnabled && (streamOutcome === 'ok' || streamOutcome === 'recovered')) {
                recordGuestQuestion();
            }
            refreshTierStatus();
            /* Unconditional, and by BOTH of this run's possible names: a detached run reaches
               here with the foreground belonging to a later one, and it still has to take its
               own mark out of the registry — otherwise the sidebar would show it working
               forever. The provisional 'run:<streamId>' slot is cleared as well as the row id,
               because a run released DURING the row's creation is never re-keyed — the
               registry effect stopped following it — and clearing only the row id left that
               provisional entry claiming a run for the life of the tab: the tab-close warning
               fired forever and the home composer never unlocked. */
            /* The throttled store write may still be holding this run's finished answer;
               hand it over before the marks come down — the tab-close warning disarms with
               them, and a close inside the throttle window would persist a blank bubble
               over an answer that had already arrived. */
            flushConversationStoreWrite();
            // Keep Recent stable without blocking another turn on a completed stream.
            holdRecentPriority(runConversationId, savedConversationRefresh);
            /* 'unknown' means the stream dropped and the server still called the run live.
               Its mark stays: the answer is coming, the sidebar should say so, and a second
               turn must not start on that history id meanwhile. Everything else — finished,
               recovered, or genuinely lost — is over, and its mark comes down. */
            if (streamOutcome !== 'unknown') {
                if (runConversationId) clearActiveRun(runConversationId);
                clearPendingRun(streamId);
            }
            /* A released run reaches here with the foreground belonging to a later one, so its
               frames — `complete` included — were dropped and nothing has told the reader their
               answer landed. That notification is most of what makes leaving a run to finish a
               usable thing to do. */
            if (activeStreamIdRef.current !== streamId
                && runConversationId
                && (streamOutcome === 'ok' || streamOutcome === 'recovered')) {
                announceBackgroundCompletion(runConversationId);
            }
            if (activeStreamIdRef.current === streamId) {
                setIsLoading(false);
                setIsProcessing(false);
                activeStreamIdRef.current = null;
                applyPendingClarification(null);
                setClarificationDrafts({});
                setClarificationError('');
                setClarificationSubmitting(false);
            }
        }
        // The turn was started; a queued follow-up that reaches here must not be re-queued.
        return true;
    };

    const updateClarificationDraft = useCallback((questionKey, nextDraft) => {
        setClarificationDrafts((prev) => ({
            ...prev,
            [questionKey]: {
                selected: Array.isArray(nextDraft?.selected) ? nextDraft.selected : [],
                text: typeof nextDraft?.text === 'string' ? nextDraft.text : '',
                otherSelected: Boolean(nextDraft?.otherSelected),
            },
        }));
    }, []);

    const hasInvalidOtherSelection = useMemo(() => {
        const questions = pendingClarification?.questions;
        if (!Array.isArray(questions)) return false;
        return questions.some((question, index) => {
            const responseType = String(question?.response_type || 'text').toLowerCase();
            if (responseType !== 'single') return false;
            const questionKey = getClarificationQuestionKey(question, index);
            const draft = clarificationDrafts[questionKey];
            if (!draft?.otherSelected) return false;
            return !String(draft.text || '').trim();
        });
    }, [pendingClarification, clarificationDrafts]);

    const submitClarification = useCallback(async ({ useDefaults = false } = {}) => {
        // Read the round from the ref FIRST. This handler is reached from a click deep inside a
        // memoised MessageCard; the ref is written by the SSE handler the instant the frame lands
        // and cannot be a render behind. State is kept as the fallback so the two can only
        // disagree in the direction of "the ref is fresher".
        const { questions, sessionId, invocationId, stage } = resolveClarifyRound(
            pendingClarificationRef.current, pendingClarification, sessionIdRef.current,
        );
        // Last-resort guard. handleSubmit mints the session id before the stream opens and the
        // agent puts invocation_id + stage on every clarification frame, so all three are present
        // by construction. The old copy here said the session had "expired", which was a guess —
        // nothing had timed out, we simply had no address to answer at — and it sent the user off
        // to re-ask, which bills a second full run. Issue #12 asked for exactly this: "Missing
        // session_id / invocation_id → fix client state, do not invent 'expired'."
        if (!invocationId || !stage || !sessionId) {
            logDev('[LLM] clarify: incomplete round', { invocationId, stage, sessionId });
            // Deliberately does NOT promise the run will carry on: clarify.timeout_s is null, so a
            // round nobody can answer leaves the run paused rather than proceeding on defaults.
            setClarificationError(
                'Could not identify this clarification round, so your answers cannot be sent and '
                + 'this investigation cannot continue. Please ask your question again.',
            );
            return;
        }

        setClarificationSubmitting(true);
        setClarificationError('');
        try {
            const answers = useDefaults
                ? []
                : buildClarifyAnswers(questions, clarificationDrafts);

            const result = await llmService.clarify({
                invocation_id: invocationId,
                stage,
                session_id: sessionId,
                answers,
            });

            applyPendingClarification(null);
            setClarificationDrafts({});
            setClarificationError('');
            setClarificationSubmitting(false);

            if (result?.resolved === false) {
                const retryQuestion = typeof result?.retry_question === 'string'
                    ? result.retry_question.trim()
                    : '';
                if (retryQuestion) {
                    message.info('Clarification session expired. Restarting with your answers applied.');
                    const prior = lastSearchOptionsRef.current || {};
                    handleSubmit(null, retryQuestion, null, {
                        resetInvestigateSession: true,
                        searchOptions: {
                            investigateEnabled: true,
                            filters: prior.filters,
                            rankingMode: prior.rankingMode,
                            maxArticles: prior.maxArticles,
                            // A restarted clarify run is the SAME question; re-running it on a
                            // different tier would silently change the answer's provenance.
                            serviceTier: prior.serviceTier,
                        },
                    });
                } else {
                    message.info('Clarification session not found. Continuing with default research scope.');
                }
            }
        } catch (error) {
            const detail = error?.response?.data?.detail || error?.message || 'Unable to submit clarification.';
            setClarificationError(String(detail));
            setClarificationSubmitting(false);
        }
    }, [clarificationDrafts, llmService, pendingClarification]);

    // Resume completed DR runs if the tab was backgrounded / SSE dropped quietly
    useEffect(() => {
        if (!isLoading) return undefined;

        const tryRecover = async () => {
            if (!runIdRef.current || !runningInvestigateRef.current) return;
            /* The reattach owns its conversation's polling, by session id, and writes the
               result itself. Two pollers on one transcript is how a recovered answer got
               written twice, and how one of them wrote the WRONG run's. */
            if (resumingConversationRef.current) return;
            /* Whose transcript this poll may write into. `runIdRef` is a single ref and this
               effect fires whenever `isLoading` is true for any reason — including a reattach
               in a different conversation — so without this, conversation A's deep-research
               report was written into conversation B's empty bubble, persisted there, and
               announced as B's. */
            const forConversationId = runningConversationIdRef.current;
            try {
                const run = await llmService.getRun({ runId: runIdRef.current });
                /* This poll runs whenever the view is loading without owning a reattach —
                   a backgrounded tab, an SSE that died quietly — so it is also the only
                   thing watching the run in those states. Keep the panel current from it. */
                applyRunProgress(run, Boolean(runningInvestigateRef.current));
                if (!(run && (run.status === 'complete' || run.response))) return;
                if (String(runningConversationIdRef.current ?? '')
                    !== String(forConversationId ?? '')) return;
                announceInvestigateComplete();
                applyPendingClarification(null);
                setIsProcessing(false);
                setStreamingStepName('');
                setIsLoading(false);
                // The answer is here; nothing is interrupted. Left armed, the banner renders
                // INSTEAD of the message body and hides the very answer just recovered.
                setShowReloadPrompt(false);
                updateRunningChatHistory((prev) => {
                    if (!prev.length) return prev;
                    const newHistory = [...prev];
                    const last = newHistory[newHistory.length - 1];
                    if (!last || last.role !== 'assistant' || last.content) return prev;
                    newHistory[newHistory.length - 1] = {
                        ...last,
                        content: run.response || '',
                        references: parseReferences(run.references),
                        thinkingSteps: thinkingStepsRef.current,
                        thoughtDurationMs: last.thoughtDurationMs || null,
                        trajectory: run.trajectory || null,
                        investigateMode: true,
                        investigateFunnel: settledFunnel(),
                        investigatePhase: 'verifying',
                        investigatePercent: investigatePercentRef.current ?? 100,
                        investigateKeywords: [...(investigateKeywordsRef.current || [])],
                        investigatePapers: [...(investigatePapersRef.current || [])],
                    };
                    if (run.response) llmService.updateMessages(run.response);
                    return newHistory;
                });
                announceBackgroundCompletion();
                activeStreamIdRef.current = null;
            } catch (error) {
                logDev('[LLM] visibility run recovery failed', error);
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                tryRecover();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        const intervalId = window.setInterval(tryRecover, 45000);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.clearInterval(intervalId);
        };
    }, [
        announceBackgroundCompletion,
        applyRunProgress,
        isLoading,
        llmService,
        settledFunnel,
        updateRunningChatHistory,
    ]);

    // Deliberately no abort when the app shell itself unmounts. Normal route changes now keep
    // this controller mounted, while a full shell teardown can still be recovered from the
    // persisted history id (and, for investigate, the run id).
    useEffect(() => () => {}, []);

    const handleSaveEdit = async (e, index, content) => {
        if (content.trim() === '' || isLoading) return;
        const historyId = activeConversationIdRef.current;
        if (!historyId) return;
        const invocationId = await ensureInvocationIdForIndex(index);
        if (!invocationId) {
            message.error('Unable to edit this message right now.');
            return;
        }

        try {
            await llmService.rewind(historyId, invocationId);
        } catch (error) {
            message.error('Unable to rewind conversation. Please try again.');
            return;
        }
        const editedHistory = chatHistory.slice(0, index);
        setChatHistory(editedHistory);
        setShowReloadPrompt(false);
        // The edited question keeps the files the original was sent with.
        handleSubmit(e, content, null, {
            baseHistory: editedHistory,
            attachments: chatHistory[index]?.attachments,
        });
    };

    const handleCopyMessage = (content) => {
        if (!window.navigator?.clipboard?.writeText) {
            message.error('Copy is not supported in this browser context.');
            return;
        }

        window.navigator.clipboard.writeText(content)
            .then(() => {
                message.success('Content copied to clipboard');
            })
            .catch(err => {
                console.error('Failed to copy content: ', err);
                message.error('Copy failed, please select and copy manually');
            });
    };

    /* New Chat, mid-run included.

       It used to navigate away and return without resetting anything, so the reader landed on
       a page that still believed the old conversation was selected and still had `isLoading`
       set — which left the composer disabled with "Another conversation is still loading" and
       no way out but waiting. New Chat now always reaches an empty chat.

       `keepRunning` is what makes that safe: the run is released rather than aborted, so the
       answer the reader walked away from is still written, saved against its own history id,
       and shown by `resumeUnfinishedRun` when they come back to it. */
    const handleClear = useCallback(() => {
        startNewConversation({ keepRunning: isLoading });
        navigate(CHAT_HOME_PATH);
    }, [isLoading, navigate, startNewConversation]);

    useEffect(() => {
        const handleMobileHeaderNewChat = () => {
            handleClear();
        };

        window.addEventListener(MOBILE_HEADER_NEW_CHAT_EVENT, handleMobileHeaderNewChat);
        return () => {
            window.removeEventListener(MOBILE_HEADER_NEW_CHAT_EVENT, handleMobileHeaderNewChat);
        };
    }, [handleClear]);

    const handleToggleConversationBookmark = async () => {
        if (authLoading) return;
        if (!isAuthenticated) {
            openLoginModal();
            return;
        }
        const currentId = activeConversationIdRef.current || activeConversationId;
        if (!currentId) return;
        const entry = {
            id: String(currentId),
            title: chatTitle,
            updatedAt: activeConversation?.updatedAt || new Date().toISOString(),
            messageCount: activeConversation?.messageCount ?? chatHistory.length,
        };
        try {
            const next = await toggleConversationBookmark(entry);
            setConversationBookmarksState(next);
        } catch (error) {
            setConversationBookmarksState(getConversationBookmarks());
        }
    };

    const handleEditChatTitle = () => {
        if (authLoading) return;
        if (!isAuthenticated) {
            openLoginModal();
            return;
        }
        const currentId = activeConversationIdRef.current || activeConversationId;
        if (!currentId) return;
        setChatTitleDraft(chatTitle || '');
        setIsEditingChatTitle(true);
    };

    const handleCancelChatTitleEdit = () => {
        setIsEditingChatTitle(false);
        setChatTitleDraft('');
    };

    const handleConfirmChatTitleEdit = async () => {
        if (authLoading) return;
        if (!isAuthenticated) {
            openLoginModal();
            return;
        }
        const currentId = activeConversationIdRef.current || activeConversationId;
        if (!currentId) return;
        const currentTitle = chatTitle || '';
        const trimmed = chatTitleDraft.trim();
        if (!trimmed || trimmed === currentTitle) {
            handleCancelChatTitleEdit();
            return;
        }
        try {
            await updateConversationTitle(currentId, trimmed);
            setConversationsState(getConversations());
            handleCancelChatTitleEdit();
        } catch (error) {
            message.error('Unable to update chat title');
        }
    };

    const handleMessageClick = (index) => {
        if (chatHistory[index].role === 'assistant') {
            if (useMobileReferencesDrawer) {
                setIsMobileReferencesDrawerOpen(true);
            } else if (isReferencesCollapsed) {
                expandReferences();
            }
            prevSelectedMessageIndexRef.current = null;
            setSelectedMessageIndex(index);
        }
    };

    useEffect(() => {
        if (!chatHistory.length) {
            lastAutoSelectedRef.current = null;
            setSelectedMessageIndex(null);
            return;
        }
        if (isProcessing) return;

        let lastAssistantIndex = -1;
        for (let i = chatHistory.length - 1; i >= 0; i -= 1) {
            if (chatHistory[i]?.role === 'assistant') {
                lastAssistantIndex = i;
                break;
            }
        }
        if (lastAssistantIndex < 0) return;
        if (lastAutoSelectedRef.current === lastAssistantIndex) return;

        lastAutoSelectedRef.current = lastAssistantIndex;
        setSelectedMessageIndex(lastAssistantIndex);
    }, [chatHistory, isProcessing]);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container || isProcessing) return undefined;

        const updateSelectedResponse = () => {
            const containerRect = container.getBoundingClientRect();
            const anchorY = containerRect.top + (containerRect.height * 0.38);
            const assistantCards = Array.from(container.querySelectorAll('.message-card[data-message-role="assistant"]'));
            if (!assistantCards.length) return;

            const visibleCards = assistantCards.filter((card) => {
                const cardRect = card.getBoundingClientRect();
                return cardRect.bottom > containerRect.top && cardRect.top < containerRect.bottom;
            });
            const candidates = visibleCards.length ? visibleCards : assistantCards;
            const activeCard = candidates.reduce((closest, card) => {
                const closestDistance = Math.abs(closest.getBoundingClientRect().top - anchorY);
                const cardDistance = Math.abs(card.getBoundingClientRect().top - anchorY);
                return cardDistance < closestDistance ? card : closest;
            });
            const nextIndex = Number(activeCard.dataset.messageIndex);
            if (Number.isInteger(nextIndex)) {
                setSelectedMessageIndex((currentIndex) => currentIndex === nextIndex ? currentIndex : nextIndex);
            }
        };

        let animationFrameId = requestAnimationFrame(updateSelectedResponse);
        const handleScroll = () => {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = requestAnimationFrame(updateSelectedResponse);
        };
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            cancelAnimationFrame(animationFrameId);
            container.removeEventListener('scroll', handleScroll);
        };
    }, [chatHistory, isProcessing]);

    // useEffect(() => {
    //     if (!isLoading && !isProcessing && chatHistory.length > 0) {
    //         const lastMessage = chatHistory[chatHistory.length - 1];
    //         if (lastMessage.role === 'assistant' && selectedMessageIndex === null) {
    //             const lastAssistantIndex = chatHistory.length - 1;
    //             setTimeout(() => {
    //                 setSelectedMessageIndex(lastAssistantIndex);
    //             }, 300);
    //         }
    //     }
    // }, [isLoading, isProcessing, chatHistory]);

    const handleExampleClick = async (query) => {
        if (isLoading) return;
        startNewConversation();
        handleSubmit(null, query, null, { forceNewConversation: true });
    };

    const getInvocationIdForTurn = (messages, targetIndex) => {
        if (!Array.isArray(messages)) return null;
        const direct = messages[targetIndex]?.invocationId;
        if (direct) return direct;
        const prev = messages[targetIndex - 1];
        return prev?.invocationId || null;
    };

    const ensureInvocationIdForIndex = async (targetIndex) => {
        const existing = getInvocationIdForTurn(chatHistory, targetIndex);
        if (existing) return existing;
        const historyId = activeConversationIdRef.current;
        if (!historyId) return null;
        try {
            const detail = await fetchConversationDetail(historyId);
            const refreshed = detail?.messages || [];
            setChatHistory(refreshed);
            return getInvocationIdForTurn(refreshed, targetIndex);
        } catch (error) {
            return null;
        }
    };

    const handleRegenerateResponse = async (e, index) => {
        if (isLoading) return;
        const historyId = activeConversationIdRef.current;
        if (!historyId) return;

        const assistantMessage = chatHistory[index];
        if (!assistantMessage || assistantMessage.role !== 'assistant') return;
        const userMessage = chatHistory[index - 1];
        if (!userMessage || userMessage.role !== 'user') return;

        const invocationId = await ensureInvocationIdForIndex(index);
        if (!invocationId) {
            message.error('Unable to regenerate this response right now.');
            return;
        }

        try {
            await llmService.rewind(historyId, invocationId);
        } catch (error) {
            message.error('Unable to rewind conversation. Please try again.');
            return;
        }
        const trimmedHistory = chatHistory.slice(0, index - 1);
        setChatHistory(trimmedHistory);
        setShowReloadPrompt(false);
        handleSubmit(e, userMessage.content, null, {
            baseHistory: trimmedHistory,
            // The same question, so the same files.
            attachments: userMessage.attachments,
        });
    };

    const handleReloadLatest = () => {
        if (isLoading) return;
        const lastIndex = chatHistory.length - 1;
        if (lastIndex < 1) return;
        const lastMessage = chatHistory[lastIndex];
        if (!lastMessage || lastMessage.role !== 'assistant') return;
        setShowReloadPrompt(false);
        handleRegenerateResponse(null, lastIndex);
    };

    const handleStopStreaming = useCallback(() => {
        /* Tell the SERVER to stop before letting go of the view.

           Aborting the stream only detaches this tab. On BOTH pipelines the backend relay
           and the agent run are decoupled from the socket on purpose — that is what lets an
           answer survive a closed tab — so Stop used to leave the run grinding on at full
           token cost while the UI showed nothing, and leave the conversation's exchange
           unfinished, which is what keeps the sidebar's "still answering" mark up.

           Fired before the abort, and deliberately not awaited: the button must feel
           instant, and the request carries its own run id so it does not depend on any
           state the abort is about to clear. Best-effort by nature — the run may have
           finished a moment earlier, or its id may have been evicted — so a failure is
           logged and ignored rather than blocking the user from stopping their view. */
        const runId = runIdRef.current;
        const investigate = Boolean(runningInvestigateRef.current);
        if (runId) {
            llmService.cancelRun(runId, { investigate }).catch((error) => {
                logDev('cancelRun failed', error);
            });
        } else if (sessionIdRef.current) {
            /* A reattached view polls by SESSION id and may not have been told the run id
               yet — the reload destroyed the one it had. Without this, Stop on such a view
               told the server nothing at all and the run carried on spending minutes and
               tokens on an answer nobody would read. One extra round trip, best-effort. */
            const sessionId = sessionIdRef.current;
            llmService.getRun({ sessionId })
                .then((run) => (run?.run_id
                    ? llmService.cancelRun(run.run_id, { investigate })
                    : null))
                .catch((error) => logDev('cancelRun by session failed', error));
        }
        if (abortControllerRef.current) abortControllerRef.current.abort();
        /* The two views that own no socket — see `stoppedResumeKeyRef`. The reattach loop
           reads its key on the next pass and settles the turn; a submit still waiting for
           its stream stops the run on its `Started` frame, which is the first moment the
           run has a name to be cancelled by. */
        if (resumingConversationRef.current) {
            stoppedResumeKeyRef.current = resumingConversationRef.current;
        }
        if (activeStreamIdRef.current) stopRequestedStreamRef.current = activeStreamIdRef.current;
        if (!abortControllerRef.current && !activeStreamIdRef.current) {
            /* A REATTACHED run: nothing to abort, and no request of its own that will ever
               reach a `finally`, so do here what the stream's own would have done. Left out,
               the reader's Stop produced a stopped message under a progress panel that kept
               counting — for up to three seconds before the reattach loop woke, and forever
               in a view that had no loop either.

               Deliberately NOT the pre-stream case (`activeStreamIdRef` still set): that
               submit is alive and will unwind through its own `finally` the moment its
               conversation round trip returns. Tearing the view down from here instead left
               `isLoading` false with a submit still running, and the conversation-restore
               effect — which stands back only while a run owns the view — then replaced the
               reader's question with whatever it decided to open. */
            dripRef.current?.stop();
            setAnswerReady(false);
            setIsLoading(false);
            setIsProcessing(false);
            setStreamingStepName('');
            applyPendingClarification(null);
            setClarificationDrafts({});
            setClarificationError('');
            setClarificationSubmitting(false);
            const stoppedConversationId = runningConversationIdRef.current;
            if (stoppedConversationId) forgetRun(String(stoppedConversationId));
            else clearPendingRun();
        }
        /* Say the run was stopped, when there is nothing else to show.

           Deep research reveals its report only at the very end, so a run stopped mid-way
           leaves an assistant bubble with no text in it — which reads as a broken answer
           rather than as the thing the reader just asked for. Chat can reach the same state
           if it is stopped before the first token. The server writes the same sentence into
           the conversation for the same reason, so the two copies agree and a reload does
           not swap one for the other. A run that had already streamed text keeps it: partial
           work is still worth reading. */
        updateRunningChatHistory((prev) => withStoppedMessage(prev, { investigate }));
    }, [applyPendingClarification, llmService, updateRunningChatHistory]);

    const renderMessages = () => {
        // Only the last assistant card is the one being written, and every use of the live-run
        // props inside MessageCard sits behind its `isLoading`. Handing those props to the
        // settled cards too changed their props on every frame, so each streamed chunk
        // re-rendered — and re-parsed the markdown of — every answer in the conversation.
        const lastIndex = chatHistory.length - 1;
        return (<Box sx={{ p: isPhoneDevice ? 1 : 2 }}>{chatHistory.map((message, index) => {
            const isStreamingCard = isViewingRunningConversation
                && isProcessing
                && index === lastIndex
                && message.role === 'assistant';
            return (
            /* One message failing to draw costs that message, not the conversation. Without
               this, a single malformed answer unmounted the whole app — including the view
               of every run still being written. The reset key ticks when the message
               SETTLES or its attachments land (the final text, references, trajectory) —
               those are the updates that can fix a bad fragment. Not content length: that
               changes on every streamed tick, and a deterministically broken card would
               re-throw thirty times a second for the length of the answer. */
            <ErrorBoundary
                key={`${activeConversationId || 'new'}:${index}`}
                label={`message ${index}`}
                resetKey={`${message?.thoughtDurationMs ?? ''}:${message?.references?.length ?? 0}:${message?.trajectory ? 1 : 0}:${message?.content ? 1 : 0}`}
                fallback={(
                    <Box className="message-card message-card-failed" sx={{ p: 2, color: 'text.secondary' }}>
                        This message could not be displayed.
                    </Box>
                )}
            >
            <MessageCard
                index={index}
                message={message}
                referenceSortOption={sortOption}
                referenceSummaryMap={referenceSummaryMap}
                totalMessages={chatHistory.length}
                isProcessing={isStreamingCard}
                streamingGroups={isStreamingCard ? streamingGroups : NO_GROUPS}
                preamble={isStreamingCard ? preambleText : ''}
                streamingStepName={isStreamingCard ? streamingStepName : ''}
                investigatePhase={isStreamingCard ? investigatePhase : null}
                investigateFunnel={isStreamingCard ? investigateFunnel : null}
                investigateStartedAt={isStreamingCard ? investigateStartedAt : null}
                investigatePercent={isStreamingCard ? investigatePercent : null}
                investigateKeywords={isStreamingCard ? investigateKeywords : NO_KEYWORDS}
                investigatePapers={isStreamingCard ? investigatePapers : NO_PAPERS}
                investigateDetail={isStreamingCard ? investigateDetail : NO_DETAIL}
                pendingClarification={isStreamingCard ? pendingClarification : null}
                clarificationDrafts={clarificationDrafts}
                clarificationError={clarificationError}
                clarificationSubmitting={clarificationSubmitting}
                hasInvalidOtherSelection={hasInvalidOtherSelection}
                onUpdateClarificationDraft={updateClarificationDraft}
                onSubmitClarification={submitClarification}
                onSkipClarification={submitClarification}
                onDisplayFunnel={onDisplayFunnel}
                getDisplayFunnel={getDisplayFunnel}
                refresh={stableRefresh}
                copy={stableCopy}
                save={stableSave}
                downloadConversation={stableDownload}
                onOpenFeedback={stableOpenFeedback}
                showReloadPrompt={showReloadPrompt}
                onReloadLatest={stableReloadLatest}
                onStop={handleStopStreaming}
                interactionLocked={isLoading}
                conversationId={activeConversationId}
                answerReady={isStreamingCard && answerReady}
            />
            </ErrorBoundary>
            );
        })}
        {/* Only the ones waiting on THIS thread. They used to be drawn wherever the reader
            happened to be, so a question queued in one conversation appeared to be pending in
            another — and then was sent there. An entry queued before its row existed has no
            conversation to be shown under yet, and belongs to whatever is open. */}
        {promptsForConversation(
            pendingQueuedPrompts(queuedPrompts),
            activeConversationId,
            activeConversationId == null
                ? queueOwnerKeyRef.current
                : null,
        ).map((item) => (
            <Container
                key={item.id}
                className="message-pair queued-prompt"
                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', mb: '5px', justifyContent: 'flex-end' }}
            >
                <Box className="queued-prompt-bubble">
                    <span className="queued-prompt-text">
                        {item.text}
                        {(() => {
                            const files = normalizeAttachmentList(item.searchOptions?.attachments).length;
                            return files ? ` · ${files} file${files === 1 ? '' : 's'} attached` : null;
                        })()}
                    </span>
                    <button
                        type="button"
                        className="queued-prompt-remove"
                        title="Remove from queue"
                        aria-label={`Remove queued question: ${item.text}`}
                        onClick={() => removeQueuedPrompt(item)}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </button>
                </Box>
            </Container>
        ))}
        </Box>);
    };

    const [sortOption, setSortOption] = useState('Year');
    const [citeDialogOpen, setCiteDialogOpen] = useState(false);
    const [selectedCitation, setSelectedCitation] = useState(null);
    const [hoveredPubmedId, setHoveredPubmedId] = useState(null);
    const [isReferenceScopeOpen, setIsReferenceScopeOpen] = useState(false);
    const referencesListRef = useRef(null);
    const referenceSourceOptions = useMemo(() => chatHistory
        .map((item, itemIndex) => {
            const refs = Array.isArray(item?.references) ? item.references : [];
            if (item?.role !== 'assistant' || refs.length === 0) return null;
            const previousUserMessage = chatHistory
                .slice(0, itemIndex)
                .reverse()
                .find((entry) => entry?.role === 'user' && entry?.content);
            const label = previousUserMessage?.content || item?.content || `Response ${itemIndex + 1}`;
            return {
                index: itemIndex,
                label: String(label).replace(/\s+/g, ' ').trim(),
                count: refs.length,
            };
        })
        .filter(Boolean), [chatHistory]);

    const referenceSourceIndex = resolveReferenceSourceIndex(chatHistory, selectedMessageIndex);
    const selectedReferenceSource = referenceSourceOptions.find((item) => item.index === referenceSourceIndex) || null;

    const references = referenceSourceIndex !== null
        ? chatHistory[referenceSourceIndex]?.references || []
        : [];
    const [referenceSummaryMap, setReferenceSummaryMap] = useState({});
    const referenceSummaryPendingRef = useRef(new Set());

    useEffect(() => {
        const candidates = Array.from(new Set(
            references
                .filter(isPlaceholderPmidReference)
                .map((ref) => extractPmidFromReference(ref))
                .filter((pmid) => pmid && !referenceSummaryMap[pmid] && !referenceSummaryPendingRef.current.has(pmid))
        ));

        if (candidates.length === 0) return;

        candidates.forEach((pmid) => referenceSummaryPendingRef.current.add(pmid));

        fetchPubmedSummaryMap(candidates)
            .then((incomingMap) => {
                if (!incomingMap || typeof incomingMap !== 'object') return;
                setReferenceSummaryMap((prev) => ({
                    ...prev,
                    ...incomingMap,
                }));
            })
            .catch((error) => {
                logDev('[LLM] Failed to enrich placeholder references via esummary', error);
            })
            .finally(() => {
                candidates.forEach((pmid) => referenceSummaryPendingRef.current.delete(pmid));
            });
    }, [references, referenceSummaryMap]);

    const enrichedReferences = useMemo(
        () => enrichReferenceSummaries(references, referenceSummaryMap),
        [references, referenceSummaryMap]
    );

    useEffect(() => {
        if (!hoveredPubmedId) return;
        const isStillVisible = enrichedReferences.some((ref) => {
            const pubmedId = extractPmidFromReference(ref);
            return pubmedId === hoveredPubmedId;
        });
        if (!isStillVisible) {
            setHoveredPubmedId(null);
        }
    }, [hoveredPubmedId, enrichedReferences]);

    // Use the same enriched order for the panel and the inline citation display numbers.
    const sortedReferences = useMemo(() => sortReferences(
        enrichedReferences.map((reference, originalIndex) => ({ reference, originalIndex })),
        sortOption,
    ), [enrichedReferences, sortOption]);
    const isExportDisabled = sortedReferences.length === 0;

    const handleExportReferences = async () => {
        if (sortedReferences.length === 0) return;

        // Each entry renders from NCBI's record for its PMID (authors, volume, pages, DOI);
        // a reference NCBI has no record for falls back to the card's own fields.
        const cards = sortedReferences.map(({ reference: ref }) => [
            ref.title || '', ref.url || '', ref.citation_count ?? 0, ref.year ?? '', ref.journal || '', ref.authors || '',
        ]);
        const pmids = cards.map((card) => cslFromCard(card).PMID).filter(Boolean);
        const records = await fetchCitations(pmids);
        const bibTexContent = cards.map((card) => {
            const fallback = cslFromCard(card);
            return toBibTeX(records[fallback.PMID] || fallback);
        }).join('\n\n');

        const blob = new Blob([bibTexContent], { type: 'application/x-bibtex' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const now = new Date();
        const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const time = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
        a.download = `references_${date}_${time}.bib`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        message.success('References exported as BibTeX');
    };

    const handleCiteClick = (url) => {
        setSelectedCitation(url);
        setCiteDialogOpen(true);
    };

    const handleOpenFeedback = () => {
        setFeedbackRating(0);
        setFeedbackText('');
        setFeedbackOpen(true);
    };

    const handleCloseFeedback = () => {
        setFeedbackOpen(false);
        setFeedbackRating(0);
        setFeedbackText('');
        setFeedbackSubmitting(false);
    };

    const handleSubmitFeedback = async () => {
        if (!Number.isInteger(feedbackRating) || feedbackRating < 1 || feedbackRating > 5) {
            message.error('Please select a rating from 1 to 5 stars.');
            return;
        }

        const historyId = activeConversationIdRef.current || activeConversationId;
        if (!historyId) {
            message.error('Unable to submit feedback: conversation hid is missing.');
            return;
        }

        try {
            setFeedbackSubmitting(true);
            const submittedSessionId = String(historyId);
            const response = await submitChatFeedback({
                sessionId: submittedSessionId,
                rating: feedbackRating,
                feedback: feedbackText.trim(),
            });

            if (response?.ok !== true) {
                throw new Error(response?.message || 'Feedback submission failed.');
            }

            if (typeof response?.message === 'string' && /not\s*found/i.test(response.message)) {
                throw new Error('Conversation not found for current user.');
            }

            const updatedHint = response?.updated ? ' Existing feedback was updated.' : '';
            setFeedbackSuccessText(`${response?.message || 'Feedback submitted'}${updatedHint}`.trim());
            setFeedbackSuccessOpen(true);
            handleCloseFeedback();
        } catch (error) {
            const backendDetail = error.response?.data?.detail || error.response?.data?.message || error.message;
            const submittedSessionId = String(historyId);
            const normalizedDetail = typeof backendDetail === 'string' ? backendDetail.trim() : '';
            const isRouteNotFound = error.response?.status === 404
                && /^not\s+found$/i.test(normalizedDetail || '');
            const isConversationNotFound =
                /conversation\s+not\s+found|session[_\s-]*id.*not\s+exist|belongs\s+to\s+a\s+different\s+user/i
                    .test(normalizedDetail || '');

            if (isRouteNotFound) {
                message.error(`Feedback API endpoint not found. session_id=${submittedSessionId}. Please verify backend route deployment.`);
            } else if (isConversationNotFound) {
                message.error(`Conversation not found for current user (session_id=${submittedSessionId}). Please reopen this chat and try again.`);
            } else {
                message.error((normalizedDetail || 'Failed to submit feedback. Please try again.'));
            }
            setFeedbackSubmitting(false);
        }
    };

    const handleCloseFeedbackSuccess = (_, reason) => {
        if (reason === 'clickaway') return;
        setFeedbackSuccessOpen(false);
    };

    const handleCloseCiteDialog = () => {
        setCiteDialogOpen(false);
        setSelectedCitation(null);
    };

    // Hovering a citation brings its entry into view in the references panel.
    //
    // Deliberately not scrollIntoView: that scrolls *every* scrollable ancestor
    // of the target, not just the list, so bringing an entry to the middle of
    // the panel also nudged the column the citation itself lives in. The chip
    // slid out from under the pointer, which is a mouseout, which closed the
    // card the hover had just opened — the card blinked away exactly as the
    // scroll arrived. Scrolling the list and nothing else leaves the chip where
    // the pointer left it.
    useEffect(() => {
        const list = referencesListRef.current;
        if (!hoveredPubmedId || !list) return;

        // Scoped to the list for the same reason: both the desktop panel and the
        // mobile drawer mark their entries, and a document-wide query answers
        // with whichever is first in the DOM rather than the one on screen.
        const target = list.querySelector(`[data-pubmed-id="${hoveredPubmedId}"]`);
        if (!target) return;

        // Rects rather than offsetTop: offsetTop is measured from the nearest
        // positioned ancestor, which is not necessarily this list.
        const offset = target.getBoundingClientRect().top - list.getBoundingClientRect().top;
        const centred = offset - (list.clientHeight - target.offsetHeight) / 2;
        list.scrollTo({ top: Math.max(0, list.scrollTop + centred), behavior: 'smooth' });
    }, [hoveredPubmedId]);

    const handleDownloadConversation = (messageIndex) => {
        if (chatHistory.length === 0) return;

        const assistantMessage = chatHistory[messageIndex];
        const userMessage = messageIndex > 0 ? chatHistory[messageIndex - 1] : null;

        if (!assistantMessage || assistantMessage.role !== 'assistant') return;

        let conversationText = 'Q&A Export\n';
        conversationText += '='.repeat(50) + '\n\n';

        if (userMessage && userMessage.role === 'user') {
            conversationText += `[User] ${userMessage.timestamp || ''}\n`;
            conversationText += '-'.repeat(50) + '\n';
            conversationText += userMessage.content + '\n\n';
            conversationText += '='.repeat(50) + '\n\n';
        }

        conversationText += `[Assistant] ${assistantMessage.timestamp || ''}\n`;
        conversationText += '-'.repeat(50) + '\n';
        conversationText += assistantMessage.content + '\n';

        if (assistantMessage.references && assistantMessage.references.length > 0) {
            conversationText += '\n\nReferences:\n';
            conversationText += '-'.repeat(50) + '\n';
            assistantMessage.references.forEach((ref, refIndex) => {
                const pubmedId = ref.url.split('/').filter(Boolean).pop();
                conversationText += `[${refIndex + 1}] ${ref.authors} (${ref.year}). ${ref.title}. ${ref.journal}. PubMed ID: ${pubmedId}\n\n`;
            });
        }

        const blob = new Blob([conversationText], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const now = new Date();
        const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const time = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
        a.download = `qa_export_${date}_${time}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        message.success('Q&A downloaded');
    };

    // Declared here, after every handler above exists, and read by `renderMessages` — which is
    // defined earlier but only called from the JSX below. Without these the memo on MessageCard
    // never held: six props were a new function on every render.
    const stableSubmit = useStableCallback(handleSubmit);

    /**
     * Send now, or queue if an answer is still being written.
     *
     * The decision is made here rather than in the bar so the bar keeps one action with one
     * meaning — "this text is finished, take it" — whatever the run happens to be doing.
     *
     * A run in a DIFFERENT conversation is refused rather than queued: this queue belongs to
     * the thread on screen, and holding a question against a run the reader cannot see would
     * fire it at a moment they have no reason to expect.
     */
    const submitOrQueue = useCallback((event, composerOptions, queryMethod = 'button') => {
        event?.preventDefault?.();
        /* Files on the composer ride on the turn's options, so a queued follow-up keeps its
           own. Never while one is still uploading or failed — the composer holds the send back
           and its chips say why — and never on Investigate, which does not read them. A
           question that is only files asks for a summary of them. */
        const readyAttachments = composerAttachments.readyAttachments;
        if (composerAttachments.hasItems
            && (composerAttachments.isUploading || composerAttachments.hasErrors
                || composerOptions?.investigateEnabled)) {
            return;
        }
        const typed = userInput.trim();
        const text = typed || (readyAttachments.length ? defaultQuestionFor(readyAttachments) : '');
        if (!text) return;
        const searchOptions = readyAttachments.length
            ? { ...composerOptions, attachments: readyAttachments }
            : composerOptions;
        // Checked before queueing as well as before sending: a follow-up must not be taken out
        // of the box and drawn as a pending bubble that will never be sent. A guest is told why.
        if (isLimitReachedEffective) {
            if (isGuest) requireSignIn(limitWarningText);
            return;
        }
        /* "Busy" is the REGISTRY's word, not only the view's. The view flags describe the
           run the view follows; a background follow-up holds its conversation without ever
           touching them, and a submit into it used to be refused silently deep inside
           `handleSubmit` — Enter did nothing, the text stayed in the box, nothing said why.
           A question into a busy thread is queued, whoever is doing the answering. A
           question into any other thread just starts: it is its own session with its own
           history id, and the run the reader left keeps being written. */
        const targetConversationId = activeConversationIdRef.current;
        const targetBusy = isConversationRunning(targetConversationId)
            || (isLoading && isViewingRunningConversation)
            /* The nameless thread mid-recovery, before the first poll has flipped any flag: a
               guest's reattach holds no registry mark (there is no conversation to mark) and
               no isLoading yet, but a submit here would start a second turn on the very
               session the recovery is polling. */
            || (targetConversationId == null
                && Boolean(resumingConversationRef.current)
                && !runningConversationIdRef.current);
        if (!targetBusy) {
            // `input` only when the words are not the box's own (files with no question).
            stableSubmit(event, typed ? null : text, null, {
                searchOptions,
                queryMethod,
                fromComposer: true,
            });
            return;
        }
        const ownerConversationId = isViewingRunningConversation
            ? queuedPromptOwner(
                runningConversationIdRef.current,
                activeConversationIdRef.current,
            )
            : (targetConversationId != null ? String(targetConversationId) : null);
        const ownerRunKey = ownerConversationId == null
            ? queueOwnerKeyRef.current
            : null;
        queueSeqRef.current += 1;
        setQueuedPrompts((prev) => [...prev, {
            id: `q-${Date.now()}-${queueSeqRef.current}-${Math.random().toString(36).slice(2)}`,
            text,
            // The thread this is a follow-up TO — see promptQueue.js. When the view is
            // following the run, the run's own id is the most current name for it; when the
            // busy thread is being answered in the background, the screen's id is.
            conversationId: ownerConversationId,
            // Stable across guest turns; a completed transport must not orphan its queue.
            // Also separates provisional conversations until Saved gives them history ids.
            runKey: ownerRunKey,
            // Captured now rather than read at send time: they describe the turn the reader
            // meant to ask for.
            searchOptions,
            pipelineVersion: 1,
            queryMethod,
        }]);
        setUserInput('');
        clearComposerAttachments();
    }, [userInput, isLoading, isViewingRunningConversation, isLimitReachedEffective,
        isGuest, requireSignIn, limitWarningText, stableSubmit, composerAttachments,
        clearComposerAttachments]);

    const removeQueuedPrompt = useCallback((entry) => {
        if (!entry?.id) return;
        consumeQueuedPrompt(entry);
        /* A pending snapshot write reads the latest ref when its timer fires. Update that ref
           first, otherwise the timer can put the consumed prompt straight back after the
           durable copy below removed it. */
        const liveSnapshot = liveRunSnapshotRef.current;
        const sameSnapshot = liveSnapshot
            && String(liveSnapshot.conversationId ?? '')
                === String(entry.conversationId ?? '');
        if (sameSnapshot && Array.isArray(liveSnapshot.queuedPrompts)) {
            liveRunSnapshotRef.current = {
                ...liveSnapshot,
                queuedPrompts: liveSnapshot.queuedPrompts.filter(
                    (item) => item?.id !== entry.id,
                ),
            };
        }
        const initialSnapshot = initialRunSnapshotRef.current;
        const sameInitialSnapshot = initialSnapshot
            && String(initialSnapshot.conversationId ?? '')
                === String(entry.conversationId ?? '');
        if (sameInitialSnapshot && Array.isArray(initialSnapshot.queuedPrompts)) {
            initialRunSnapshotRef.current = {
                ...initialSnapshot,
                queuedPrompts: initialSnapshot.queuedPrompts.filter(
                    (item) => item?.id !== entry.id,
                ),
            };
        }
        removeQueuedPromptFromSnapshot(entry.conversationId ?? null, entry.id);
        /* By the entry's own identity — the id, for anything minted since ids became unique.
           This used to compare the conversation id and run key too, and both are rewritten
           while an entry waits (a Saved frame files it under its conversation), so a copy
           claimed between this effect reading the queue and React flushing the state survived
           its own removal and was sent a second time. Legacy `q-N` ids keep the scoped
           comparison, where it is the only thing telling two of them apart. */
        const goneIdentity = queueEntryIdentity(entry);
        setQueuedPrompts((prev) => prev.filter((item) => (
            item !== entry && queueEntryIdentity(item) !== goneIdentity
        )));
    }, []);

    /* The other half of `removeQueuedPrompt`. An entry leaves the queue BEFORE its turn is
       submitted, so that a conversation switch cannot submit it twice; if the submit then
       refuses — over quota, the auth check still running, a throw before the request left —
       that would be the silent end of a question the reader watched leave their composer.
       This puts it back, pending again, where they can see it. */
    const requeueQueuedPrompt = useCallback((entry) => {
        if (!entry?.id) return;
        unconsumeQueuedPrompt(entry);
        restoreQueuedPromptToSnapshot(entry.conversationId ?? null, entry);
        const identity = queueEntryIdentity(entry);
        setQueuedPrompts((prev) => {
            if (prev.some((item) => queueEntryIdentity(item) === identity)) return prev;
            /* At the FRONT, not the end. `nextReleasableEntry` takes the earliest entry for a
               conversation, and that is how two follow-ups keep the order they were asked in —
               appending would send the reader's second question before their first. */
            return [entry, ...prev];
        });
    }, []);

    const announceBackgroundClarification = useCallback((forConversationId, round) => {
        const notificationKey = [
            forConversationId || 'current',
            round?.invocationId || 'unknown',
            round?.stage || 'unknown',
        ].join(':');
        if (backgroundClarificationNotifiedRef.current.has(notificationKey)) return;
        backgroundClarificationNotifiedRef.current.add(notificationKey);
        message.info('Investigate needs your input. Return to the conversation to continue.');
    }, []);

    /**
     * Answer a queued follow-up in a conversation the reader has left.
     *
     * The view must not notice this run at all. Everything it touches is per-run or
     * per-conversation — the registry mark, the conversation's stored messages, its stored
     * session id — and none of it is the view's. The singletons (`activeStreamIdRef`,
     * `isLoading`, the streaming panels, `sessionIdRef`, `runIdRef`) describe the run the VIEW
     * follows; the released follow-up used to go through `handleSubmit` and take them all,
     * which is exactly how one conversation's spinner, progress and answer smeared across
     * whatever conversation was open when the queue let go.
     *
     * The reader can watch it anyway: its optimistic turn is in the store and its session id
     * is stored, so opening its conversation reattaches through the same polling path a
     * reload uses.
     */
    const runBackgroundTurn = async (entry) => {
        const conversationId = String(entry.conversationId);
        const streamId = `${Date.now()}-bg-${Math.random().toString(36).slice(2, 8)}`;
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const requestStartedAt = Date.now();
        const requestSearchOptions = resolveQueuedSearchOptions(entry, getConversationInvestigateMode(conversationId));
        const trackQuerySubmitSuccess = createQuerySubmitSuccessTracker(entry.queryMethod);
        const investigateEnabled = Boolean(
            requestSearchOptions?.investigateEnabled ?? getConversationInvestigateMode(conversationId),
        );
        recordSubmittedMode(conversationId, investigateEnabled);
        /* Settled before the run is registered, because the registry records it: it is the
           address this answer can be collected at after the page goes away. */
        const sessionId = getStoredSessionId(conversationId) || mintSessionId();
        setStoredSessionId(conversationId, sessionId);
        /* Registered before anything asynchronous: the moment the queue entry is gone, the
           registry is the only thing standing between this run and a second release onto the
           same history id. */
        setActiveRun({ kind: investigateEnabled ? 'investigate' : 'chat', runId: null, conversationId, sessionId, key: streamId });
        backgroundRunsRef.current.add(String(conversationId));
        /* From here the conversation is marked as answering, in the shared registry and in
           this tab's own record, and BOTH have to come down however this ends. Anything that
           throws before the request — a full sessionStorage, an unreadable stored transcript —
           used to leave the marks up for the life of the mount: the conversation reads as
           permanently answering, and (since the visit-time reconcile now trusts this tab's
           record) nothing clears it, so every later question to it queues and never goes. */
        const releaseBackgroundMarks = () => {
            backgroundRunsRef.current.delete(String(conversationId));
            clearActiveRun(conversationId);
        };
        try {
            // A NEW run re-arms this conversation's completion notice: the set de-duplicates per
            // conversation, and without this a second background follow-up finished silently.
            backgroundCompletionNotifiedRef.current.delete(conversationId);

            /* The stored copy can be frozen mid-exchange: a run released by New Chat stops
               writing the store the moment the view lets go (its frames are dropped), so the
               store still ends [question, empty assistant] after the server has long since
               finished. Building on that would file this turn after a blank answer and send the
               agent a truncated context. The release only happens once the previous run settled,
               so the server's copy is whole — fetch it when the stored one is not. */
            let base = getStoredChatHistory(conversationId);
            if (!base.length || isExchangeUnfinished(base)) {
                try {
                    const detail = await fetchConversationDetail(conversationId);
                    const serverBase = detail?.messages || [];
                    if (serverBase.length >= base.length) base = serverBase;
                } catch (error) {
                    logDev('[LLM] background turn base refetch failed', error);
                }
            }
            // The files the queued follow-up was written with — AI Chat only, as in handleSubmit.
            const turnAttachments = investigateEnabled
                ? []
                : normalizeAttachmentList(requestSearchOptions?.attachments);
            const turnAnswerOptions = answerOptionsForTurn(requestSearchOptions, base, investigateEnabled);
            const userMessage = {
                role: 'user',
                content: entry.text,
                references: [],
                timestamp,
                investigateMode: investigateEnabled,
                modeSource: 'submitted',
                ...(turnAttachments.length ? { attachments: turnAttachments } : {}),
            };
            let history = [...base, userMessage, {
                role: 'assistant',
                content: '',
                references: [],
                timestamp,
                thinkingSteps: [],
                thoughtDurationMs: null,
                trajectory: null,
                investigateMode: investigateEnabled,
            }];
            let savedConversationRefresh = null;
            writeConversationMessages(conversationId, history);

            const localThinkingSteps = [];
            let settled = false;
            let succeeded = false;
            const settleWith = (assistantMessage) => {
                if (settled) return;
                settled = true;
                history = [...history.slice(0, -1), assistantMessage];
                writeConversationMessages(conversationId, history);
            };

            try {
                await llmService.chat(entry.text, new AbortController(), (update) => {
                    trackQuerySubmitSuccess(update);
                    switch (update.type) {
                        case 'step':
                            if (update.step === 'Error') {
                                settleWith({
                                    role: 'assistant',
                                    content: update.content,
                                    references: [],
                                    timestamp,
                                    thinkingSteps: [...localThinkingSteps],
                                    thoughtDurationMs: Date.now() - requestStartedAt,
                                    investigateMode: investigateEnabled,
                                });
                            } else if (update.step && String(update.content ?? '').trim()) {
                                localThinkingSteps.push({ step: update.step, content: update.content });
                            }
                            break;
                        /* 'final', not 'complete': the service names the finished-answer frame
                           'final' (LLMAgent.jsx maps step 'Complete' to it), and listening for a
                           frame that never comes left the stored transcript ending in an empty
                           assistant bubble until the server copy was next fetched. */
                        case 'final':
                            if (update.sessionId) {
                                setStoredSessionId(conversationId, update.sessionId);
                            }
                            succeeded = true;
                            settleWith({
                                role: 'assistant',
                                content: update.answer,
                                references: parseReferences(update.references),
                                directCitations: parseDirectCitations(update.directCitations),
                                kgQueryList: update.kgQueryList || [],
                                answerOptions: update.answerOptions || {},
                                timestamp,
                                thinkingSteps: [...localThinkingSteps],
                                thoughtDurationMs: Date.now() - requestStartedAt,
                                trajectory: update.trajectory || null,
                                investigateMode: investigateEnabled,
                            });
                            break;
                        case 'saved': {
                            applyStreamCredits(update.credits);
                            const savedId = update.historyId ? String(update.historyId) : conversationId;
                            if (update.sessionId) {
                                setStoredSessionId(savedId, update.sessionId);
                            }
                            if (isAuthenticated) {
                                savedConversationRefresh = fetchConversations()
                                    .then((list) => setConversationsState(list))
                                    .catch((error) => logDev('[LLM] Failed to refresh conversations', error));
                            }
                            break;
                        }
                        case 'error':
                            applyStreamCredits(update.credits);
                            if (update.refusal) handleRefusal(update.refusal);
                            settleWith({
                                role: 'assistant',
                                // A refusal is said in the backend's words (utils/refusals.js).
                                content: update.refusal ? refusalText(update.refusal) : `Error: ${update.error}`,
                                references: [],
                                timestamp,
                                thinkingSteps: [...localThinkingSteps],
                                thoughtDurationMs: Date.now() - requestStartedAt,
                                investigateMode: investigateEnabled,
                            });
                            break;
                        default:
                            break;
                    }
                }, {
                    historyId: conversationId,
                    sessionId,
                    filters: Array.isArray(requestSearchOptions?.filters)
                        ? requestSearchOptions.filters
                        : undefined,
                    rankingMode: typeof requestSearchOptions?.rankingMode === 'string'
                        ? requestSearchOptions.rankingMode
                        : undefined,
                    investigateEnabled,
                    // The turn's OWN tier, off the options bag rather than off `serviceTier`: a
                    // queued follow-up must send the tier that was showing when the reader hit
                    // send, not whatever the picker moved to while it waited in the queue.
                    serviceTier: effectiveTier(requestSearchOptions?.serviceTier, { isGuest }) || undefined,
                    attachments: turnAttachments.length ? attachmentIdsOf(turnAttachments) : undefined,
                    figures: turnAnswerOptions.figures,
                    graph: turnAnswerOptions.graph,
                    messagesOverride: [...base, userMessage].map((msg) => ({
                        role: msg?.role,
                        content: msg?.content,
                    })),
                });
            } catch (error) {
                logDev('[LLM] background turn failed', error);
                if (error?.response?.status === 429) {
                    setIsQueryLimitReached(true);
                }
                /* The stream dying does not mean the answer died — the agent keeps writing and
                   retains the run against its session id, which is the same recovery a reload
                   uses. Poll for a while; only a run the server says is gone gets an error
                   written into the transcript. Giving up while it still says "running" writes
                   nothing: the exchange stays visibly unfinished and opening the conversation
                   resumes it through the ordinary reattach path. */
                let outcome = 'unknown';
                for (let attempt = 0; attempt < 100 && !settled; attempt += 1) {
                    let run = null;
                    let missing = false;
                    try {
                        run = await llmService.getRun({ sessionId });
                    } catch (pollError) {
                        missing = pollError?.response?.status === 404;
                    }
                    if (run && (run.status === 'complete' || run.response)) {
                        succeeded = true;
                        settleWith({
                            role: 'assistant',
                            content: run.response || '',
                            references: parseReferences(run.references),
                            timestamp,
                            thinkingSteps: [...localThinkingSteps],
                            thoughtDurationMs: Date.now() - requestStartedAt,
                            trajectory: run.trajectory || null,
                            investigateMode: investigateEnabled,
                        });
                        outcome = 'recovered';
                        break;
                    }
                    if (run?.status === 'error' || missing) {
                        outcome = 'lost';
                        break;
                    }
                    await new Promise((resolve) => setTimeout(resolve, RESUME_POLL_MS));
                }
                if (outcome === 'lost') {
                    settleWith({
                        role: 'assistant',
                        content: 'Sorry, I encountered an error while processing your request. Please try again.',
                        references: [],
                        timestamp,
                        thinkingSteps: [...localThinkingSteps],
                        thoughtDurationMs: Date.now() - requestStartedAt,
                        investigateMode: investigateEnabled,
                    });
                }
            } finally {
                refreshTierStatus();
                holdRecentPriority(conversationId, savedConversationRefresh);
                releaseBackgroundMarks();
                // Only a real answer is announced as ready; an error or an unfinished turn
                // saying "is ready" would send the reader to an answer that is not there.
                if (succeeded) announceBackgroundCompletion(conversationId);
            }
        } catch (error) {
            // Nothing was sent: the marks come down and the caller puts the follow-up back.
            releaseBackgroundMarks();
            throw error;
        }
    };
    const stableRunBackgroundTurn = useStableCallback(runBackgroundTurn);

    /**
     * Release queued prompts as their conversations come free.
     *
     * Each entry waits on its OWN conversation, not on the view: a follow-up to the thread on
     * screen is submitted normally once that thread is idle, and one to a thread the reader
     * has left rides `runBackgroundTurn` without the view noticing. The registry revision is
     * among the dependencies because "came free" is a registry event — a run finishing in the
     * background changes no view state, and the queue used to sleep through it, releasing at
     * whatever unrelated re-render happened next.
     *
     * Dispatch claims are scoped to a conversation and release with a React notification.
     * A ref-only release could strand the next entry after the final run-state render.
     */
    const { revision: queueDispatchRevision, isDispatching, dispatch } = useQueueDispatch();
    useEffect(() => {
        if (!queuedPrompts.length || isLimitReachedEffective) return;
        /* Nothing may be released while the auth check is still running: the submit path
           refuses then, and an entry is consumed before it is submitted. Holding is the safe
           state — the effect runs again the moment auth resolves. */
        if (authLoading) return;
        if (!isAuthenticated) {
            /* And once it has answered "no account", holding forever would leave pending
               bubbles on screen that nothing will ever send. A snapshot written before asking
               required an account can still restore some. Drop them, so what is drawn is only
               what can still happen. */
            queuedPrompts.forEach((entry) => removeQueuedPrompt(entry));
            return;
        }
        const viewBusy = isLoading || isProcessing || isConversationLoading
            || isDispatching(activeConversationIdRef.current ?? '__guest__')
            // The nameless thread counts as busy while its recovery is polling — the flags
            // only flip after the first poll answers. See submitOrQueue.
            || Boolean(resumingConversationRef.current && !activeConversationIdRef.current
                && !runningConversationIdRef.current);
        const next = nextReleasableEntry(pendingQueuedPrompts(queuedPrompts), {
            activeConversationId: activeConversationIdRef.current,
            activeRunKey: activeConversationIdRef.current == null
                ? queueOwnerKeyRef.current
                : null,
            isConversationRunning: (id) => isConversationRunning(id) || isDispatching(id),
            /* An investigate follow-up can stop to ask a clarifying question, which only the
               reader can answer — it must not run where nobody is watching. */
            requiresScreen: (entry) => Boolean(
                resolveQueuedSearchOptions(entry, getConversationInvestigateMode(entry?.conversationId)).investigateEnabled,
            ),
            viewBusy,
        });
        if (!next) return;
        /* Remove it from memory AND from its conversation snapshot before starting the turn.
           Otherwise switching away and back restores this already-consumed entry and submits
           one duplicate on every visit. */
        removeQueuedPrompt(next);
        /* Into the conversation it was written against, not the one on screen. */
        const { conversationId: targetId, onScreen: targetIsOnScreen } = releaseTargetFor(
            next,
            activeConversationIdRef.current,
            activeConversationIdRef.current == null
                ? queueOwnerKeyRef.current
                : null,
        );
        if (!targetIsOnScreen && targetId != null) {
            // It has its own error handling once the request is away; this catches what can
            // throw BEFORE that — a full sessionStorage, an unreadable stored transcript —
            // where the entry would otherwise be consumed and gone.
            Promise.resolve(stableRunBackgroundTurn(next)).catch((error) => {
                logDev('[LLM] Queued background turn failed to start', error);
                requeueQueuedPrompt(next);
            });
            return;
        }
        dispatch(targetId ?? '__guest__', () =>
            stableSubmit(null, next.text, null, {
                searchOptions: resolveQueuedSearchOptions(next, getConversationInvestigateMode(targetId)),
                queryMethod: next.queryMethod,
                ...(targetId == null ? {} : { conversationId: targetId }),
            }).then((started) => {
                /* `handleSubmit` answers whether a turn actually began. Anything OTHER than a
                   plain true means it did not: a refusal (already answering, over quota, auth
                   not settled) answers false, and `dispatch` itself resolves with undefined
                   without calling the submit at all when that conversation is already claimed.
                   The entry is out of the queue by then either way. */
                if (started !== true) requeueQueuedPrompt(next);
            }),
        ).catch((error) => {
            logDev('[LLM] Queued submit failed', error);
            requeueQueuedPrompt(next);
        });
    }, [
        isLoading, isProcessing, isConversationLoading, activeConversationId,
        runRegistryRevision, queuedPrompts, isLimitReachedEffective,
        authLoading, isAuthenticated,
        stableSubmit, stableRunBackgroundTurn, removeQueuedPrompt, requeueQueuedPrompt,
        queueDispatchRevision, isDispatching, dispatch,
    ]);

    const stableRefresh = useStableCallback(handleRegenerateResponse);
    const stableCopy = useStableCallback(handleCopyMessage);
    const stableSave = useStableCallback(handleSaveEdit);
    const stableDownload = useStableCallback(handleDownloadConversation);
    const stableOpenFeedback = useStableCallback(handleOpenFeedback);
    const stableReloadLatest = useStableCallback(handleReloadLatest);

    /* Nothing to show: no conversation restored, nothing streaming, and no question
       handed over from the home page.
       
       This used to be the "Explore Biomedical Literature" screen — a second home page,
       reachable by typing /chat, and shown for as long as it took the first token to
       arrive after a question was submitted, which is why it flashed. Home is the page
       that does this job, so go there instead of drawing an emptier version of it.
       
       Deliberately not an unconditional redirect on /chat: recovering a run after a
       reload arrives here with no router state either, and sending that to the home
       page would undo it. What separates the two is whether there is a conversation
       to restore. */
    const hasNothingToShow = !isConversationLoading
        && !isProcessing
        // A question handed over from the home page is on its way: the state that carried it
        // has already been cleared, and the conversation it will live in does not exist yet.
        // Reading "nothing to show" in that window sent the asker back to the home page.
        && !initialQueryTransitionRef.current
        && chatHistory.length === 0
        && !activeConversationId
        && !location.state?.initialQuery
        && !location.state?.conversationId
        // The address names a conversation: it is still being fetched, not absent. Redirecting
        // here is what made a reopened /chat/<id> bounce to the home page.
        && !routePublicId;

    // Keep the controller, refs and effects mounted while another page is visible, but do not
    // leave a hidden copy of the very large chat DOM (or its Helmet metadata) in that page.
    if (!isRouteActive) {
        return null;
    }

    if (hasNothingToShow) {
        return <Navigate to={CHAT_HOME_PATH} replace />;
    }

    return (
        <>
            <Helmet>
                <title>AI Chat - Genomic Literature Knowledge Base</title>
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&icon_names=forum" />
                <meta name="description" content="Ask, Analyze, Cite. Start a chat now with GLKB, your scientific research AI assistant." />
                <meta property="og:title" content="AI Chat - Genomic Literature Knowledge Base | AI-Powered Genomics Search" />
                <meta property="og:description" content="Ask, Analyze, Cite. Start a chat now with GLKB, your scientific research AI assistant." />
            </Helmet>

            <CiteDialog
                open={citeDialogOpen}
                onClose={handleCloseCiteDialog}
                citation={selectedCitation}
            />

            <Snackbar
                open={feedbackSuccessOpen}
                autoHideDuration={3000}
                onClose={handleCloseFeedbackSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={handleCloseFeedbackSuccess}
                    sx={{ width: '100%' }}
                >
                    {feedbackSuccessText}
                </Alert>
            </Snackbar>

            {/* Clarify is inline in the investigate message (Figma Asking Question). Modal kept disabled. */}
            <Dialog
                open={false}
                onClose={() => {}}
                disableEscapeKeyDown
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '20px',
                        fontWeight: 600,
                        color: 'var(--color-grey-900)',
                    }}
                >
                    Clarify Your Research Scope
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '14px',
                            color: 'var(--color-text-tertiary)',
                            lineHeight: 1.5,
                            mb: 2,
                        }}
                    >
                        Answering these helps the agent narrow evidence and improve citation quality.
                    </Typography>

                    {pendingClarification?.reason && (
                        <Box
                            sx={{
                                borderRadius: '10px',
                                backgroundColor: 'var(--color-brand-subtle)',
                                border: '1px solid var(--color-border-default)',
                                px: 1.5,
                                py: 1,
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'DM Sans, sans-serif',
                                    fontSize: '12px',
                                    color: 'var(--color-blue-700)',
                                    lineHeight: 1.45,
                                }}
                            >
                                {pendingClarification.reason}
                            </Typography>
                        </Box>
                    )}

                    <Stack spacing={2}>
                        {(pendingClarification?.questions || []).map((question, index) => {
                            const questionKey = getClarificationQuestionKey(question, index);
                            const draft = clarificationDrafts[questionKey] || { selected: [], text: '', otherSelected: false };
                            const selected = Array.isArray(draft.selected) ? draft.selected : [];
                            const otherText = typeof draft.text === 'string' ? draft.text : '';
                            const responseType = String(question?.response_type || 'text').toLowerCase();
                            const options = Array.isArray(question?.options) ? question.options : [];
                            const radioValue = draft.otherSelected ? '__other__' : (selected[0] || '');

                            return (
                                <Box
                                    key={questionKey}
                                    sx={{
                                        border: '1px solid var(--color-border-default)',
                                        borderRadius: '12px',
                                        p: 1.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'DM Sans, sans-serif',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: 'var(--color-text-tertiary)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.03em',
                                            mb: 0.5,
                                        }}
                                    >
                                        {question?.header || `Question ${index + 1}`}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily: 'DM Sans, sans-serif',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: 'var(--color-text-secondary)',
                                            lineHeight: 1.45,
                                            mb: 1,
                                        }}
                                    >
                                        {question?.question || ''}
                                    </Typography>

                                    {responseType === 'single' && (
                                        <>
                                            <RadioGroup
                                                value={radioValue}
                                                onChange={(event) => {
                                                    const nextValue = event.target.value;
                                                    if (nextValue === '__other__') {
                                                        updateClarificationDraft(questionKey, {
                                                            selected: [],
                                                            text: otherText,
                                                            otherSelected: true,
                                                        });
                                                        return;
                                                    }
                                                    updateClarificationDraft(questionKey, {
                                                        selected: nextValue ? [nextValue] : [],
                                                        text: '',
                                                        otherSelected: false,
                                                    });
                                                }}
                                            >
                                                {options.map((option) => {
                                                    const optionLabel = String(option?.label || '').trim();
                                                    if (!optionLabel) return null;
                                                    return (
                                                        <FormControlLabel
                                                            key={optionLabel}
                                                            value={optionLabel}
                                                            control={<Radio size="small" />}
                                                            label={option?.description || optionLabel}
                                                        />
                                                    );
                                                })}
                                                <FormControlLabel value="__other__" control={<Radio size="small" />} label="Other" />
                                            </RadioGroup>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Optional custom answer"
                                                value={otherText}
                                                onChange={(event) => {
                                                    updateClarificationDraft(questionKey, {
                                                        selected: [],
                                                        text: event.target.value,
                                                        otherSelected: true,
                                                    });
                                                }}
                                                sx={{ mt: 1 }}
                                            />
                                        </>
                                    )}

                                    {responseType === 'multi' && (
                                        <>
                                            <Stack spacing={0.5}>
                                                {options.map((option) => {
                                                    const optionLabel = String(option?.label || '').trim();
                                                    if (!optionLabel) return null;
                                                    const checked = selected.includes(optionLabel);
                                                    return (
                                                        <FormControlLabel
                                                            key={optionLabel}
                                                            control={(
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={checked}
                                                                    onChange={(event) => {
                                                                        const nextSelected = event.target.checked
                                                                            ? [...selected, optionLabel]
                                                                            : selected.filter((item) => item !== optionLabel);
                                                                        updateClarificationDraft(questionKey, {
                                                                            selected: Array.from(new Set(nextSelected)),
                                                                            text: otherText,
                                                                        });
                                                                    }}
                                                                />
                                                            )}
                                                            label={option?.description || optionLabel}
                                                        />
                                                    );
                                                })}
                                            </Stack>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Optional additional context"
                                                value={otherText}
                                                onChange={(event) => {
                                                    updateClarificationDraft(questionKey, {
                                                        selected,
                                                        text: event.target.value,
                                                    });
                                                }}
                                                sx={{ mt: 1 }}
                                            />
                                        </>
                                    )}

                                    {responseType === 'text' && (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Type your answer"
                                            value={otherText}
                                            onChange={(event) => {
                                                updateClarificationDraft(questionKey, {
                                                    selected: [],
                                                    text: event.target.value,
                                                });
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>

                    {clarificationError && (
                        <Typography
                            sx={{
                                mt: 2,
                                fontFamily: 'DM Sans, sans-serif',
                                fontSize: '12px',
                                color: 'var(--color-status-error-text)',
                            }}
                        >
                            {clarificationError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <MuiButton
                        disabled={clarificationSubmitting}
                        onClick={() => submitClarification({ useDefaults: true })}
                        sx={{
                            borderRadius: '10px',
                            border: '1px solid var(--color-border-strong)',
                            textTransform: 'none',
                            fontFamily: 'DM Sans, sans-serif',
                            color: 'var(--color-grey-600)',
                        }}
                    >
                        Skip (Use Defaults)
                    </MuiButton>
                    <MuiButton
                        disabled={clarificationSubmitting || hasInvalidOtherSelection}
                        onClick={() => submitClarification({ useDefaults: false })}
                        sx={{
                            borderRadius: '10px',
                            border: '1px solid var(--color-brand-primary)',
                            backgroundColor: 'var(--color-brand-primary)',
                            textTransform: 'none',
                            fontFamily: 'DM Sans, sans-serif',
                            color: 'var(--color-neutral-white)',
                            '&:hover': {
                                backgroundColor: 'var(--color-blue-600)',
                                borderColor: 'var(--color-blue-600)',
                            },
                        }}
                    >
                        {clarificationSubmitting ? 'Submitting...' : 'Submit Answers'}
                    </MuiButton>
                </DialogActions>
            </Dialog>

            {/* The leave-while-running dialog is gone with the guard that raised it. */}

            <Dialog
                open={feedbackOpen}
                onClose={handleCloseFeedback}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: '512px',
                        borderRadius: '16px',
                        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                    },
                }}
            >
                <Box sx={{ p: '40px', display: 'flex', flexDirection: 'column', gap: '28px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                            sx={{
                                fontFamily: 'DM Sans, sans-serif',
                                fontSize: '24px',
                                fontWeight: '700 !important',
                                lineHeight: 1.3,
                                color: 'var(--color-text-secondary)',
                            }}
                        >
                            Share your feedback
                        </Typography>
                        <IconButton onClick={handleCloseFeedback} aria-label="Close feedback dialog">
                            <ClearIcon sx={{ color: 'var(--color-text-tertiary)' }} />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Typography
                            sx={{
                                fontFamily: 'DM Sans, sans-serif',
                                fontSize: '16px',
                                fontWeight: '400 !important',
                                lineHeight: 1.5,
                                color: 'var(--color-text-secondary)',
                            }}
                        >
                            Your feedback helps us improve GLKB.
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <IconButton
                                    key={star}
                                    onClick={() => setFeedbackRating(star)}
                                    sx={{ p: 0, width: '40px', height: '40px' }}
                                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                                >
                                    <StarIcon
                                        sx={{
                                            fontSize: 32,
                                            color: feedbackRating >= star ? '#F5AF18' : 'var(--color-grey-200)',
                                        }}
                                    />
                                </IconButton>
                            ))}
                        </Box>

                        <TextField
                            multiline
                            minRows={4}
                            value={feedbackText}
                            onChange={(event) => setFeedbackText(event.target.value)}
                            placeholder="What did you think of this response?  (optional)"
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontFamily: 'DM Sans, sans-serif',
                                    fontSize: '16px',
                                    fontWeight: '400 !important',
                                    color: 'var(--color-text-secondary)',
                                    '& fieldset': {
                                        borderColor: 'var(--color-grey-400)',
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'var(--color-grey-400)',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '28px' }}>
                        <MuiButton
                            onClick={handleCloseFeedback}
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid var(--color-border-strong)',
                                color: 'var(--color-text-secondary)',
                                backgroundColor: 'var(--color-background-surface)',
                                textTransform: 'none',
                                fontFamily: 'DM Sans, sans-serif',
                                fontSize: '16px',
                                fontWeight: '400 !important',
                                lineHeight: 1.3,
                                px: '16px',
                                py: '8px',
                                minWidth: '96px',
                            }}
                        >
                            Cancel
                        </MuiButton>
                        <MuiButton
                            onClick={handleSubmitFeedback}
                            disabled={feedbackSubmitting || feedbackRating < 1}
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: 'var(--color-brand-primary)',
                                color: 'var(--color-neutral-white)',
                                textTransform: 'none',
                                fontFamily: 'DM Sans, sans-serif',
                                fontSize: '16px',
                                fontWeight: '400 !important',
                                lineHeight: 1.3,
                                px: '16px',
                                py: '8px',
                                minWidth: '170px',
                                '&:hover': {
                                    backgroundColor: 'var(--color-blue-600)',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'var(--color-blue-200)',
                                    color: 'var(--color-neutral-white)',
                                },
                            }}
                        >
                            {feedbackSubmitting ? 'Submitting...' : 'Submit feedback'}
                        </MuiButton>
                    </Box>
                </Box>
            </Dialog>

            <div className="llm-page">
                <Grid className="llm-grid" container sx={{ width: "100%" }}>
                    <Grid item xs={12} className="llm-subgrid">
                        <div className="llm-main-content">
                            {/* <MuiButton variant="text" sx={{
                                color: 'var(--color-text-secondary)',
                                fontFamily: 'Open Sans, sans-serif',
                                alignSelf: 'flex-start',
                                zIndex: 1,
                                borderRadius: '24px',
                                marginTop: '16px',
                                marginBottom: '16px',
                            }}
                                onClick={() => navigate('/')}>
                                <ArrowBackIcon />Back
                            </MuiButton> */}
                            <div className='llm-content'>
                                <div className="llm-agent-container">
                                    <div className="chat-and-references">
                                        <Box ref={splitContainerRef} className="llm-split" sx={{ display: 'flex', minHeight: 0, height: '100%' }}>
                                            <Box
                                                className="llm-column"
                                                sx={{
                                                    flex: useMobileReferencesDrawer || isReferencesCollapsed
                                                        ? '1 1 auto'
                                                        : `0 0 ${leftPaneWidth}%`,
                                                    minWidth: useMobileReferencesDrawer ? 0 : `${LEFT_MIN_PX}px`,
                                                }}
                                            >
                                                <div className="chat-container">
                                                    <Box className="llm-header" sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '0 24px',
                                                        height: '66px',
                                                        backgroundColor: 'var(--color-background-surface)',
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            minWidth: 0,
                                                            flex: 1,
                                                        }}>
                                                            <Typography sx={{
                                                                fontFamily: 'Geist, sans-serif',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                lineHeight: '18px',
                                                                color: 'var(--color-text-primary)',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                maxWidth: '420px',
                                                            }}>
                                                                {chatTitle}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleToggleConversationBookmark}
                                                                disabled={
                                                                    authLoading
                                                                    || !isAuthenticated
                                                                    || (!activeConversationIdRef.current && !activeConversationId)
                                                                }
                                                                sx={{
                                                                    padding: '4px',
                                                                    color: isConversationBookmarked ? 'var(--color-brand-primary)' : 'var(--color-text-tertiary)',
                                                                    '&:hover': {
                                                                        backgroundColor: 'var(--color-background-subtle)',
                                                                    },
                                                                }}
                                                                title={isConversationBookmarked ? 'Remove bookmark' : 'Bookmark this chat'}
                                                            >
                                                                {isConversationBookmarked ? (
                                                                    <BookmarkIcon sx={{ fontSize: 16 }} />
                                                                ) : (
                                                                    <BookmarkBorderIcon sx={{ fontSize: 16 }} />
                                                                )}
                                                            </IconButton>
                                                        </Box>
                                                        {(useMobileReferencesDrawer || isReferencesCollapsed) && (
                                                            <MuiButton
                                                                className={`llm-header-references-toggle${useMobileReferencesDrawer ? ' mobile' : ''}`}
                                                                onClick={useMobileReferencesDrawer
                                                                    ? () => setIsMobileReferencesDrawerOpen(true) : expandReferences}
                                                                startIcon={useMobileReferencesDrawer ? undefined : <ReferenceIcon />}
                                                                aria-haspopup={useMobileReferencesDrawer ? 'dialog' : undefined}
                                                                aria-expanded={useMobileReferencesDrawer ? isMobileReferencesDrawerOpen : !isReferencesCollapsed}
                                                            >
                                                                References
                                                            </MuiButton>
                                                        )}
                                                    </Box>

                                                    <div ref={messagesContainerRef} className="messages-container">
                                                        {/* For screen readers only: the visual signal that an answer is
                                                            being written is a spinner and a growing card, neither of
                                                            which a reader hears. Announced politely — never interrupting
                                                            what is being read — and cleared when the run settles. */}
                                                        <Box
                                                            role="status"
                                                            aria-live="polite"
                                                            sx={{
                                                                position: 'absolute',
                                                                width: '1px',
                                                                height: '1px',
                                                                margin: '-1px',
                                                                padding: 0,
                                                                overflow: 'hidden',
                                                                clip: 'rect(0 0 0 0)',
                                                                whiteSpace: 'nowrap',
                                                                border: 0,
                                                            }}
                                                        >
                                                            {isViewingRunningConversation
                                                                ? (answerReady
                                                                    ? 'The answer is ready and its references are being finalized.'
                                                                    : 'The assistant is answering.')
                                                                : ''}
                                                        </Box>
                                                        {!isConversationLoading && renderMessages()}
                                                        <div ref={messagesEndRef} />
                                                    </div>
                                                    {isConversationLoading && loadingConversationId && (
                                                        <div className="chat-loading-overlay">
                                                            <CircularProgress size={28} sx={{ color: 'var(--color-grey-900)' }} />
                                                            <Typography sx={{
                                                                fontFamily: 'Open Sans, sans-serif',
                                                                fontSize: '14px',
                                                                fontWeight: 400,
                                                                color: 'var(--color-text-tertiary)',
                                                            }}>
                                                                Loading chat history... This may take ~20 seconds
                                                            </Typography>
                                                        </div>
                                                    )}

                                                    {/* <div className="chat-header">
                                                    <form onSubmit={handleSubmit} className="input-form">
                                                        <MuiButton
                                                            variant='outlined'
                                                            value={userInput}
                                                            onChange={(e) => setUserInput(e.target.value)}
                                                            placeholder="Ask a question about the biomedical literature..."
                                                            className="message-input"
                                                            disabled={isLoading}
                                                        />
                                                        <button
                                                            type="submit"
                                                            className="send-button"
                                                                border: "1px solid var(--color-brand-primary)",
                                                                bgcolor: "var(--color-grey-25)",
                                                                color: "var(--color-brand-primary)",
                                                                "& .MuiButton-startIcon": {
                                                                    color: "var(--color-brand-primary)",
                                                                },
                                                                "& .MuiSvgIcon-root": {
                                                                    color: "var(--color-brand-primary)",
                                                                },
                                                        >
                                                            Send
                                                                    color: "var(--color-brand-primary)",
                                                                    boxShadow: index == selectedMessageIndex ? "0 0 0 1px var(--color-brand-primary)" : "none",
                                                            icon={<DeleteOutlined />}
                                                                boxShadow: index == selectedMessageIndex ? "0 0 0 1px var(--color-brand-primary)" : "none",
                                                            className="clear-button"
                                                            disabled={isLoading}
                                                        >
                                                            Clear History
                                                        </Button>
                                                    </form>
                                                </div> */}
                                                    {showLimitWarning && (
                                                        <div className="llm-limit-warning">
                                                            <span className="llm-limit-warning-text">
                                                                {limitWarningText}
                                                            </span>
                                                            {isGuest && (
                                                                <button
                                                                    type="button"
                                                                    className="llm-limit-warning-button"
                                                                    onClick={() => requireSignIn(limitWarningText)}
                                                                >
                                                                    Sign in
                                                                </button>
                                                            )}
                                                            {/* A signed-in reader out of credits can buy more —
                                                                once a store is configured (config/features.js). */}
                                                            {!isGuest && CREDITS_PURCHASE_URL && (
                                                                <a
                                                                    className="llm-limit-warning-button"
                                                                    href={CREDITS_PURCHASE_URL}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={() => trackGtagEvent('credits_buy_click', { source: 'chat_limit_notice' })}
                                                                >
                                                                    Buy credits
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Figma "Asking Question" hangs the panel off the
                                                        composer (111:4385 sits at y=-502 inside the
                                                        input bar frame), not in the message stream:
                                                        it is a question to answer before the run can
                                                        go on, so it belongs where the answer is typed
                                                        and must not scroll away with the transcript. */}
                                                    <div className="composer-dock">
                                                        {isViewingRunningConversation && pendingClarification && (
                                                            <div className="clarify-float">
                                                                <ClarifyPanel
                                                                    pendingClarification={pendingClarification}
                                                                    clarificationDrafts={clarificationDrafts}
                                                                    clarificationError={clarificationError}
                                                                    clarificationSubmitting={clarificationSubmitting}
                                                                    hasInvalidOtherSelection={hasInvalidOtherSelection}
                                                                    onUpdateDraft={updateClarificationDraft}
                                                                    onSubmit={() => submitClarification({ useDefaults: false })}
                                                                    onSkip={() => submitClarification({ useDefaults: true })}
                                                                />
                                                            </div>
                                                        )}
                                                    <ChatSearchBar
                                                        userInput={userInput}
                                                        setUserInput={setUserInput}
                                                        isLoading={isLoading}
                                                        isRunElsewhere={isLoading && !isViewingRunningConversation}
                                                        // A guest at the limit keeps a live composer: sending
                                                        // opens the sign-in overlay instead (handleSubmit).
                                                        isQueryLimitReached={isLimitReachedEffective && !isGuest}
                                                        investigateEnabled={chatInvestigateEnabled}
                                                        serviceTier={serviceTier}
                                                        pipelineIsDeepResearch={chatInvestigateEnabled}
                                                        onServiceTierChange={handleServiceTierChange}
                                                        onServiceTierResolveDefault={setServiceTier}
                                                        isGuest={isGuest}
                                                        onRequireSignIn={requestPremiumSignIn}
                                                        attachments={composerAttachments}
                                                        onAttachRequireSignIn={requestAttachSignIn}
                                                        onSubmit={(event, submissionMeta) => {
                                                            submitOrQueue(event, {
                                                                investigateEnabled: chatInvestigateEnabled,
                                                                serviceTier,
                                                            }, submissionMeta?.queryMethod || 'button');
                                                        }}
                                                        onStop={handleStopStreaming}
                                                    />
                                                    </div>
                                                </div>
                                            </Box>
                                            {!useMobileReferencesDrawer && !isReferencesCollapsed && (
                                                <>
                                                    <div className="llm-split-divider" onMouseDown={handleSplitMouseDown}>
                                                        {isDraggingSplit && (
                                                            <div
                                                                className="llm-split-drag-indicator"
                                                                style={{ top: `${dragIndicatorY}px` }}
                                                            />
                                                        )}
                                                    </div>
                                                    <Box className="llm-column references-column" sx={{ flex: 1, minWidth: `${RIGHT_MIN_PX}px` }}>
                                                        <IconButton
                                                            size="small"
                                                            className="references-collapse-toggle"
                                                            onClick={collapseReferences}
                                                            aria-label="Collapse references"
                                                            title="Collapse references"
                                                        >
                                                            <ChevronRightIcon sx={{ fontSize: 20 }} />
                                                        </IconButton>
                                                        <div style={{ height: '100%', width: '100%' }}>
                                                            <div className="references-container">
                                                                <div className="references-header-row">
                                                                    <div className="references-header-main">
                                                                        <h3 className="references-title">References</h3>
                                                                        <button
                                                                            type="button"
                                                                            className="references-scope-trigger"
                                                                            onClick={() => setIsReferenceScopeOpen((prev) => !prev)}
                                                                            aria-expanded={isReferenceScopeOpen}
                                                                        >
                                                                            <span className="material-symbols-outlined references-scope-icon" aria-hidden="true">forum</span>
                                                                            <span>{selectedReferenceSource?.label || 'Select a response'}</span>
                                                                            <ChevronRightIcon className={`references-scope-chevron${isReferenceScopeOpen ? ' expanded' : ''}`} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                {isReferenceScopeOpen && (
                                                                    <div className="references-scope-panel">
                                                                        {referenceSourceOptions.map((item, optionIndex) => (
                                                                            <button
                                                                                key={item.index}
                                                                                type="button"
                                                                                className="references-scope-option"
                                                                                onClick={() => {
                                                                                    setSelectedMessageIndex(item.index);
                                                                                    setIsReferenceScopeOpen(false);
                                                                                }}
                                                                            >
                                                                                <span className="references-scope-option-label">
                                                                                    <span className="references-scope-option-number">{optionIndex + 1}.</span> {item.label}
                                                                                </span>
                                                                                <span className={`references-scope-radio${referenceSourceIndex === item.index ? ' selected' : ''}`} />
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                <div className="references-toolbar-row">
                                                                    {/* While a run is in flight the count is not yet knowable — "0 Citations"
                                                                        reads as "this answer has no sources", so show nothing until it lands. */}
                                                                    <span className="references-count-label">
                                                                        {isProcessing && sortedReferences.length === 0
                                                                            ? ''
                                                                            : `${sortedReferences.length} Citations`}
                                                                    </span>
                                                                    <div className="references-toolbar-actions">
                                                                        <IconButton
                                                                            size="small"
                                                                            className="references-action-button"
                                                                            onClick={handleExportReferences}
                                                                            disabled={isExportDisabled}
                                                                            title="Export all references"
                                                                        >
                                                                            <DownloadIcon
                                                                                aria-label="Download references"
                                                                                style={{
                                                                                    width: '14px',
                                                                                    height: '14px',
                                                                                    display: 'block',
                                                                                    color: isExportDisabled ? 'var(--color-grey-300)' : 'var(--color-text-tertiary)',
                                                                                }}
                                                                            />
                                                                        </IconButton>
                                                                        <ToggleButtonGroup
                                                                            size="small"
                                                                            exclusive
                                                                            value={sortOption}
                                                                            onChange={(event, value) => {
                                                                                if (value !== null) {
                                                                                    setSortOption(value);
                                                                                }
                                                                            }}
                                                                            className="references-sort-toggle"
                                                                        >
                                                                            <ToggleButton value="Citations">Citation</ToggleButton>
                                                                            <ToggleButton value="Year">Year</ToggleButton>
                                                                        </ToggleButtonGroup>
                                                                    </div>
                                                                </div>

                                                                {sortedReferences.length > 0 ? (
                                                                    <div ref={referencesListRef} className="references-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                                                        {sortedReferences.map(({ reference: ref, originalIndex, displayNumber }) => {
                                                                            const url = [
                                                                                ref.title,
                                                                                ref.url,
                                                                                ref.citation_count,
                                                                                ref.year,
                                                                                ref.journal,
                                                                                ref.authors,
                                                                                // [6] the real full text, where the paper has one
                                                                                ref.fulltext_url,
                                                                            ];
                                                                            const pubmedId = ref.url.split('/').filter(Boolean).pop();
                                                                            const isHighlighted = hoveredPubmedId === pubmedId;
                                                                            return (
                                                                                <div
                                                                                    key={`${pubmedId}-${originalIndex}`}
                                                                                    data-pubmed-id={pubmedId}
                                                                                    className={`reference-entry-wrapper${isHighlighted ? ' highlighted' : ''}`}
                                                                                >
                                                                                    <ReferenceCard
                                                                                        url={url}
                                                                                        evidence={ref.evidence}
                                                                                        sourceHid={activeConversationIdRef.current || activeConversationId}
                                                                                        handleClick={handleClick}
                                                                                        onCiteClick={handleCiteClick}
                                                                                        isHighlighted={isHighlighted}
                                                                                        index={displayNumber}
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : isProcessing ? (
                                                                    <ReferencesSkeleton />
                                                                ) : (
                                                                    <p style={{ padding: '16px 32px' }}>No references available for this response.</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                        {useMobileReferencesDrawer && (
                                            <Drawer
                                                anchor="bottom"
                                                open={isMobileReferencesDrawerOpen}
                                                onClose={() => setIsMobileReferencesDrawerOpen(false)}
                                                PaperProps={{ className: 'llm-mobile-references-drawer', role: 'dialog', 'aria-label': 'References' }}
                                            >
                                                <div className="references-container llm-mobile-references-container">
                                                    <div className="references-drawer-handle" aria-hidden="true" />
                                                    <div className="references-header-row">
                                                        <div className="references-header-main">
                                                            <h3 className="references-title">References</h3>
                                                            <button
                                                                type="button"
                                                                className="references-scope-trigger"
                                                                onClick={() => setIsReferenceScopeOpen((prev) => !prev)}
                                                                aria-label="Select reference source"
                                                                aria-expanded={isReferenceScopeOpen}
                                                            >
                                                                <span className="material-symbols-outlined references-scope-icon" aria-hidden="true">forum</span>
                                                                <span>{selectedReferenceSource?.label || 'Select a response'}</span>
                                                                <ChevronRightIcon className={`references-scope-chevron${isReferenceScopeOpen ? ' expanded' : ''}`} />
                                                            </button>
                                                        </div>
                                                        <IconButton
                                                            size="small"
                                                            className="references-action-button"
                                                            onClick={() => setIsMobileReferencesDrawerOpen(false)}
                                                            aria-label="Close references"
                                                        >
                                                            <CloseIcon sx={{ fontSize: 20, color: 'var(--color-text-tertiary)' }} />
                                                        </IconButton>
                                                    </div>
                                                    {isReferenceScopeOpen && (
                                                        <div className="references-scope-panel">
                                                            {referenceSourceOptions.map((item, optionIndex) => (
                                                                <button key={item.index} type="button" className="references-scope-option"
                                                                    onClick={() => {
                                                                        setSelectedMessageIndex(item.index);
                                                                        setIsReferenceScopeOpen(false);
                                                                    }}>
                                                                    <span className="references-scope-option-label">
                                                                        <span className="references-scope-option-number">{optionIndex + 1}.</span> {item.label}
                                                                    </span>
                                                                    <span className={`references-scope-radio${referenceSourceIndex === item.index ? ' selected' : ''}`} />
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="references-toolbar-row references-mobile-footer">
                                                        <span className="references-count-label">{sortedReferences.length} Citations</span>
                                                        <div className="references-toolbar-actions">
                                                            <ToggleButtonGroup
                                                                size="small"
                                                                exclusive
                                                                value={sortOption}
                                                                onChange={(event, value) => {
                                                                    if (value !== null) {
                                                                        setSortOption(value);
                                                                    }
                                                                }}
                                                                className="references-sort-toggle"
                                                            >
                                                                <ToggleButton value="Citations">Citation</ToggleButton>
                                                                <ToggleButton value="Year">Year</ToggleButton>
                                                            </ToggleButtonGroup>
                                                            <IconButton
                                                                size="small"
                                                                className="references-action-button"
                                                                onClick={handleExportReferences}
                                                                disabled={isExportDisabled}
                                                                title="Export all references"
                                                            >
                                                                <DownloadIcon
                                                                    aria-label="Download references"
                                                                    style={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        display: 'block',
                                                                        color: isExportDisabled ? 'var(--color-grey-300)' : 'var(--color-text-tertiary)',
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </div>
                                                    </div>

                                                    {sortedReferences.length > 0 ? (
                                                        <div ref={referencesListRef} className="references-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingLeft: '16px', paddingRight: '16px' }}>
                                                            {sortedReferences.map(({ reference: ref, originalIndex, displayNumber }) => {
                                                                const url = [
                                                                    ref.title,
                                                                    ref.url,
                                                                    ref.citation_count,
                                                                    ref.year,
                                                                    ref.journal,
                                                                    ref.authors,
                                                                    ref.fulltext_url,
                                                                ];
                                                                const pubmedId = ref.url.split('/').filter(Boolean).pop();
                                                                const isHighlighted = hoveredPubmedId === pubmedId;
                                                                return (
                                                                    <div
                                                                        key={`${pubmedId}-${originalIndex}`}
                                                                        data-pubmed-id={pubmedId}
                                                                        className={`reference-entry-wrapper${isHighlighted ? ' highlighted' : ''}`}
                                                                    >
                                                                        <ReferenceCard
                                                                            url={url}
                                                                            evidence={ref.evidence}
                                                                            sourceHid={activeConversationIdRef.current || activeConversationId}
                                                                            handleClick={handleClick}
                                                                            onCiteClick={handleCiteClick}
                                                                            isHighlighted={isHighlighted}
                                                                            index={displayNumber}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <p style={{ padding: '16px 20px' }}>No references available for this response.</p>
                                                    )}
                                                </div>
                                            </Drawer>
                                        )}


                                    </div>
                                </div>
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </>
    );
}

export default LLMAgent; 
